import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Toaster as SonnerToaster, toast } from 'sonner';
import { Analytics } from '@vercel/analytics/react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from './components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { Badge } from './components/ui/badge';
import { Checkbox } from './components/ui/checkbox';
import { Switch } from './components/ui/switch';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription 
} from './components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './components/ui/alert-dialog';
import { 
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel 
} from './components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from './components/ui/popover';
import { translations, getStatusClass } from './lib/translations';

/** Velocità effettiva in base agli status (Afferrato/Trattenuto/… → 0; Rallentato/Esausto 2 → metà; Fretta → doppia) */
function getEffectiveSpeed(combatant) {
  const base = combatant.speed ?? 30;
  const names = (combatant.statuses || []).map(s => s.name);
  const speedZero = ['Afferrato', 'Trattenuto', 'Paralizzato', 'Stordito', 'Privo di sensi'].some(s => names.includes(s)) || names.includes('Esausto 5');
  if (speedZero) return 0;
  if (names.some(n => n === 'Rallentato' || n === 'Esausto 2')) return Math.floor(base / 2);
  if (names.some(n => n === 'Fretta')) return base * 2;
  return base;
}

/** CA effettiva in base agli status (Fretta +2; Rallentato -2; Prono -2) */
function getEffectiveAC(combatant) {
  const base = combatant.ac != null && combatant.ac !== '' ? Number(combatant.ac) : null;
  if (base == null) return null;
  const names = (combatant.statuses || []).map(s => s.name);
  let mod = 0;
  if (names.includes('Fretta')) mod += 2;
  if (names.includes('Rallentato')) mod -= 2;
  if (names.includes('Prono')) mod -= 2;
  return Math.max(0, base + mod);
}

/** True se almeno uno status concede vantaggio ai tiri del personaggio (Benedizione, Fretta, Invisibile, ecc.) */
function hasAdvantageFromStatuses(combatant) {
  const names = (combatant.statuses || []).map(s => s.name);
  const advantageStatuses = ['Benedizione', 'Fretta', 'Invisibile', 'Guidare'];
  return names.some(n => advantageStatuses.includes(n));
}

/** True se almeno uno status impone svantaggio (Avvelenato, Stordito, Prono, Spaventato, Impaurito, Trattenuto, Assordato, ecc.) */
function hasDisadvantageFromStatuses(combatant) {
  const names = (combatant.statuses || []).map(s => s.name);
  const disadvantageStatuses = ['Avvelenato', 'Stordito', 'Prono', 'Spaventato', 'Impaurito', 'Trattenuto', 'Assordato', 'Rallentato'];
  return names.some(n => disadvantageStatuses.includes(n));
}

import { useCombat } from './hooks/useCombat';
import { useCharacters } from './hooks/useCharacters';
import { useNpcPresets, getNpcPresetsFromStorage, NPC_PRESETS_STORAGE_KEY } from './hooks/useNpcPresets';
import { usePeer, useMasterLobby, usePlayerLobby, decodeMasterId } from './hooks/usePeer';
import { Html5Qrcode } from 'html5-qrcode';
import { 
  Sun, Moon, Languages, Plus, ChevronRight, RotateCcw, RotateCw, Download, Upload, 
  MoreVertical, Heart, Zap, X, Skull, Edit2, Trash2, Flame, Users, User,
  Swords, Copy, Link as LinkIcon, ArrowLeft, Check, Loader2, Crown, Shield, QrCode, Eye, EyeOff,
  ScrollText, Sword, AlertCircle, CheckCircle, UserPlus, UserMinus, Sparkles, ChevronDown, ChevronUp, Footprints, Dices, Palette, Network, FileText,
  MapPin, MessageSquare, Package, Calendar, BookOpen, ListChecks, Coins
} from 'lucide-react';
import './App.css';

/** Icona scatto: figura che corre con linee di velocità (adattata da silhouette) */
const RunningPersonIcon = ({ className = 'w-3.5 h-3.5', ...props }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>
    {/* Linee di velocità dietro la figura */}
    <path d="M4 8h2.5" strokeWidth="2" />
    <path d="M3 12h3" strokeWidth="2" />
    <path d="M4 16h2" strokeWidth="2" />
    {/* Testa */}
    <circle cx="14" cy="5" r="2.2" />
    {/* Busto inclinato in avanti */}
    <path d="M14 7.5l1.5 5" />
    {/* Braccio avanti (piegato) */}
    <path d="M15.5 9l3.5-1.5 1 2" />
    {/* Braccio indietro (piegato) */}
    <path d="M14 10l-2.5 2-1 3" />
    {/* Gamba avanti (mid-step) */}
    <path d="M15.5 12.5l2.5 1 1 5" />
    {/* Gamba indietro (spinta) */}
    <path d="M14 12.5l-2 1.5-1.5 5" />
  </svg>
);

// D&D 5e: 1 PP = 10 GP, 1 GP = 2 EP = 10 SP = 100 CP → in rame: PP=1000, GP=100, EP=50, SP=10, CP=1
const COIN_RATES = { pp: 1000, gp: 100, ep: 50, sp: 10, cp: 1 };
const COIN_ORDER = ['pp', 'gp', 'ep', 'sp', 'cp'];
const DUNGEON_WALLET_URL = 'https://dungeonwallet.com';
const DUNGEON_WALLET_ICON = '/wallet-mascot.png';
function getCoinLabels(t) {
  return t ? { pp: t.coinPlatino || 'MP', gp: t.coinOro || 'MO', ep: t.coinElettro || 'ME', sp: t.coinArgento || 'MA', cp: t.coinRame || 'MR' } : { pp: 'MP', gp: 'MO', ep: 'ME', sp: 'MA', cp: 'MR' };
}

function coinPouchToCopper(pouch) {
  if (!pouch) return 0;
  return COIN_ORDER.reduce((sum, k) => sum + (Math.max(0, parseInt(pouch[k], 10) || 0) * COIN_RATES[k]), 0);
}

function copperToCoinPouch(totalCopper) {
  let rem = Math.max(0, Math.floor(totalCopper));
  const out = { pp: 0, gp: 0, ep: 0, sp: 0, cp: 0 };
  out.pp = Math.floor(rem / COIN_RATES.pp); rem %= COIN_RATES.pp;
  out.gp = Math.floor(rem / COIN_RATES.gp); rem %= COIN_RATES.gp;
  out.ep = Math.floor(rem / COIN_RATES.ep); rem %= COIN_RATES.ep;
  out.sp = Math.floor(rem / COIN_RATES.sp); rem %= COIN_RATES.sp;
  out.cp = rem;
  return out;
}

/** Modal borsa monete: saldo in evidenza, poi Aggiungi/Spendi. addOnly=true (es. master multiplayer) mostra solo Aggiungi. */
const CoinPouchModal = ({ isOpen, onClose, combatant, onSave, t, addOnly = false }) => {
  const labels = getCoinLabels(t);
  const safePouch = combatant?.coinPouch && typeof combatant.coinPouch === 'object'
    ? { pp: combatant.coinPouch.pp ?? 0, gp: combatant.coinPouch.gp ?? 0, ep: combatant.coinPouch.ep ?? 0, sp: combatant.coinPouch.sp ?? 0, cp: combatant.coinPouch.cp ?? 0 }
    : { pp: 0, gp: 0, ep: 0, sp: 0, cp: 0 };
  const [panel, setPanel] = useState('main'); // 'main' | 'add' | 'spend'
  const [addAmounts, setAddAmounts] = useState({ pp: '', gp: '', ep: '', sp: '', cp: '' });
  const [spendAmounts, setSpendAmounts] = useState({ pp: '', gp: '', ep: '', sp: '', cp: '' });
  const [spendResult, setSpendResult] = useState(null);
  const [showEmptyConfirm, setShowEmptyConfirm] = useState(false);

  useEffect(() => {
    if (isOpen) { setSpendResult(null); setPanel('main'); }
  }, [isOpen]);

  const currentTotal = coinPouchToCopper(safePouch);
  const emptyPouch = { pp: 0, gp: 0, ep: 0, sp: 0, cp: 0 };

  const handleAdd = () => {
    const add = { pp: Math.max(0, parseInt(addAmounts.pp, 10) || 0), gp: Math.max(0, parseInt(addAmounts.gp, 10) || 0), ep: Math.max(0, parseInt(addAmounts.ep, 10) || 0), sp: Math.max(0, parseInt(addAmounts.sp, 10) || 0), cp: Math.max(0, parseInt(addAmounts.cp, 10) || 0) };
    const summed = { pp: safePouch.pp + add.pp, gp: safePouch.gp + add.gp, ep: safePouch.ep + add.ep, sp: safePouch.sp + add.sp, cp: safePouch.cp + add.cp };
    const totalCopper = coinPouchToCopper(summed);
    const newPouch = copperToCoinPouch(totalCopper);
    onSave(newPouch);
    setAddAmounts({ pp: '', gp: '', ep: '', sp: '', cp: '' });
    setPanel('main');
  };

  const handleSpend = () => {
    const spend = coinPouchToCopper({ pp: spendAmounts.pp || 0, gp: spendAmounts.gp || 0, ep: spendAmounts.ep || 0, sp: spendAmounts.sp || 0, cp: spendAmounts.cp || 0 });
    if (spend <= 0) {
      setSpendResult('zero');
      return;
    }
    if (spend > currentTotal) {
      setSpendResult('insufficient');
      return;
    }
    const newTotal = currentTotal - spend;
    const newPouch = copperToCoinPouch(newTotal);
    onSave(newPouch);
    setSpendAmounts({ pp: '', gp: '', ep: '', sp: '', cp: '' });
    setSpendResult(null);
    setPanel('main');
  };

  const spendTotal = coinPouchToCopper({ pp: spendAmounts.pp || 0, gp: spendAmounts.gp || 0, ep: spendAmounts.ep || 0, sp: spendAmounts.sp || 0, cp: spendAmounts.cp || 0 });

  if (!combatant) return null;

  const backToMain = () => { setPanel('main'); setSpendResult(null); };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90dvh] overflow-hidden flex flex-col p-0 gap-0" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader className="px-5 pt-5 pb-4 shrink-0 border-b border-border/50">
          <div className="flex items-center gap-2">
            {(panel === 'add' || panel === 'spend') && (
              <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0 -ml-1" onClick={backToMain} aria-label={t?.cancel || 'Indietro'}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
            )}
            <DialogTitle className="flex items-center gap-2.5 text-lg font-semibold flex-1 min-w-0">
              <div className="w-9 h-9 rounded-lg accent-bg-10 flex items-center justify-center shrink-0">
                <Coins className="w-5 h-5 accent-text" />
              </div>
              <span className="truncate">
                {panel === 'add' ? (t?.coinPouchAdd || 'Aggiungi monete') : panel === 'spend' ? (t?.coinPouchSpend || 'Spendi monete') : `${t?.coinPouchTitle || 'Borsa monete'} · ${combatant.name}`}
              </span>
            </DialogTitle>
          </div>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* Saldo: sempre visibile in cima */}
          <div className="rounded-xl bg-muted/40 border border-border/50 p-4">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">{t?.coinPouchCurrent || 'Saldo attuale'}</p>
            <div className="grid grid-cols-5 gap-3 sm:gap-4">
              {COIN_ORDER.map(k => (
                <div key={k} className="text-center">
                  <p className="text-[10px] sm:text-xs text-muted-foreground font-medium mb-0.5">{labels[k]}</p>
                  <p className="text-sm sm:text-base font-mono font-semibold tabular-nums">{safePouch[k] ?? 0}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border/50 text-center">
              {t?.coinPouchTotalCopper || 'Totale'}: <span className="font-mono font-medium text-foreground">{currentTotal}</span> {labels.cp}
            </p>
          </div>

          {panel === 'main' && (
            <>
              <div className={addOnly ? '' : 'grid grid-cols-2 gap-3'}>
                <Button size="lg" className={addOnly ? 'w-full h-12 font-medium' : 'h-12 font-medium'} onClick={() => setPanel('add')}>
                  <Plus className="w-5 h-5 mr-2" />{t?.coinPouchAddButton || 'Aggiungi'}
                </Button>
                {!addOnly && (
                  <Button size="lg" variant="secondary" className="h-12 font-medium" onClick={() => setPanel('spend')} disabled={currentTotal <= 0}>
                    {t?.coinPouchSpendButton || 'Spendi'}
                  </Button>
                )}
              </div>
              {!addOnly && (
                <div className="pt-2 border-t border-border/50">
                  <Button variant="ghost" size="sm" className="w-full text-destructive hover:bg-destructive/10 hover:text-destructive h-8 text-xs" onClick={() => setShowEmptyConfirm(true)} disabled={currentTotal <= 0}>
                    {t?.coinPouchEmptyAll || 'Svuota tutto'}
                  </Button>
                </div>
              )}
            </>
          )}

          {panel === 'add' && (
            <div>
              <div className="grid grid-cols-5 gap-2 mb-4">
                {COIN_ORDER.map(k => (
                  <div key={k}>
                    <label className="block text-[10px] sm:text-xs text-muted-foreground mb-1 text-center">{labels[k]}</label>
                    <Input type="number" min="0" inputMode="numeric" className="h-9 text-center font-mono text-sm" value={addAmounts[k]} onChange={(e) => setAddAmounts(prev => ({ ...prev, [k]: e.target.value }))} placeholder="0" />
                  </div>
                ))}
              </div>
              <Button className="w-full" onClick={handleAdd} disabled={!COIN_ORDER.some(k => (parseInt(addAmounts[k], 10) || 0) > 0)}>
                <Plus className="w-4 h-4 mr-2" />{t?.coinPouchAddButton || 'Aggiungi'}
              </Button>
            </div>
          )}

          {panel === 'spend' && (
            <div>
              <div className="grid grid-cols-5 gap-2 mb-4">
                {COIN_ORDER.map(k => (
                  <div key={k}>
                    <label className="block text-[10px] sm:text-xs text-muted-foreground mb-1 text-center">{labels[k]}</label>
                    <Input type="number" min="0" inputMode="numeric" className="h-9 text-center font-mono text-sm" value={spendAmounts[k]} onChange={(e) => setSpendAmounts(prev => ({ ...prev, [k]: e.target.value }))} placeholder="0" />
                  </div>
                ))}
              </div>
              {spendTotal > 0 && <p className="text-xs text-muted-foreground mb-2">{t?.coinPouchSpendEquivalent || 'Equiv.'}: {spendTotal} {labels.cp}</p>}
              {spendResult === 'insufficient' && <p className="text-xs text-destructive mb-2">{t?.coinPouchInsufficient || 'Saldo insufficiente.'}</p>}
              {spendResult === 'zero' && <p className="text-xs text-muted-foreground mb-2">{t?.coinPouchEnterAmount || 'Inserisci importo.'}</p>}
              <Button variant="secondary" className="w-full" onClick={handleSpend} disabled={spendTotal <= 0}>
                {t?.coinPouchSpendButton || 'Spendi'}
              </Button>
            </div>
          )}
        </div>

        {!addOnly && (
        <AlertDialog open={showEmptyConfirm} onOpenChange={setShowEmptyConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t?.coinPouchEmptyConfirmTitle || 'Svuotare la borsa?'}</AlertDialogTitle>
              <AlertDialogDescription>{t?.coinPouchEmptyConfirmDesc || 'Tutte le monete verranno azzerate. Questa azione non può essere annullata.'}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t?.cancel || 'Annulla'}</AlertDialogCancel>
              <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => { onSave(emptyPouch); setShowEmptyConfirm(false); }}>
                {t?.coinPouchEmptyConfirmButton || 'Svuota tutto'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ============ PROVIDERS ============

const ACCENT_OPTIONS = [
  { id: 'amber', label: 'Ambra', color: 'hsl(38, 92%, 50%)' },
  { id: 'emerald', label: 'Smeraldo', color: 'hsl(160, 84%, 39%)' },
  { id: 'sky', label: 'Cielo', color: 'hsl(199, 89%, 48%)' },
  { id: 'violet', label: 'Viola', color: 'hsl(263, 70%, 50%)' },
  { id: 'rose', label: 'Rosa', color: 'hsl(350, 89%, 60%)' },
  { id: 'black', label: 'Nero', color: 'hsl(0, 0%, 18%)' },
];

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => localStorage.getItem('dnd-theme') || 'light');
  const [accent, setAccent] = useState(() => localStorage.getItem('dnd-accent') || 'sky');
  useEffect(() => {
    localStorage.setItem('dnd-theme', theme);
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);
  useEffect(() => {
    localStorage.setItem('dnd-accent', accent);
    document.documentElement.setAttribute('data-accent', accent);
  }, [accent]);
  const toggleTheme = () => setTheme(prev => prev === 'light' ? 'dark' : 'light');
  return children({ theme, toggleTheme, accent, setAccent });
};

/** Menu a tendina discreto: tema (chiaro/scuro) + colore accento */
const AppearanceDropdown = ({ theme, toggleTheme, accent, setAccent, t }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" aria-label={t?.ariaThemeAccent || 'Tema e accento'}>
        <Palette className="w-5 h-5" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-52">
      <DropdownMenuLabel className="text-xs text-muted-foreground">{t?.themeLabel || 'Tema'}</DropdownMenuLabel>
      <DropdownMenuItem onClick={() => theme !== 'light' && toggleTheme()}>
        <Sun className="w-4 h-4 mr-2" />
        {t?.themeLight || 'Chiaro'}
        {theme === 'light' && <Check className="w-4 h-4 ml-auto" />}
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => theme !== 'dark' && toggleTheme()}>
        <Moon className="w-4 h-4 mr-2" />
        {t?.themeDark || 'Scuro'}
        {theme === 'dark' && <Check className="w-4 h-4 ml-auto" />}
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuLabel className="text-xs text-muted-foreground">{t?.accentLabel || 'Accento'}</DropdownMenuLabel>
      <div className="px-2 py-2 grid grid-cols-3 gap-2 justify-items-center">
        {ACCENT_OPTIONS.map(({ id, color, label }) => (
          <button
            key={id}
            type="button"
            onClick={() => setAccent(id)}
            className={`w-8 h-8 rounded-full border-2 transition-all shrink-0 ${accent === id ? 'border-foreground scale-110 ring-2 ring-offset-2 ring-offset-background ring-muted-foreground' : 'border-transparent hover:scale-105'}`}
            style={{ backgroundColor: color }}
            title={label}
            aria-label={id}
            aria-pressed={accent === id}
          />
        ))}
      </div>
    </DropdownMenuContent>
  </DropdownMenu>
);

const LANG_OPTIONS = [
  { code: 'it', label: 'Italiano' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
];

const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem('dnd-lang') || 'it');
  useEffect(() => { localStorage.setItem('dnd-lang', lang); }, [lang]);
  const toggleLang = () => {
    setLang(prev => {
      if (prev === 'it') return 'en';
      if (prev === 'en') return 'es';
      if (prev === 'es') return 'fr';
      return 'it';
    });
  };
  return children({ lang, setLang, toggleLang, t: translations[lang] });
};

const LanguageDropdown = ({ lang, setLang, t }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant="ghost" size="sm" className="h-9 sm:h-8 gap-1.5 text-muted-foreground font-normal px-2 sm:px-3">
        <Languages className="w-4 h-4 shrink-0" />
        <span className="text-xs hidden sm:inline">{t?.language || 'Lingua'}: <span className="font-mono uppercase">{lang}</span></span>
        <span className="text-xs font-mono uppercase sm:hidden">{lang}</span>
        <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-70" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      {LANG_OPTIONS.map((opt) => (
        <DropdownMenuItem key={opt.code} onClick={() => setLang(opt.code)} className={lang === opt.code ? 'bg-accent' : ''}>
          {opt.label} ({opt.code.toUpperCase()})
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

// Custom Toaster
const Toaster = ({ theme }) => (
  <SonnerToaster 
    position="top-center" 
    richColors 
    closeButton
    theme={theme}
    toastOptions={{
      classNames: {
        toast: "group toast group-[.toaster]:bg-background/90 group-[.toaster]:backdrop-blur-xl group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg group-[.toaster]:border-[hsl(var(--accent-theme)/0.2)]",
      },
    }}
  />
);

// ============ SHARED COMPONENTS ============

const HpBar = ({ current, max, tempHp = 0 }) => {
  const percentage = Math.max(0, Math.min(100, (current / max) * 100));
  const tempPercentage = Math.max(0, Math.min(100 - percentage, (tempHp / max) * 100));
  let colorClass = 'bg-green-500';
  if (percentage <= 25) colorClass = 'bg-red-500';
  else if (percentage <= 50) colorClass = 'bg-yellow-500';
  return (
    <div className="w-full h-2 bg-muted rounded-full overflow-hidden flex">
      <div className={`h-full ${colorClass} transition-all duration-300`} style={{ width: `${percentage}%` }} />
      {tempHp > 0 && (
        <div className="h-full bg-cyan-400 transition-all duration-300" style={{ width: `${tempPercentage}%` }} />
      )}
    </div>
  );
};

// Mini status indicator for at-a-glance view
const StatusIndicator = ({ hp, maxHp, isKO, t }) => {
  if (isKO) return <span className="w-3 h-3 rounded-full bg-gray-500" title={t?.ko || 'KO'} />;
  const percentage = (hp / maxHp) * 100;
  if (percentage > 50) return <span className="w-3 h-3 rounded-full bg-green-500" title={t?.healthy || 'Healthy'} />;
  if (percentage > 25) return <span className="w-3 h-3 rounded-full bg-yellow-500" title={t?.injured || 'Injured'} />;
  return <span className="w-3 h-3 rounded-full bg-red-500 animate-pulse" title={t?.critical || 'Critical'} />;
};

const StatusTag = ({ status, onRemove, t }) => {
  const colorClass = getStatusClass(status.name);
  const statusDict = t?.statusDictionary || {};
  const label = statusDict[status.name]?.name ?? status.name;
  const description = statusDict[status.name]?.description ?? '';
  const valueLabel = status.value != null && status.value > 0 && (status.name === 'Rigenerazione' || status.name === 'Sanguinante')
    ? (status.name === 'Rigenerazione' ? ` +${status.value}/turn` : ` ${status.value}/turn`)
    : '';
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Badge variant="outline" className={`${colorClass} border flex items-center gap-1 text-xs py-0.5 px-2 cursor-pointer hover:opacity-90`}>
          <span>{label}{valueLabel}</span>
          {status.duration !== null && <span className="font-mono text-[10px] opacity-70">({status.duration})</span>}
          {onRemove && (
            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove(); }} className="ml-1 hover:opacity-70" aria-label={t?.remove || 'Rimuovi'}>
              <X className="w-3 h-3" />
            </button>
          )}
        </Badge>
      </PopoverTrigger>
      <PopoverContent className="max-w-xs p-3" align="start" side="top">
        <p className="font-medium text-sm">{label}</p>
        {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
      </PopoverContent>
    </Popover>
  );
};

// App name constant
const APP_NAME = "CoCo";
const APP_SUBTITLE = "(Combat Companion)";

// Placeholder per campi nome: testo "Ad esempio: X" con X random (nuovo ad ogni apertura popup)
const NAME_EXAMPLES = ['Fenrir', 'Tormash', 'Eliodas', 'Valdogh', 'Burnock', 'Dave'];
const getRandomNamePlaceholder = (t) => {
  const name = NAME_EXAMPLES[Math.floor(Math.random() * NAME_EXAMPLES.length)];
  const template = t?.nameExample || 'Ad esempio: {name}';
  return template.replace('{name}', name);
};

// ============ TUTORIAL COMPONENT ============

const TutorialDialog = ({ isOpen, onClose, mode, t }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const personalSteps = [
    { title: t?.tutorialPersonalWelcomeTitle ?? 'Benvenuto al Tracking Personale!', content: t?.tutorialPersonalWelcomeContent ?? 'Questa modalità ti permette di gestire i tuoi personaggi e combattimenti in locale, senza connessione.', icon: <User className="w-12 h-12 text-primary" /> },
    { title: t?.tutorialPersonal1Title ?? '1. Crea i tuoi personaggi', content: t?.tutorialPersonal1Content ?? 'Clicca su \'Nuovo Personaggio\' per creare e salvare i tuoi personaggi.', icon: <Plus className="w-12 h-12 text-primary" /> },
    { title: t?.tutorialPersonal2Title ?? '2. Inizia il combattimento', content: t?.tutorialPersonal2Content ?? 'Clicca \'Combattimento\' su un personaggio per aggiungerlo alla battaglia.', icon: <Swords className="w-12 h-12 text-primary" /> },
    { title: t?.tutorialPersonal3Title ?? '3. Gestisci i turni', content: t?.tutorialPersonal3Content ?? 'Usa i pulsanti per modificare HP, aggiungere status e avanzare i turni.', icon: <Zap className="w-12 h-12 accent-text" /> },
  ];

  const masterSteps = [
    { title: t?.tutorialMasterWelcomeTitle ?? 'Benvenuto alla Modalità Master Offline!', content: t?.tutorialMasterWelcomeContent ?? 'Gestisci un combattimento completo con giocatori, NPC, nemici e companion!', icon: <Crown className="w-12 h-12 accent-text" /> },
    { title: t?.tutorialMaster1Title ?? '1. Tipi di Combattenti', content: t?.tutorialMaster1Content ?? 'Giocatori (Bianco), NPC Alleati (Blu), Nemici (Rosso), Companion (Verde).', icon: <Users className="w-12 h-12 text-primary" /> },
    { title: t?.tutorialMaster2Title ?? '2. Aggiungi Combattenti', content: t?.tutorialMaster2Content ?? 'Clicca \'Aggiungi\' e scegli il tipo. Puoi nascondere HP e CA ai giocatori.', icon: <Plus className="w-12 h-12 text-primary" /> },
    { title: t?.tutorialMaster3Title ?? '3. Iniziativa & Turni', content: t?.tutorialMaster3Content ?? 'L\'ordine di iniziativa appare in alto. Usa \'Prossimo Turno\' per avanzare.', icon: <Zap className="w-12 h-12 accent-text" /> },
  ];

  const steps = mode === 'master' ? masterSteps : personalSteps;

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      localStorage.setItem(`tutorial-${mode}-completed`, 'true');
      onClose();
    }
  };

  const handleSkip = () => {
    localStorage.setItem(`tutorial-${mode}-completed`, 'true');
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={handleSkip}>
      <DialogContent className="max-w-md mx-4">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>{t?.tutorialTitle || 'Tutorial'}</span>
            <Badge variant="outline">{currentStep + 1} / {steps.length}</Badge>
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-4 text-center">
          <div className="mb-4 flex justify-center">{steps[currentStep].icon}</div>
          <h3 className="text-lg font-bold mb-3">{steps[currentStep].title}</h3>
          <p className="text-muted-foreground whitespace-pre-line text-sm">{steps[currentStep].content}</p>
        </div>

        {/* Progress dots - centered */}
        <div className="flex justify-center gap-1.5 py-2">
          {steps.map((_, idx) => (
            <div 
              key={idx} 
              className={`w-2 h-2 rounded-full transition-all ${idx === currentStep ? 'bg-primary w-6' : 'bg-muted'}`} 
            />
          ))}
        </div>
        
        {/* Buttons - full width on mobile */}
        <div className="flex justify-between gap-4 pt-2">
          <Button variant="ghost" className="flex-1" onClick={handleSkip}>{t?.tutorialSkip || 'Salta'}</Button>
          <Button className="flex-1" onClick={handleNext}>
            {currentStep < steps.length - 1 ? (t?.tutorialNext || 'Avanti') : (t?.tutorialStart || 'Inizia')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============ HOME SCREEN ============

const STORAGE_CHARACTERS = 'dnd-saved-characters';
const STORAGE_COMBAT = 'dnd-combat-tracker';
/** Bump when replacing logo assets so mobile/PWA cache picks up new files */
const LOGO_ASSET_VERSION = '20260207';
const logoUrl = (theme) =>
  `${theme === 'dark' ? '/logo-dark.png' : '/logo.png'}?v=${LOGO_ASSET_VERSION}`;
const KOFI_WIDGET_SCRIPT = 'https://storage.ko-fi.com/cdn/widget/Widget_2.js';
const KOFI_PAGE_ID = 'V7V31ULOT1';
const KOFI_ACCENT_BY_THEME = {
  amber: '#f59e0b',
  emerald: '#10b981',
  sky: '#0ea5e9',
  violet: '#8b5cf6',
  rose: '#f43f5e',
  black: '#2e2e2e',
};

const INSTALL_BANNER_HIDDEN_KEY = 'coco-install-banner-hidden';
const INSTALL_BANNER_POSTPONED_KEY = 'coco-install-banner-postponed';
const KOFI_BANNER_VISIBLE_KEY = 'coco-kofi-banner-visible';
const POSTPONE_HOURS = 24;

const getInstallBannerPostponed = () => {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(INSTALL_BANNER_POSTPONED_KEY) : null;
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    return !isNaN(ts) && (Date.now() - ts < POSTPONE_HOURS * 60 * 60 * 1000);
  } catch { return false; }
};

const HomeScreen = ({ onSelectMode, onManageCharacters, onOpenHowItWorks, onRejoinLobby, onDismissSavedLobby, savedLobby, t, theme, toggleTheme, accent, setAccent, lang, setLang }) => {
  const fileInputRef = useRef(null);
  const importModeRef = useRef('characters');
  const [installBannerDismissed, setInstallBannerDismissed] = useState(() => {
    try { return typeof localStorage !== 'undefined' && localStorage.getItem(INSTALL_BANNER_HIDDEN_KEY) === 'true'; } catch { return false; }
  });
  const [installBannerPostponed, setInstallBannerPostponed] = useState(getInstallBannerPostponed);
  const [showInstallSteps, setShowInstallSteps] = useState(false);
  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' && window.innerWidth >= 768);
  const [isStandalone, setIsStandalone] = useState(false);
  const [multiplayerUnlockClicks, setMultiplayerUnlockClicks] = useState(0);
  const [kofiBannerVisible, setKofiBannerVisible] = useState(() => {
    try {
      const stored = localStorage.getItem(KOFI_BANNER_VISIBLE_KEY);
      return stored === null ? true : stored === 'true';
    } catch {
      return true;
    }
  });

  const setKofiBannerVisiblePersist = useCallback((visible) => {
    try {
      localStorage.setItem(KOFI_BANNER_VISIBLE_KEY, String(visible));
    } catch (_) {}
    setKofiBannerVisible(visible);
    if (!visible) {
      const mount = document.getElementById('kofi-widget-mount');
      if (mount) mount.innerHTML = '';
    }
  }, []);

  useEffect(() => {
    const mqDesktop = window.matchMedia('(min-width: 768px)');
    const mqStandalone = window.matchMedia('(display-mode: standalone)');
    const onResize = () => setIsDesktop(window.innerWidth >= 768);
    const onStandalone = () => setIsStandalone(mqStandalone.matches || !!(navigator.standalone));
    setIsStandalone(mqStandalone.matches || !!(navigator.standalone));
    mqDesktop.addEventListener('change', onResize);
    mqStandalone.addEventListener('change', onStandalone);
    return () => {
      mqDesktop.removeEventListener('change', onResize);
      mqStandalone.removeEventListener('change', onStandalone);
    };
  }, []);

  useEffect(() => {
    if (!kofiBannerVisible) return;
    const apply = () => {
      if (!window.kofiwidget2) return;
      const kofiAccent = KOFI_ACCENT_BY_THEME[accent] || KOFI_ACCENT_BY_THEME.violet;
      window.kofiwidget2.init(t?.supportKofi || 'Supporta il progetto su Ko-fi', kofiAccent, KOFI_PAGE_ID);
      const mount = document.getElementById('kofi-widget-mount');
      if (mount) mount.innerHTML = window.kofiwidget2.getHTML();
    };
    if (window.kofiwidget2) {
      apply();
      return;
    }
    const existing = document.querySelector(`script[src="${KOFI_WIDGET_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener('load', apply, { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = KOFI_WIDGET_SCRIPT;
    script.async = true;
    script.onload = apply;
    document.body.appendChild(script);
  }, [accent, t, kofiBannerVisible]);

  const hideInstallBannerForever = () => {
    try { localStorage.setItem(INSTALL_BANNER_HIDDEN_KEY, 'true'); } catch (_) {}
    setInstallBannerDismissed(true);
  };

  const postponeInstallBanner = () => {
    try { localStorage.setItem(INSTALL_BANNER_POSTPONED_KEY, String(Date.now())); } catch (_) {}
    setInstallBannerPostponed(true);
  };

  const showInstallBanner = !isStandalone && !installBannerDismissed && !installBannerPostponed;
  const showMultiplayerMode = multiplayerUnlockClicks >= 5;

  const getInstallTutorialType = () => {
    if (typeof navigator === 'undefined') return 'desktop';
    const ua = navigator.userAgent || '';
    if (/iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) return 'ios';
    if (/Android/.test(ua)) return 'android';
    return 'desktop';
  };
  const installTutorialType = getInstallTutorialType();

  const getCharactersFromStorage = () => {
    try {
      const raw = localStorage.getItem(STORAGE_CHARACTERS);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  };

  const getCombatStateFromStorage = () => {
    try {
      const raw = localStorage.getItem(STORAGE_COMBAT);
      if (!raw) return { combatants: [], currentTurnIndex: 0, round: 1 };
      const parsed = JSON.parse(raw);
      return { combatants: parsed.combatants || [], currentTurnIndex: parsed.currentTurnIndex ?? 0, round: parsed.round ?? 1 };
    } catch { return { combatants: [], currentTurnIndex: 0, round: 1 }; }
  };

  const handleExportFullBackup = () => {
    try {
      const characters = getCharactersFromStorage();
      const combatState = getCombatStateFromStorage();
      const npcPresets = getNpcPresetsFromStorage();
      const backup = {
        type: 'coco-full-backup',
        version: 1,
        exportedAt: new Date().toISOString(),
        characters,
        combatState,
        npcPresets,
        settings: { theme, accent, lang },
      };
      const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `coco-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t.dataExported || 'Backup esportato');
    } catch (e) {
      console.error(e);
      toast.error(t.importError || 'Errore durante l\'esportazione');
    }
  };

  const handleImportFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const raw = event.target?.result;
        const data = JSON.parse(raw);
        if (data.type === 'coco-full-backup' && data.characters && Array.isArray(data.characters)) {
          localStorage.setItem(STORAGE_CHARACTERS, JSON.stringify(data.characters));
          if (data.combatState && Array.isArray(data.combatState.combatants)) {
            localStorage.setItem(STORAGE_COMBAT, JSON.stringify(data.combatState));
          }
          if (data.npcPresets != null && Array.isArray(data.npcPresets)) {
            localStorage.setItem(NPC_PRESETS_STORAGE_KEY, JSON.stringify(data.npcPresets));
          }
          if (data.settings) {
            if (data.settings.accent != null) setAccent(data.settings.accent);
            if (data.settings.lang != null) setLang(data.settings.lang);
          }
          toast.success(t.dataImported || 'Backup ripristinato');
        } else if (importModeRef.current === 'characters') {
          let chars = [];
          if (Array.isArray(data)) chars = data;
          else if (data.characters && Array.isArray(data.characters)) chars = data.characters;
          else { toast.error(t.importError || 'Formato file non valido'); e.target.value = ''; return; }
          const valid = chars.filter(c => c.name && c.maxHp);
          if (valid.length === 0) { toast.error(t.importError || 'Nessun personaggio valido'); e.target.value = ''; return; }
          const current = getCharactersFromStorage();
          const existingNames = new Set(current.map(c => c.name.toLowerCase()));
          const toAdd = valid.map(c => ({ ...c, id: Math.random().toString(36).substr(2, 9) })).filter(c => !existingNames.has(c.name.toLowerCase()));
          localStorage.setItem(STORAGE_CHARACTERS, JSON.stringify([...current, ...toAdd]));
          toast.success(`${toAdd.length} ${t.charactersImported || 'personaggi importati'}`);
        } else {
          toast.error(t.importError || 'File non valido. Usa un backup CoCo o un file personaggi.');
        }
        importModeRef.current = 'characters';
      } catch (err) {
        console.error(err);
        toast.error(t.importError || 'Errore durante l\'importazione');
        importModeRef.current = 'characters';
      }
      e.target.value = '';
    };
    reader.readAsText(file);
  };

  const handleExportCharacters = () => {
    const characters = getCharactersFromStorage();
    if (characters.length === 0) {
      toast.error(t.noCharactersToExport || 'Nessun personaggio da esportare');
      return;
    }
    const blob = new Blob([JSON.stringify({ version: '1.0', exportedAt: new Date().toISOString(), characters }, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'i miei personaggi.json';
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${characters.length} ${t.charactersExported || 'personaggi esportati'}`);
  };

  const characters = getCharactersFromStorage();

  return (
    <div className="coco-v2-screen min-h-[100svh] sm:min-h-[100dvh] flex flex-col overflow-y-auto overflow-x-hidden pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)] scroll-pb-4 sm:overflow-visible">
      {/* Header – safe area in alto solo qui (evita doppio padding e logo “lontano” dall’header) */}
      <header className="sticky top-0 z-50 glass shrink-0 coco-v2-header pt-[max(0.25rem,env(safe-area-inset-top))] lg:pt-0">
        <div className="w-full max-w-lg sm:max-w-2xl lg:max-w-6xl xl:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-2 lg:pb-0 sm:h-16 min-h-[3.25rem] sm:min-h-14 flex items-center justify-between gap-2 flex-wrap sm:flex-nowrap">
          <div className="flex items-baseline gap-2 min-w-0">
            <h1 className="font-bold text-lg sm:text-xl truncate">{APP_NAME}</h1>
            <span className="text-xs text-muted-foreground hidden sm:inline shrink-0">{APP_SUBTITLE}</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <input type="file" ref={fileInputRef} accept=".json" className="hidden" onChange={handleImportFile} />
            <Button
              variant="ghost"
              size="sm"
              onClick={onOpenHowItWorks}
              className="h-8 px-2 text-xs sm:hidden accent-text hover:accent-bg-10 accent-border-30 border"
              aria-label={t.howItWorksCta || 'Come funziona CoCo?'}
              title={t.howItWorksCta || 'Come funziona CoCo?'}
            >
              <BookOpen className="w-3.5 h-3.5 mr-1" />
              {t.guideShort || 'Guida'}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1.5">
                  <Download className="w-4 h-4" />
                  <span className="hidden sm:inline">{t.dataMenu || 'Dati'}</span>
                  <ChevronDown className="w-4 h-4 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel className="text-xs text-muted-foreground">{t.backupCompleteDesc || 'Personaggi + combattimento + impostazioni'}</DropdownMenuLabel>
                <DropdownMenuItem onClick={handleExportFullBackup}>
                  <Download className="w-4 h-4 mr-2" />
                  {t.backupComplete || 'Backup completo'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { importModeRef.current = 'full'; fileInputRef.current?.click(); }}>
                  <Upload className="w-4 h-4 mr-2" />
                  {t.restoreBackup || 'Ripristina backup'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-muted-foreground">{t.dataMenuCharactersSection || 'Solo personaggi'}</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => { importModeRef.current = 'characters'; fileInputRef.current?.click(); }}>
                  <Upload className="w-4 h-4 mr-2" />
                  {t.importCharactersOnly || 'Importa personaggi'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportCharacters} disabled={characters.length === 0}>
                  <Download className="w-4 h-4 mr-2" />
                  {t.exportCharactersOnly || 'Esporta personaggi'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <LanguageDropdown lang={lang} setLang={setLang} t={t} />
            <AppearanceDropdown theme={theme} toggleTheme={toggleTheme} accent={accent} setAccent={setAccent} t={t} />
          </div>
        </div>
      </header>

      {/* Rejoin Lobby Banner - Only show if there's a saved lobby */}
      {savedLobby && (
        <div className="accent-bg-10 border-b accent-border-30 shrink-0">
          <div className="w-full max-w-lg sm:max-w-2xl lg:max-w-6xl xl:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm min-w-0">
              <LinkIcon className="w-4 h-4 accent-text shrink-0" />
              <span className="accent-text opacity-90 truncate">
                {savedLobby.role === 'master' ? (t.rejoinLobbySavedMaster || 'Lobby Master salvata') : (t.rejoinLobbySavedPlayer || 'Lobby Giocatore salvata')}
              </span>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={onDismissSavedLobby}
                className="text-xs h-7 text-muted-foreground hover:text-foreground"
                title={t.dismissSavedLobby || 'Chiudi lobby salvata'}
              >
                {t.dismissSavedLobby || 'Chiudi lobby'}
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onRejoinLobby}
                className="text-xs h-7 accent-border-50 hover:accent-bg-20"
              >
                {t.rejoinLobbyButton || 'Rientra'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Banner "Installa come app" - desktop e mobile, discreto: riga con "Iniziamo!" / "Non ora", tutorial al clic */}
      {showInstallBanner && (
        <div className="w-full max-w-lg sm:max-w-2xl lg:max-w-6xl xl:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-2 sm:pt-3 shrink-0">
          {!showInstallSteps ? (
            <div className="flex flex-wrap items-center justify-center gap-2 py-1.5 px-2 sm:px-3 rounded-lg bg-muted/40 border border-border/50 w-fit max-w-full mx-auto">
              <span className="text-xs text-muted-foreground leading-8">{t.installAppHint || 'Installa CoCo come app'}</span>
              <Button variant="outline" size="sm" className="h-8 text-xs accent-border-30 hover:accent-bg-10 px-3 shrink-0" onClick={() => setShowInstallSteps(true)}>
                {t.installAppLetsGo || 'Iniziamo!'}
              </Button>
              <Button variant="ghost" size="sm" className="h-8 text-xs text-muted-foreground hover:text-foreground px-3 shrink-0" onClick={postponeInstallBanner}>
                {t.installAppNotNow || 'Non ora'}
              </Button>
            </div>
          ) : (
            <Card className="border accent-border-30 bg-card/95 relative">
              <Button variant="ghost" size="icon" className="absolute top-2 right-2 h-8 w-8 text-muted-foreground hover:text-foreground" onClick={() => setShowInstallSteps(false)} aria-label={t?.close || 'Chiudi'}>
                <X className="w-4 h-4" />
              </Button>
              <CardContent className="p-4 pr-10">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg accent-bg-10 flex items-center justify-center shrink-0">
                    <Download className="w-5 h-5 accent-text" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-sm mb-2">{t.installAppTitle || 'Installa il sito come un\'app'}</h3>
                    {installTutorialType === 'ios' ? (
                      <p className="text-xs text-muted-foreground leading-relaxed">{t.installAppStepIos || 'Su iPhone/iPad: in Safari tocca Condividi e poi «Aggiungi a Home».'}</p>
                    ) : installTutorialType === 'android' ? (
                      <p className="text-xs text-muted-foreground leading-relaxed">{t.installAppStepAndroid || 'Su Android: in Chrome tocca Menu (⋮) e scegli «Aggiungi a Home» o «Installa app».'}</p>
                    ) : (
                      <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
                        <li>{t.installAppStep1}</li>
                        <li>{t.installAppStep2}</li>
                        <li>{t.installAppStep3}</li>
                      </ol>
                    )}
                    <Button variant="ghost" size="sm" className="mt-3 h-8 text-xs text-muted-foreground hover:text-foreground px-3" onClick={hideInstallBannerForever}>
                      {t.installAppDontShow || 'Non mostrare più'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Main: padding basso calibrato sul footer fisso + safe area; da lg centrato verticalmente */}
      <main className="relative flex-1 flex flex-col items-center justify-start lg:justify-center w-full max-w-lg sm:max-w-2xl lg:max-w-6xl xl:max-w-7xl mx-auto min-h-0 px-4 sm:px-6 pt-0 pb-[max(4.5rem,calc(env(safe-area-inset-bottom)+4rem))] sm:pb-20 lg:py-8 lg:pb-32">
        <div className="flex flex-col lg:flex-row lg:flex-1 lg:items-center lg:justify-center lg:min-h-0 w-full gap-2 sm:gap-5 lg:gap-10 xl:gap-14">
          {/* Colonna sinistra: solo logo */}
          <div className="flex flex-col items-center lg:w-[min(100%,380px)] xl:w-[min(100%,420px)] lg:shrink-0 home-entrance home-entrance--1">
            <div
              className="w-full max-w-[220px] sm:max-w-[300px] md:max-w-[380px] lg:max-w-none mx-auto shrink-0"
              onClick={() => setMultiplayerUnlockClicks((prev) => Math.min(prev + 1, 5))}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setMultiplayerUnlockClicks((prev) => Math.min(prev + 1, 5));
                }
              }}
              aria-label="Logo CoCo"
            >
              <img src={logoUrl(theme)} alt="CoCo Logo" className="w-full h-auto object-contain select-none" />
            </div>
            <div className="home-entrance home-entrance--2 mt-0 sm:mt-1 w-full max-w-md text-center space-y-1 sm:space-y-2">
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t.homeAppIntro || 'CoCo è un combat tracker per D&D 5e: gestisci iniziativa, turni, punti ferita (HP), status, note e preparazione sessione in modo rapido, ordinato e ottimizzato per Master e Player.'}
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={onOpenHowItWorks}
                className="hidden sm:inline-flex h-8 text-xs accent-text hover:accent-bg-10 accent-border-30 border"
              >
                {t.howItWorksCta || 'Come funziona CoCo?'}
              </Button>
            </div>
          </div>

          {/* Colonna destra: Personaggi sopra «Seleziona modalità», poi schede (stessa larghezza max-w-md) */}
          <div className="flex flex-col items-center w-full lg:flex-1 lg:min-w-0 lg:justify-center lg:items-center space-y-2 sm:space-y-3 lg:space-y-5">
            <Button
              variant="outline"
              size="sm"
              onClick={onManageCharacters}
              className="home-entrance home-entrance--2 accent-border-30 hover:accent-bg-10 shrink-0 w-full max-w-md touch-manipulation active:scale-[0.98] motion-reduce:active:scale-100 transition-transform lg:min-h-[3.25rem] lg:px-10 lg:text-base lg:gap-2.5 [&_svg]:lg:w-5 [&_svg]:lg:h-5"
              aria-label="Gestisci Personaggi"
            >
              <User className="w-4 h-4 mr-2" />
              {t.characters || 'Personaggi'}
            </Button>
            <div className="home-entrance home-entrance--3 text-center space-y-2 lg:space-y-2.5 w-full max-w-md mx-auto px-1">
              <h2 className="coco-v2-display-title text-xl sm:text-2xl font-bold leading-tight">{t.selectMode || 'Seleziona Modalità'}</h2>
              <p className="text-sm text-muted-foreground max-w-sm mx-auto lg:max-w-md leading-snug">{t.selectModeDesc || 'Scegli come vuoi gestire il combattimento'}</p>
            </div>

            <div className="grid gap-2 sm:gap-4 w-full max-w-md mx-auto">
              {/* Personal Tracking - colori tema come Master Offline e Multiplayer */}
              <Card 
                className="coco-v2-mode-card home-entrance home-entrance--4 cursor-pointer touch-manipulation transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] accent-border-30"
                onClick={() => onSelectMode('personal')}
                data-testid="personal-mode-btn"
              >
                <CardContent className="p-4 sm:p-6 flex items-center gap-3 sm:gap-4 text-left">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full accent-bg-10 flex items-center justify-center shrink-0">
                    <User className="w-4.5 h-4.5 sm:w-6 sm:h-6 accent-text" />
                  </div>
                  <div className="flex-1 min-w-0 pr-1 sm:pr-2">
                    <h3 className="font-bold text-base sm:text-lg leading-tight">{t.personalTracking || 'Tracking Personale'}</h3>
                    <p className="text-sm text-muted-foreground leading-snug">{t.personalTrackingDesc || 'Gestisci i tuoi personaggi localmente'}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground shrink-0" />
                </CardContent>
              </Card>

              {/* Master Offline Mode */}
              <Card 
                className="coco-v2-mode-card home-entrance home-entrance--5 cursor-pointer touch-manipulation transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] accent-border-30"
                onClick={() => onSelectMode('master-offline')}
                data-testid="master-offline-btn"
              >
                <CardContent className="p-4 sm:p-6 flex items-center gap-3 sm:gap-4 text-left">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full accent-bg-10 flex items-center justify-center shrink-0">
                    <Crown className="w-4.5 h-4.5 sm:w-6 sm:h-6 accent-text" />
                  </div>
                  <div className="flex-1 min-w-0 pr-1 sm:pr-2">
                    <h3 className="font-bold text-base sm:text-lg leading-tight">
                      {t.masterOffline || 'Master Offline'}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-snug">{t.masterOfflineDesc || 'Gestisci i combattimenti al tavolo: iniziativa, turni e PF. Prepara la sessione con NPC e appunti, poi entra in battaglia.'}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground shrink-0" />
                </CardContent>
              </Card>

              {showMultiplayerMode && (
                <Card
                  className="coco-v2-mode-card home-entrance home-entrance--6 cursor-pointer touch-manipulation transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] accent-border-30"
                  onClick={() => onSelectMode('multiplayer')}
                  data-testid="multiplayer-btn"
                >
                  <CardContent className="p-4 sm:p-6 flex items-center gap-3 sm:gap-4 text-left">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full accent-bg-10 flex items-center justify-center shrink-0">
                      <Network className="w-4.5 h-4.5 sm:w-6 sm:h-6 accent-text" />
                    </div>
                    <div className="flex-1 min-w-0 pr-1 sm:pr-2">
                      <h3 className="font-bold text-base sm:text-lg leading-tight">{t.multiplayer || 'Multiplayer'}</h3>
                      <p className="text-sm text-muted-foreground leading-snug">{t.multiplayerDesc || 'Master online e entra in lobby'}</p>
                    </div>
                    <Badge variant="outline" className="text-xs accent-bg-10 accent-text accent-border-30 shrink-0">
                      {t.multiplayerBeta || 'Beta'}
                    </Badge>
                    <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground shrink-0" />
                  </CardContent>
                </Card>
              )}

            </div>
          </div>
        </div>
      </main>
      <footer className="app-footer-pinned">
        <div className="app-footer-island mx-auto max-w-md space-y-1.5 rounded-2xl px-3 py-2 text-center backdrop-blur-md">
          {kofiBannerVisible ? (
            <>
              <p className="flex flex-wrap items-center justify-center gap-1 text-[11px] leading-tight text-muted-foreground">
                {t.footerSupportText || 'CoCo è senza pubblicità ed è gratis (ma non per me 😔) quindi se vuoi:'}
              </p>
              <div className="kofi-widget-wrap flex justify-center [&_a.kofi-button]:transition-opacity" id="kofi-widget-mount" />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 text-[11px] text-muted-foreground hover:text-foreground px-2"
                onClick={() => setKofiBannerVisiblePersist(false)}
              >
                {t.kofiHideBanner || 'Nascondi banner'}
              </Button>
            </>
          ) : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 text-xs accent-border-30 hover:accent-bg-10"
              onClick={() => setKofiBannerVisiblePersist(true)}
            >
              {t.kofiShowBanner || 'Mostra supporto Ko-fi'}
            </Button>
          )}
          <p className="flex flex-wrap items-center justify-center gap-1 text-[11px] leading-tight text-muted-foreground">
            {t.footerMadeWithLove || 'Made with love in 2026 by TakaDev 💛'}
            <span className="text-muted-foreground/50" aria-hidden>·</span>
            <RouterLink to="/privacy" className="underline underline-offset-2 hover:text-foreground transition-colors">
              {t.privacyPolicy || 'Privacy'}
            </RouterLink>
          </p>
        </div>
      </footer>
    </div>
  );
};

const HowItWorksScreen = ({ onBack, t, theme, toggleTheme, accent, setAccent, lang, setLang }) => {
  const sections = [
    {
      title: t.howItWorksInitiativeTitle || '1) Tracker iniziativa D&D 5e',
      body: t.howItWorksInitiativeBody || 'Monitora ordine di iniziativa, turno attivo e round del combattimento D&D 5e in pochi tocchi. Perfetto per tenere il ritmo al tavolo senza perdere informazioni.',
      icon: <Zap className="w-5 h-5 accent-text" />,
    },
    {
      title: t.howItWorksHpTitle || '2) Gestione HP, status e risorse',
      body: t.howItWorksHpBody || 'Aggiorna rapidamente punti ferita (HP), KO, condizioni, slot incantesimo e risorse turno per personaggi e NPC. Tutto in una vista chiara per il combat tracking.',
      icon: <Heart className="w-5 h-5 text-red-500" />,
    },
    {
      title: t.howItWorksPrepTitle || '3) Tool Master per preparazione sessione',
      body: t.howItWorksPrepBody || 'Organizza luoghi, note, personaggi, dialoghi, oggetti e missioni prima della sessione. In gioco hai tutto pronto in un unico strumento per Dungeon Master.',
      icon: <BookOpen className="w-5 h-5 accent-text" />,
    },
  ];

  return (
    <div className="coco-v2-screen min-h-[100svh] sm:min-h-[100dvh] flex flex-col">
      <Toaster theme={theme} />
      <header className="sticky top-0 z-50 glass coco-v2-header">
        <div className="w-full max-w-lg sm:max-w-2xl lg:max-w-6xl xl:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={onBack}><ArrowLeft className="w-5 h-5" /></Button>
            <h1 className="font-semibold tracking-tight text-base sm:text-lg truncate">{t.howItWorksTitle || 'Come funziona CoCo'}</h1>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <LanguageDropdown lang={lang} setLang={setLang} t={t} />
            <AppearanceDropdown theme={theme} toggleTheme={toggleTheme} accent={accent} setAccent={setAccent} t={t} />
          </div>
        </div>
      </header>

      <main className="flex-1 w-full max-w-lg sm:max-w-2xl lg:max-w-6xl xl:max-w-7xl mx-auto min-w-0 px-4 sm:px-6 lg:px-8 py-6 pb-10">
        <div className="flex flex-col lg:flex-row lg:items-start gap-4 lg:gap-6 xl:gap-8">
          <aside className="w-full lg:w-[280px] xl:w-[320px] lg:shrink-0">
            <p className="text-[15px] text-muted-foreground leading-7 max-w-prose">
              {t.howItWorksIntro || 'Guida rapida a CoCo, app per iniziativa D&D 5e e gestione combattimento: scopri come usare tracker turni, HP, status e strumenti Master in modo semplice.'}
            </p>
          </aside>
          <section className="flex-1 min-w-0 space-y-3 sm:space-y-4">
            {sections.map((section) => (
              <Card key={section.title} className="coco-v2-card border accent-border-30">
                <CardContent className="p-4 sm:p-5">
                  <div className="flex items-start gap-3.5">
                    <div className="w-10 h-10 rounded-lg accent-bg-10 flex items-center justify-center shrink-0 mt-0.5">
                      {section.icon}
                    </div>
                    <div className="min-w-0">
                      <h2 className="font-semibold tracking-tight text-base sm:text-lg leading-6">{section.title}</h2>
                      <p className="text-[15px] text-muted-foreground leading-7 mt-1.5">{section.body}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </section>
        </div>
      </main>
    </div>
  );
};

// ============ MULTIPLAYER SCREEN (pagina dedicata) ============

const MultiplayerScreen = ({ onBack, onSelectMode, t, theme, toggleTheme, accent, setAccent, lang, setLang }) => {
  return (
    <div className="coco-v2-screen min-h-[100svh] sm:min-h-[100dvh] flex flex-col">
      <Toaster theme={theme} />
      <header className="sticky top-0 z-50 glass coco-v2-header">
        <div className="w-full max-w-lg sm:max-w-2xl lg:max-w-6xl xl:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-0 sm:h-16 min-h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <Button variant="ghost" size="icon" className="shrink-0" onClick={onBack}><ArrowLeft className="w-5 h-5" /></Button>
            <div className="flex items-center gap-2 min-w-0 flex-wrap">
              <Network className="w-5 h-5 accent-text shrink-0" />
              <h1 className="font-bold text-base sm:text-lg truncate">{t.multiplayer || 'Multiplayer'}</h1>
              <Badge variant="outline" className="text-xs accent-bg-10 accent-text accent-border-30 shrink-0">
                {t.multiplayerBeta || 'Beta'}
              </Badge>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <LanguageDropdown lang={lang} setLang={setLang} t={t} />
            <AppearanceDropdown theme={theme} toggleTheme={toggleTheme} accent={accent} setAccent={setAccent} t={t} />
          </div>
        </div>
      </header>
      <main className="flex-1 w-full max-w-lg sm:max-w-2xl lg:max-w-6xl xl:max-w-7xl mx-auto min-w-0 px-4 sm:px-6 lg:px-8 py-5 sm:py-6 pb-8 sm:pb-6 overflow-x-hidden">
        <div className="flex flex-col lg:flex-row lg:items-start gap-4 lg:gap-6 xl:gap-8">
          <aside className="w-full lg:w-[280px] xl:w-[320px] lg:shrink-0">
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t.multiplayerDesc || 'Master online e entra in lobby'}
            </p>
          </aside>
          <section className="flex-1 min-w-0">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
          <Card 
            className="cursor-pointer hover:border-accent/50 hover:accent-border-50 active:scale-[0.99] active:opacity-95 transition-all border accent-border-30 flex flex-col h-full min-h-[220px] sm:min-h-0 rounded-2xl sm:rounded-xl shadow-sm hover:shadow-md"
            onClick={() => onSelectMode('master')}
            data-testid="master-mode-btn"
          >
            <CardContent className="p-5 sm:p-6 flex flex-col flex-1 gap-4 min-h-0">
              <div className="flex items-start gap-3 sm:gap-3">
                <div className="w-12 h-12 rounded-xl sm:rounded-full accent-bg-10 flex items-center justify-center shrink-0">
                  <Users className="w-6 h-6 accent-text" />
                </div>
                <div className="min-w-0 flex-1 pt-0.5">
                  <h3 className="font-bold text-base sm:text-lg">{t.masterMode || 'Master Online'}</h3>
                  <p className="text-sm text-muted-foreground mt-0.5">{t.masterModeDesc || 'Crea una lobby multiplayer'}</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed flex-1 min-h-0">
                {t.masterModeDescLong || 'Sei il Dungeon Master? Crea una lobby, condividi il codice con i giocatori e gestisci iniziativa, HP e turni per tutti in tempo reale.'}
              </p>
              <div className="flex justify-end pt-2 border-t border-border/50">
                <span className="text-xs font-medium accent-text flex items-center gap-1 py-1.5">
                  {t.masterMode || 'Master Online'}
                  <ChevronRight className="w-4 h-4" />
                </span>
              </div>
            </CardContent>
          </Card>
          <Card 
            className="cursor-pointer hover:border-accent/50 hover:accent-border-50 active:scale-[0.99] active:opacity-95 transition-all border accent-border-30 flex flex-col h-full min-h-[220px] sm:min-h-0 rounded-2xl sm:rounded-xl shadow-sm hover:shadow-md"
            onClick={() => onSelectMode('join')}
            data-testid="join-mode-btn"
          >
            <CardContent className="p-5 sm:p-6 flex flex-col flex-1 gap-4 min-h-0">
              <div className="flex items-start gap-3 sm:gap-3">
                <div className="w-12 h-12 rounded-xl sm:rounded-full accent-bg-10 flex items-center justify-center shrink-0">
                  <LinkIcon className="w-6 h-6 accent-text" />
                </div>
                <div className="min-w-0 flex-1 pt-0.5">
                  <h3 className="font-bold text-base sm:text-lg">{t.joinLobby || 'Entra in Lobby'}</h3>
                  <p className="text-sm text-muted-foreground mt-0.5">{t.joinLobbyDesc || 'Unisciti a una lobby esistente'}</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed flex-1 min-h-0">
                {t.joinLobbyDescLong || 'Hai il codice lobby dal tuo Master? Inseriscilo per entrare nella partita e seguire il combattimento dal tuo dispositivo in sincronia con il tavolo.'}
              </p>
              <div className="flex justify-end pt-2 border-t border-border/50">
                <span className="text-xs font-medium accent-text flex items-center gap-1 py-1.5">
                  {t.joinLobby || 'Entra in Lobby'}
                  <ChevronRight className="w-4 h-4" />
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
          </section>
        </div>
      </main>
    </div>
  );
};

// ============ CHARACTER MANAGEMENT ============

const CharacterModal = ({ isOpen, onClose, onSave, editingCharacter, t, characters = [] }) => {
  const [form, setForm] = useState({ 
    name: '', 
    maxHp: 20, 
    ac: '', 
    baseInitiative: '', 
    initiativeBonus: 0,
    speed: 30,
    hitDiceTotal: 0,
    hitDiceSides: 8,
    spellSlotsMax: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0 },
    defaultStatuses: [],
    isFamiglio: false,
    famiglioDi: '',
  });
  const [famiglioSearch, setFamiglioSearch] = useState('');
  const [famiglioOpen, setFamiglioOpen] = useState(false);
  const [spellSlotsOpen, setSpellSlotsOpen] = useState(false);
  const namePlaceholderRef = useRef(getRandomNamePlaceholder(t));
  const [, setPlaceholderTick] = useState(0);
  useEffect(() => {
    if (isOpen) {
      namePlaceholderRef.current = getRandomNamePlaceholder(t);
      setPlaceholderTick(t => t + 1);
      if (editingCharacter) {
        const maxByLevel = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0 };
        const slots = editingCharacter.spellSlots || {};
        Object.keys(maxByLevel).forEach((lvl) => {
          const n = Number(lvl);
          maxByLevel[n] = typeof slots[n]?.max === 'number' ? slots[n].max : 0;
        });
        const hd = editingCharacter.hitDice || {};
        setForm({
          name: editingCharacter.name,
          maxHp: editingCharacter.maxHp,
          ac: editingCharacter.ac || '',
          baseInitiative: editingCharacter.baseInitiative || '',
          initiativeBonus: typeof editingCharacter.initiativeBonus === 'number' ? editingCharacter.initiativeBonus : 0,
          speed: typeof editingCharacter.speed === 'number' ? editingCharacter.speed : 30,
          hitDiceTotal: typeof hd.total === 'number' ? hd.total : 0,
          hitDiceSides: [6, 8, 10, 12].includes(hd.sides) ? hd.sides : 8,
          spellSlotsMax: maxByLevel,
          defaultStatuses: editingCharacter.defaultStatuses || [],
          isFamiglio: !!editingCharacter.isFamiglio,
          famiglioDi: editingCharacter.famiglioDi || '',
        });
      } else {
        setForm({ 
          name: '', 
          maxHp: 20, 
          ac: '', 
          baseInitiative: '', 
          initiativeBonus: 0,
          speed: 30,
          hitDiceTotal: 0,
          hitDiceSides: 8,
          spellSlotsMax: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0 },
          defaultStatuses: [],
          isFamiglio: false,
          famiglioDi: '',
        });
      }
      setFamiglioSearch('');
      setFamiglioOpen(false);
      setSpellSlotsOpen(false);
    }
  }, [editingCharacter, isOpen, t]);

  const handleSave = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!form.name.trim()) return;
    const spellSlots = {};
    Object.entries(form.spellSlotsMax || {}).forEach(([lvlStr, max]) => {
      const level = Number(lvlStr);
      const parsedMax = Math.max(0, parseInt(max) || 0);
      if (parsedMax > 0) {
        spellSlots[level] = { max: parsedMax, used: 0 };
      }
    });
    const hitDiceTotal = Math.max(0, parseInt(form.hitDiceTotal, 10) || 0);
    const hitDiceSides = [6, 8, 10, 12].includes(form.hitDiceSides) ? form.hitDiceSides : 8;
    onSave({
      name: form.name.trim(),
      maxHp: parseInt(form.maxHp) || 20,
      ac: form.ac ? parseInt(form.ac) : null,
      baseInitiative: form.baseInitiative ? parseInt(form.baseInitiative) : null,
      initiativeBonus: parseInt(form.initiativeBonus) || 0,
      speed: parseInt(form.speed) || 30,
      hitDice: hitDiceTotal > 0 ? { total: hitDiceTotal, sides: hitDiceSides } : null,
      spellSlots,
      defaultStatuses: form.defaultStatuses,
      isFamiglio: !!form.isFamiglio,
      famiglioDi: form.isFamiglio && form.famiglioDi ? form.famiglioDi : null,
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="character-modal" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{editingCharacter ? t.editCharacter : t.addCharacter}</DialogTitle>
          <DialogDescription className="sr-only">{t?.characterFormDesc || 'Character form'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1.5 block">{t.name}</label>
            <Input 
              value={form.name} 
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))} 
              placeholder={namePlaceholderRef.current}
              className="placeholder:text-muted-foreground"
              data-testid="char-name-input"
            />
          </div>

          <div className="flex flex-col gap-3 py-2">
            <div className="flex items-center justify-between gap-2">
              <label className="text-sm font-medium">{t.famiglio || 'Famiglio'}</label>
              <Switch
                checked={!!form.isFamiglio}
                onCheckedChange={(checked) => setForm(prev => ({ ...prev, isFamiglio: !!checked, famiglioDi: checked ? prev.famiglioDi : '' }))}
              />
            </div>
            {form.isFamiglio && (
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground">{t.famiglioOf || 'Famiglio di'}</label>
                <Popover open={famiglioOpen} onOpenChange={setFamiglioOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-between text-left font-normal h-9 text-sm" type="button">
                      <span className="truncate">
                        {form.famiglioDi ? (characters.find(c => c.id === form.famiglioDi)?.name || t.famiglioOfPlaceholder) : (t.famiglioOfPlaceholder || 'Seleziona personaggio')}
                      </span>
                      <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                    <Input
                      placeholder={t.searchCharacter || 'Cerca...'}
                      value={famiglioSearch}
                      onChange={(e) => setFamiglioSearch(e.target.value)}
                      className="rounded-b-none border-b border-x-0 border-t-0 rounded-t-md h-9"
                    />
                    <div className="max-h-[200px] overflow-y-auto">
                      {characters
                        .filter(c => c.id !== editingCharacter?.id)
                        .filter(c => !famiglioSearch.trim() || c.name.toLowerCase().includes(famiglioSearch.toLowerCase()))
                        .map(c => (
                          <button
                            key={c.id}
                            type="button"
                            className="w-full px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground flex items-center gap-2"
                            onClick={() => { setForm(prev => ({ ...prev, famiglioDi: c.id })); setFamiglioOpen(false); setFamiglioSearch(''); }}
                          >
                            <User className="w-4 h-4 shrink-0 text-muted-foreground" />
                            <span className="truncate">{c.name}</span>
                          </button>
                        ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-sm font-medium mb-1.5 block">{t.maxHp}</label>
              <Input 
                type="number" 
                value={form.maxHp} 
                onChange={(e) => setForm(prev => ({ ...prev, maxHp: e.target.value }))} 
                min="1"
                data-testid="char-maxhp-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">CA ({t.optional})</label>
              <Input 
                type="number" 
                value={form.ac} 
                onChange={(e) => setForm(prev => ({ ...prev, ac: e.target.value }))} 
                placeholder="--"
                data-testid="char-ac-input"
                min="1"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">{t.initiativeValue} ({t.optional})</label>
              <Input 
                type="number" 
                value={form.baseInitiative} 
                onChange={(e) => setForm(prev => ({ ...prev, baseInitiative: e.target.value }))} 
                placeholder="--"
                data-testid="char-init-input"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium mb-1.5 block">{t.initiativeBonus || 'Bonus iniziativa'}</label>
              <Input
                type="number"
                value={form.initiativeBonus}
                onChange={(e) => setForm(prev => ({ ...prev, initiativeBonus: e.target.value }))}
                placeholder="0"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">{t.speedMovement || t.speed || 'Velocità di movimento'}</label>
              <Input
                type="number"
                value={form.speed}
                onChange={(e) => setForm(prev => ({ ...prev, speed: e.target.value }))}
                placeholder="30"
                min="0"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium mb-1.5 block">{t.hitDiceTotal || 'Numero dadi vita'}</label>
              <Input
                type="number"
                value={form.hitDiceTotal}
                onChange={(e) => setForm(prev => ({ ...prev, hitDiceTotal: e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10) || 0) }))}
                placeholder="0"
                min="0"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">{t.hitDiceSides || 'Tipo dado'}</label>
              <div className="flex gap-1 flex-wrap">
                {[6, 8, 10, 12].map((s) => (
                  <Button key={s} type="button" variant={form.hitDiceSides === s ? 'default' : 'outline'} size="sm" className="h-8 font-mono" onClick={() => setForm(prev => ({ ...prev, hitDiceSides: s }))}>d{s}</Button>
                ))}
              </div>
            </div>
          </div>

          <div className="border border-border/60 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setSpellSlotsOpen(prev => !prev)}
              className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left text-sm font-medium bg-muted/30 hover:bg-muted/50 transition-colors"
            >
              <span>{t.spellSlots || 'Slot incantesimo'}</span>
              <ChevronDown className={`w-4 h-4 shrink-0 text-muted-foreground transition-transform ${spellSlotsOpen ? '' : '-rotate-90'}`} />
            </button>
            {spellSlotsOpen && (
              <div className="p-3 pt-0 space-y-2 border-t border-border/50">
                <div className="grid grid-cols-3 gap-2">
                  {[1,2,3,4,5,6,7,8,9].map((lvl) => (
                    <div key={lvl} className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-6">L{lvl}</span>
                      <Input
                        type="number"
                        min="0"
                        value={form.spellSlotsMax?.[lvl] ?? 0}
                        onChange={(e) => setForm(prev => ({ 
                          ...prev, 
                          spellSlotsMax: { 
                            ...prev.spellSlotsMax, 
                            [lvl]: e.target.value 
                          } 
                        }))}
                        className="h-8"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} type="button">{t.cancel}</Button>
          <Button onClick={handleSave} disabled={!form.name.trim()} type="button" data-testid="char-save-btn">{t.save}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PersonalTrackingScreen = ({ onBack, t, theme, toggleTheme, accent, setAccent, lang, setLang, charactersOnly = false }) => {
  const { characters, addCharacter, updateCharacter, deleteCharacter, duplicateCharacter, exportCharacters, importCharacters } = useCharacters();
  const combat = useCombat();
  const { presets: npcPresets, setPresets: setNpcPresets } = useNpcPresets();
  const [view, setView] = useState('characters'); // 'characters' | 'combat'
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCharacter, setEditingCharacter] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const [selectedCharacters, setSelectedCharacters] = useState([]); // Multi-select
  const fileInputRef = useRef(null);
  const importModeRef = useRef('characters'); // 'full' | 'characters'
  
  // Mini-tutorial states
  const [showExportTip, setShowExportTip] = useState(() => !localStorage.getItem('tip-export-dismissed'));
  const [showSelectTip, setShowSelectTip] = useState(() => !localStorage.getItem('tip-select-dismissed'));
  const [hideTutorialsForever, setHideTutorialsForever] = useState(() => localStorage.getItem('hide-all-tips') === 'true');

  // Dismiss all tips forever
  const handleHideAllTips = () => {
    localStorage.setItem('hide-all-tips', 'true');
    localStorage.setItem('tip-export-dismissed', 'true');
    localStorage.setItem('tip-select-dismissed', 'true');
    setHideTutorialsForever(true);
    setShowExportTip(false);
    setShowSelectTip(false);
    toast.success(t.tutorialsDisabled || 'Tutorial disabilitati');
  };

  // Dismiss individual tips
  const dismissExportTip = () => {
    localStorage.setItem('tip-export-dismissed', 'true');
    setShowExportTip(false);
  };

  const dismissSelectTip = () => {
    localStorage.setItem('tip-select-dismissed', 'true');
    setShowSelectTip(false);
  };

  // Check if tutorial has been completed
  useEffect(() => {
    const tutorialCompleted = localStorage.getItem('tutorial-personal-completed');
    if (!tutorialCompleted) {
      setShowTutorial(true);
    }
  }, []);

  // Toggle character selection
  const toggleCharacterSelection = (charId) => {
    setSelectedCharacters(prev => 
      prev.includes(charId) 
        ? prev.filter(id => id !== charId)
        : [...prev, charId]
    );
  };

  // Start combat with selected characters
  const handleStartCombatWithSelected = () => {
    const namesInBattle = new Set(combat.combatants.map(c => c.name));
    let addedCount = 0;
    selectedCharacters.forEach(charId => {
      const character = characters.find(c => c.id === charId);
      if (character && !namesInBattle.has(character.name)) {
        combat.addCombatant({
          name: character.name,
          hp: character.maxHp,
          maxHp: character.maxHp,
          ac: character.ac || null,
          initiative: character.baseInitiative || 10,
          initiativeBonus: typeof character.initiativeBonus === 'number' ? character.initiativeBonus : 0,
          speed: typeof character.speed === 'number' ? character.speed : 30,
          hitDice: character.hitDice && character.hitDice.total > 0 ? character.hitDice : null,
          spellSlots: character.spellSlots || {},
          statuses: character.defaultStatuses?.map(s => ({ ...s, id: Math.random().toString(36).substr(2, 9), duration: null })) || [],
          famiglioDiName: character.famiglioDi ? (characters.find(c => c.id === character.famiglioDi)?.name) || null : null,
        });
        addedCount += 1;
      }
    });
    setSelectedCharacters([]);
    setView('combat');
    if (addedCount > 0) {
      toast.success(`${addedCount} personaggi aggiunti al combattimento`);
    } else {
      toast.info(t.alreadyInBattle || 'I personaggi selezionati sono già in battaglia');
    }
  };

  // Enter combat directly without characters
  const handleEnterCombatDirect = () => {
    setView('combat');
  };

  // Export solo personaggi
  const handleExportCharacters = () => {
    if (characters.length === 0) {
      toast.error(t.noCharactersToExport || 'Nessun personaggio da esportare');
      return;
    }
    exportCharacters();
    toast.success(`${characters.length} ${t.charactersExported || 'personaggi esportati'}`);
  };

  // Export backup completo (personaggi + combattimento + preset NPC + impostazioni)
  const handleExportFullBackup = () => {
    try {
      const combatJson = combat.exportData();
      const combatState = combatJson ? JSON.parse(combatJson) : { combatants: [], currentTurnIndex: 0, round: 1 };
      const backup = {
        type: 'coco-full-backup',
        version: 1,
        exportedAt: new Date().toISOString(),
        characters,
        combatState: { combatants: combatState.combatants, currentTurnIndex: combatState.currentTurnIndex ?? 0, round: combatState.round ?? 1 },
        npcPresets,
        settings: { theme, accent, lang },
      };
      const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `coco-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t.dataExported || 'Backup esportato');
    } catch (e) {
      console.error(e);
      toast.error(t.importError || 'Errore durante l\'esportazione');
    }
  };

  // Import unificato: full backup o solo personaggi (in base a importModeRef)
  const handleImportFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const raw = event.target?.result;
        const data = JSON.parse(raw);
        if (data.type === 'coco-full-backup' && data.characters && Array.isArray(data.characters)) {
          // Ripristino backup completo
          importCharacters(JSON.stringify({ characters: data.characters }), 'replace');
          if (data.combatState && Array.isArray(data.combatState.combatants)) {
            combat.importData(JSON.stringify(data.combatState));
          }
          if (data.npcPresets != null && Array.isArray(data.npcPresets)) {
            setNpcPresets(data.npcPresets);
          }
          if (data.settings) {
            if (data.settings.accent != null) setAccent(data.settings.accent);
            if (data.settings.lang != null) setLang(data.settings.lang);
          }
          toast.success(t.dataImported || 'Backup ripristinato');
        } else if (importModeRef.current === 'characters') {
          const result = importCharacters(raw);
          if (result.success) toast.success(`${result.count} ${t.charactersImported || 'personaggi importati'}`);
          else toast.error(result.error);
        } else {
          toast.error(t.importError || 'File non valido. Usa un backup CoCo o un file personaggi.');
        }
      } catch (err) {
        console.error(err);
        toast.error(t.importError || 'Errore durante l\'importazione');
      }
      importModeRef.current = 'characters';
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleSaveCharacter = (data) => {
    if (editingCharacter) {
      updateCharacter(editingCharacter.id, data);
      toast.success(t.characterUpdated || 'Personaggio aggiornato');
    } else {
      addCharacter(data);
      toast.success(t.characterSaved || 'Personaggio salvato');
    }
    setEditingCharacter(null);
  };

  const handleStartCombat = (character) => {
    combat.addCombatant({
      name: character.name,
      hp: character.maxHp,
      maxHp: character.maxHp,
      ac: character.ac || null,
      initiative: character.baseInitiative || 10,
      initiativeBonus: typeof character.initiativeBonus === 'number' ? character.initiativeBonus : 0,
      speed: typeof character.speed === 'number' ? character.speed : 30,
      hitDice: character.hitDice && character.hitDice.total > 0 ? character.hitDice : null,
      spellSlots: character.spellSlots || {},
      statuses: character.defaultStatuses?.map(s => ({ ...s, id: Math.random().toString(36).substr(2, 9), duration: null })) || [],
      famiglioDiName: character.famiglioDi ? (characters.find(c => c.id === character.famiglioDi)?.name) || null : null,
    });
    setView('combat');
    toast.success(`${character.name} ${t.addedToCombat || 'aggiunto al combattimento'}`);
  };

  const handleExport = () => {
    const data = combat.exportData();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dnd-combat-${new Date().toISOString().slice(0,10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(t.dataExported);
  };

  const handleImport = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const success = combat.importData(event.target?.result);
        toast[success ? 'success' : 'error'](success ? t.dataImported : t.importError);
      };
      reader.readAsText(file);
    }
    e.target.value = '';
  };

  const handleAddCharacterToCombat = (character) => {
    combat.addCombatant({
      name: character.name,
      hp: character.maxHp,
      maxHp: character.maxHp,
      ac: character.ac || null,
      initiative: character.baseInitiative || 10,
      initiativeBonus: typeof character.initiativeBonus === 'number' ? character.initiativeBonus : 0,
      speed: typeof character.speed === 'number' ? character.speed : 30,
      hitDice: character.hitDice && character.hitDice.total > 0 ? character.hitDice : null,
      spellSlots: character.spellSlots || {},
      statuses: character.defaultStatuses?.map(s => ({ ...s, id: Math.random().toString(36).substr(2, 9), duration: null })) || [],
      famiglioDiName: character.famiglioDi ? (characters.find(c => c.id === character.famiglioDi)?.name) || null : null,
    });
    toast.success(`${character.name} ${t.addedToCombat || 'aggiunto al combattimento'}`);
  };

  if (view === 'combat') {
    return (
      <CombatScreen 
        combat={combat}
        onBack={() => setView('characters')}
        t={t}
        theme={theme}
        toggleTheme={toggleTheme}
        accent={accent}
        setAccent={setAccent}
        lang={lang}
        setLang={setLang}
        onExport={handleExport}
        onImport={() => fileInputRef.current?.click()}
        fileInputRef={fileInputRef}
        handleImport={handleImport}
        isMaster={true}
        isPersonal={true}
        savedCharacters={characters}
        onUpdateSavedCharacterXp={updateCharacter}
        onAddSavedCharacter={handleAddCharacterToCombat}
        onGoHome={onBack}
      />
    );
  }

  return (
    <div className="coco-v2-screen min-h-[100svh] sm:min-h-[100dvh]">
      <Toaster theme={theme} />
      
      {/* Header */}
      <header className="sticky top-0 z-50 glass coco-v2-header">
        <div className="w-full max-w-lg sm:max-w-2xl lg:max-w-6xl xl:max-w-7xl mx-auto min-w-0 px-4 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={onBack}><ArrowLeft className="w-5 h-5" /></Button>
            <h1 className="font-bold text-lg accent-text truncate">{charactersOnly ? (t.characters || 'Personaggi') : (t.personalHubTitle || 'Hub del Player')}</h1>
          </div>
          <div className="flex items-center gap-1">
            <input type="file" ref={fileInputRef} accept=".json" className="hidden" onChange={handleImportFile} />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1.5 accent-text hover:accent-bg-10">
                  <Download className="w-4 h-4" />
                  <span className="hidden sm:inline">{t.dataMenu || 'Dati'}</span>
                  <ChevronDown className="w-4 h-4 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel className="text-xs text-muted-foreground">{t.backupCompleteDesc || 'Personaggi + combattimento + impostazioni'}</DropdownMenuLabel>
                <DropdownMenuItem onClick={handleExportFullBackup}>
                  <Download className="w-4 h-4 mr-2" />
                  {t.backupComplete || 'Backup completo'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { importModeRef.current = 'full'; fileInputRef.current?.click(); }}>
                  <Upload className="w-4 h-4 mr-2" />
                  {t.restoreBackup || 'Ripristina backup'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-muted-foreground">{t.dataMenuCharactersSection || 'Solo personaggi'}</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => { importModeRef.current = 'characters'; fileInputRef.current?.click(); }}>
                  <Upload className="w-4 h-4 mr-2" />
                  {t.importCharactersOnly || 'Importa personaggi'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportCharacters} disabled={characters.length === 0}>
                  <Download className="w-4 h-4 mr-2" />
                  {t.exportCharactersOnly || 'Esporta personaggi'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <LanguageDropdown lang={lang} setLang={setLang} t={t} />
            <AppearanceDropdown theme={theme} toggleTheme={toggleTheme} accent={accent} setAccent={setAccent} t={t} />
          </div>
        </div>
      </header>

      {/* Characters List */}
      <main className={`w-full max-w-lg sm:max-w-2xl lg:max-w-6xl xl:max-w-7xl mx-auto min-w-0 px-4 sm:px-6 lg:px-8 pt-3 sm:pt-4 overflow-x-hidden pad-main-above-bottom-nav`}>
        <div className="flex flex-col lg:flex-row lg:items-start gap-4 lg:gap-6 xl:gap-8">
          <aside className="w-full lg:w-[320px] xl:w-[360px] lg:shrink-0 space-y-3">
            <Card className="border accent-border-30 bg-background/55 backdrop-blur-sm hidden lg:block">
              <CardContent className="p-3 space-y-2">
                <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">{charactersOnly ? (t.characters || 'Personaggi') : (t.personalHubTitle || 'Hub del Player')}</p>
                <div className="flex items-center gap-2 text-sm">
                  <Badge variant="outline" className="accent-border-30">{characters.length} {t.characters || 'personaggi'}</Badge>
                  {!charactersOnly && (
                    <Badge variant="outline" className="accent-border-30">
                      {combat.combatants.length} {t.inBattle || 'in battaglia'}
                    </Badge>
                  )}
                </div>
                <div className="flex flex-col gap-2 pt-1">
                  <Button className="accent-bg hover:opacity-90 text-white border-0" onClick={() => setIsModalOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    {t.addCharacter || 'Nuovo Personaggio'}
                  </Button>
                  {!charactersOnly && characters.length > 0 && (
                    <>
                      <Button
                        variant="outline"
                        className="accent-border-50 accent-text hover:accent-bg-10"
                        onClick={() => {
                          const namesInBattle = new Set(combat.combatants.map(c => c.name));
                          const selectableCharacterIds = characters.filter(c => !namesInBattle.has(c.name)).map(c => c.id);
                          const areAllCharactersSelected = selectableCharacterIds.length > 0 && selectableCharacterIds.every(id => selectedCharacters.includes(id));
                          setSelectedCharacters(areAllCharactersSelected ? [] : selectableCharacterIds);
                        }}
                      >
                        <Check className="w-4 h-4 mr-2" />
                        {(() => {
                          const namesInBattle = new Set(combat.combatants.map(c => c.name));
                          const selectableCharacterIds = characters.filter(c => !namesInBattle.has(c.name)).map(c => c.id);
                          const areAllCharactersSelected = selectableCharacterIds.length > 0 && selectableCharacterIds.every(id => selectedCharacters.includes(id));
                          return areAllCharactersSelected ? (t.deselect || 'Deseleziona') : (t.selectAll || 'Seleziona tutto');
                        })()}
                      </Button>
                      <div className="text-xs text-muted-foreground px-1">
                        {selectedCharacters.length} {t.charactersSelected || 'personaggi selezionati'}
                      </div>
                    </>
                  )}
                  {!charactersOnly && (
                    selectedCharacters.length > 0 ? (
                      <Button className="accent-bg hover:opacity-90 text-white border-0" onClick={handleStartCombatWithSelected}>
                        <Swords className="w-4 h-4 mr-2" />
                        {t.fightWith || 'Combatti con'} {selectedCharacters.length} {t.selected || 'selezionati'}
                      </Button>
                    ) : (
                      <Button variant="outline" className="accent-border-50 accent-text hover:accent-bg-10" onClick={handleEnterCombatDirect}>
                        <Swords className="w-4 h-4 mr-2" />
                        {t.enterBattle || 'Entra in Battaglia'}
                      </Button>
                    )
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Mini Tutorial Tips */}
            {!hideTutorialsForever && (showExportTip || showSelectTip) && (
              <div className="space-y-3">
            {/* Export/Import Tutorial */}
            {showExportTip && (
              <div className="relative p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <button 
                  onClick={dismissExportTip}
                  className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="flex items-start gap-3 pr-6">
                  <div className="p-2 bg-blue-500/20 rounded-lg">
                    <Download className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm mb-1">{t.saveTutorialTitle || 'Salva i tuoi personaggi'}</h4>
                    <p className="text-xs text-muted-foreground">
                      {t.saveTutorialDesc || 'Usa il menu «Dati» nell\'header per backup completo (personaggi + combattimento + impostazioni) o solo personaggi.'}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Select Characters Tutorial - solo in modalità "Sono un player" */}
            {!charactersOnly && showSelectTip && characters.length > 0 && (
              <div className="relative p-4 accent-bg-10 border accent-border-30 rounded-lg">
                <button 
                  onClick={dismissSelectTip}
                  className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="flex items-start gap-3 pr-6">
                  <div className="p-2 accent-bg-20 rounded-lg">
                    <Swords className="w-5 h-5 accent-text" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm mb-1">{t.battleTutorialTitle || 'Entra in battaglia'}</h4>
                    <p className="text-xs text-muted-foreground">
                      {t.battleTutorialDesc || 'Seleziona i personaggi con le caselle a sinistra, poi clicca per entrare in battaglia.'}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Don't show tutorials button */}
            <div className="flex justify-end">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleHideAllTips}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                <EyeOff className="w-3 h-3 mr-1" />
                {t.dontShowAgain || 'Non mostrare più'}
              </Button>
            </div>
              </div>
            )}
          </aside>

          <section className="flex-1 min-w-0">
            {characters.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full accent-bg-10 flex items-center justify-center">
              <User className="w-8 h-8 accent-text" />
            </div>
            <h2 className="text-xl font-bold mb-2">{t.noCharacters || 'Nessun personaggio'}</h2>
            <p className="text-muted-foreground mb-4">{t.createFirstCharacter || 'Crea il tuo primo personaggio'}</p>
            <div className="flex flex-col gap-2 items-center">
              <Button className="accent-bg hover:opacity-90 text-white border-0" onClick={() => setIsModalOpen(true)}><Plus className="w-4 h-4 mr-2" />{t.addCharacter || 'Nuovo Personaggio'}</Button>
              {!charactersOnly && (
                <Button variant="outline" className="accent-border-50 accent-text hover:accent-bg-10" onClick={handleEnterCombatDirect}>
                  <Swords className="w-4 h-4 mr-2" />{t.enterBattle || 'Entra in Battaglia'}
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {characters.map(char => {
              const isSelected = !charactersOnly && selectedCharacters.includes(char.id);
              const namesInBattle = !charactersOnly ? combat.combatants.map(c => c.name) : [];
              const isInBattle = namesInBattle.includes(char.name);
              return (
                <Card key={char.id} className={`transition-all ${isSelected ? 'ring-2 ring-accent border-accent accent-border-50' : ''} ${isInBattle ? 'opacity-70' : ''}`}>
                  <CardContent className="p-4 flex items-center gap-3">
                    {/* Checkbox per selezione battaglia - solo in "Sono un player"; disabilitato se già in battaglia */}
                    {!charactersOnly && (
                      <button 
                        onClick={() => !isInBattle && toggleCharacterSelection(char.id)}
                        disabled={isInBattle}
                        className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all shrink-0 ${
                          isInBattle ? 'border-muted-foreground/20 bg-muted/30 cursor-not-allowed' :
                          isSelected 
                            ? 'accent-bg border-accent text-white' 
                            : 'border-muted-foreground/30 hover:accent-border-50'
                        }`}
                      >
                        {isSelected && <Check className="w-4 h-4" />}
                      </button>
                    )}
                    
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold flex items-center gap-2 flex-wrap">
                        <span className="truncate">{char.name}</span>
                        {isInBattle && <span className="text-xs font-normal text-muted-foreground whitespace-nowrap">({t.alreadyInBattle || 'già in battaglia'})</span>}
                      </h3>
                      {char.isFamiglio && char.famiglioDi && (
                        <p className="text-[11px] sm:text-xs text-muted-foreground mt-0.5 truncate">
                          {t.famiglioOf || 'Famiglio di'} {characters.find(c => c.id === char.famiglioDi)?.name ?? '—'}
                        </p>
                      )}
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1"><Heart className="w-4 h-4 text-red-500" />{char.maxHp}</span>
                        {char.ac && <span className="flex items-center gap-1"><Shield className="w-4 h-4 text-blue-500" />{char.ac}</span>}
                        {char.baseInitiative && <span className="flex items-center gap-1"><Zap className="w-4 h-4 accent-text" />{char.baseInitiative}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon"><MoreVertical className="w-4 h-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => { setEditingCharacter(char); setIsModalOpen(true); }}>
                            <Edit2 className="w-4 h-4 mr-2" />{t.edit}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { duplicateCharacter(char.id); toast.success(t.characterDuplicated || 'Personaggio duplicato'); }}>
                            <Copy className="w-4 h-4 mr-2" />{t.duplicate || 'Duplica'}
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-destructive" onClick={() => setDeleteTarget(char.id)}>
                            <Trash2 className="w-4 h-4 mr-2" />{t.delete}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
          </section>
        </div>
      </main>

      {/* Barra fissa in basso: "Nuovo personaggio" - schermata solo personaggi */}
      {charactersOnly && (
        <div className="fixed bottom-0 left-0 right-0 z-50 glass border-t border-border/50 safe-area-pb lg:hidden">
          <div className="max-w-2xl mx-auto w-full min-w-0 px-4 py-2 sm:py-3">
            <Button
              className="w-full h-11 sm:h-12 accent-bg hover:opacity-90 text-white border-0 text-base font-medium"
              onClick={() => { setEditingCharacter(null); setIsModalOpen(true); }}
            >
              <Plus className="w-5 h-5 mr-2 shrink-0" />
              {t.addCharacter || 'Nuovo Personaggio'}
            </Button>
          </div>
        </div>
      )}

      {/* Barra fissa in basso: selezione + azioni - solo in "Sono un player" */}
      {!charactersOnly && characters.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-50 glass border-t border-border/50 safe-area-pb lg:hidden">
          <div className="max-w-2xl mx-auto w-full min-w-0 px-4 py-2 sm:py-3 space-y-2">
            {(() => {
              const namesInBattle = new Set(combat.combatants.map(c => c.name));
              const selectableCharacterIds = characters.filter(c => !namesInBattle.has(c.name)).map(c => c.id);
              const areAllCharactersSelected = selectableCharacterIds.length > 0 && selectableCharacterIds.every(id => selectedCharacters.includes(id));
              return (
                <>
            <div className="flex items-center justify-between gap-2 p-2 rounded-lg accent-bg-10 border accent-border-30">
              <span className="text-sm font-medium accent-text">
                {selectedCharacters.length} {t.charactersSelected || 'personaggi selezionati'}
              </span>
              <div className="flex items-center gap-1.5">
                <Button
                  size="sm"
                  variant="ghost"
                  className="accent-text hover:accent-bg-20 h-8 text-xs"
                  onClick={() => setSelectedCharacters(areAllCharactersSelected ? [] : selectableCharacterIds)}
                >
                  {areAllCharactersSelected ? <><X className="w-3.5 h-3.5 mr-1" />{t.deselect || 'Deseleziona'}</> : <><Check className="w-3.5 h-3.5 mr-1" />{t.selectAll || 'Seleziona tutto'}</>}
                </Button>
              </div>
            </div>
            <div className="flex gap-3">
              {selectedCharacters.length > 0 ? (
                <Button className="flex-1 h-12 accent-bg hover:opacity-90 text-white border-0" onClick={handleStartCombatWithSelected}>
                  <Swords className="w-5 h-5 mr-2" />{t.fightWith || 'Combatti con'} {selectedCharacters.length} {t.selected || 'selezionati'}
                </Button>
              ) : (
                <>
                  <Button variant="outline" className="flex-1 h-12 accent-border-50 accent-text hover:accent-bg-10" onClick={() => setIsModalOpen(true)}>
                    <Plus className="w-5 h-5 mr-2" />{t.addCharacter || 'Nuovo'}
                  </Button>
                  <Button variant="outline" className="flex-1 h-12 accent-border-50 accent-text hover:accent-bg-10" onClick={handleEnterCombatDirect}>
                    <Swords className="w-5 h-5 mr-2" />{t.enterBattle || 'Entra in Battaglia'}
                  </Button>
                </>
              )}
            </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      <CharacterModal 
        isOpen={isModalOpen} 
        onClose={() => { setIsModalOpen(false); setEditingCharacter(null); }}
        onSave={handleSaveCharacter}
        editingCharacter={editingCharacter}
        characters={characters}
        t={t}
      />

      <TutorialDialog 
        isOpen={showTutorial} 
        onClose={() => setShowTutorial(false)}
        mode="personal"
        t={t}
      />

      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t.confirmDelete}</DialogTitle></DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>{t.no}</Button>
            <Button variant="destructive" onClick={() => { deleteCharacter(deleteTarget); setDeleteTarget(null); toast.success(t.characterDeleted || 'Personaggio eliminato'); }}>{t.yes}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

// ============ MASTER OFFLINE SCREEN ============

const MasterOfflineScreen = ({ onBack, t, theme, toggleTheme, accent, setAccent, lang, setLang }) => {
  const combat = useCombat('master');
  const fileInputRef = useRef(null);
  const [showTutorial, setShowTutorial] = useState(false);

  useEffect(() => {
    const tutorialCompleted = localStorage.getItem('tutorial-master-completed');
    if (!tutorialCompleted) {
      setShowTutorial(true);
    }
  }, []);

  const handleExport = () => {
    const data = { combatants: combat.combatants, round: combat.round };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `combat-master-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(t.dataExported || 'Dati esportati');
  };

  const handleImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const json = JSON.parse(ev.target.result);
        combat.importData(json);
        toast.success(t.dataImported || 'Dati importati');
      } catch (err) {
        toast.error(t.importError || 'Errore durante importazione');
      }
    };
    reader.readAsText(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="coco-v2-screen min-h-[100svh] sm:min-h-[100dvh]">
      <Toaster theme={theme} />
      <input type="file" ref={fileInputRef} className="hidden" accept=".json" onChange={handleImport} />
      
      <CombatScreen 
        combat={combat}
        onBack={onBack}
        t={t}
        theme={theme}
        toggleTheme={toggleTheme}
        accent={accent}
        setAccent={setAccent}
        lang={lang}
        setLang={setLang}
        onExport={handleExport}
        onImport={() => fileInputRef.current?.click()}
        fileInputRef={fileInputRef}
        handleImport={handleImport}
        isMaster={true}
        isPersonal={false}
        onGoHome={onBack}
      />

      <TutorialDialog 
        isOpen={showTutorial} 
        onClose={() => setShowTutorial(false)}
        mode="master"
        t={t}
      />
    </div>
  );
};

// ============ D&D STATUS LIST ============

// Complete list of D&D 5e conditions (base keys in Italian, display via translations)
const DND_STATUSES = [
  { name: 'Accecato', description: 'Non può vedere', color: 'bg-gray-500' },
  { name: 'Affascinato', description: 'Non può attaccare chi lo ha affascinato', color: 'bg-pink-500' },
  { name: 'Afferrato', description: 'Velocità 0', color: 'bg-orange-500' },
  { name: 'Assordato', description: 'Non può sentire', color: 'bg-gray-400' },
  { name: 'Avvelenato', description: 'Svantaggio ad attacchi e prove', color: 'bg-green-600' },
  { name: 'Concentrazione', description: 'Mantiene un incantesimo', color: 'bg-blue-400' },
  { name: 'Esausto', description: 'Livelli 1-6 di esaurimento', color: 'bg-amber-600', hasLevels: true },
  { name: 'Impaurito', description: 'Svantaggio mentre vede la fonte', color: 'bg-purple-600' },
  { name: 'Incapacitato', description: 'Non può fare azioni o reazioni', color: 'bg-red-400' },
  { name: 'Invisibile', description: 'Impossibile da vedere', color: 'bg-cyan-400' },
  { name: 'Paralizzato', description: 'Incapacitato, non può muoversi', color: 'bg-yellow-600' },
  { name: 'Pietrificato', description: 'Trasformato in pietra', color: 'bg-stone-500' },
  { name: 'Privo di sensi', description: 'Incapacitato, non consapevole', color: 'bg-gray-600' },
  { name: 'Prono', description: 'A terra, svantaggio attacchi', color: 'bg-brown-500' },
  { name: 'Rallentato', description: 'Velocità dimezzata', color: 'bg-blue-300' },
  { name: 'Sanguinante', description: 'Perde PF ogni turno', color: 'bg-red-600' },
  { name: 'Sorpreso', description: 'Non può muoversi o agire', color: 'bg-yellow-400' },
  { name: 'Spaventato', description: 'Svantaggio mentre vede la fonte di paura', color: 'bg-purple-500' },
  { name: 'Stordito', description: 'Incapacitato, fallisce TS For/Des', color: 'bg-yellow-500' },
  { name: 'Trattenuto', description: 'Velocità 0, svantaggio attacchi', color: 'bg-orange-600' },
  // Buff/Positive effects
  { name: 'Benedizione', description: '+1d4 attacchi e TS', color: 'bg-emerald-400' },
  { name: 'Eroismo', description: 'Immune a paura, PF temp', color: 'bg-amber-400' },
  { name: 'Fretta', description: 'Velocità raddoppiata, +2 CA', color: 'bg-cyan-500' },
  { name: 'Guidare', description: '+1d4 a una prova', color: 'bg-emerald-300' },
  { name: 'Protezione', description: 'Bonus alla CA o TS', color: 'bg-blue-500' },
  { name: 'Rigenerazione', description: 'Recupera PF ogni turno', color: 'bg-green-400' },
  { name: 'Scudo', description: '+5 CA fino al prossimo turno', color: 'bg-blue-600' },
  { name: 'Volare', description: 'Può volare', color: 'bg-sky-400' },
].sort((a, b) => a.name.localeCompare(b.name, 'it'));

// Exhaustion levels submenu
const EXHAUSTION_LEVELS = [
  { level: 1, effect: 'Svantaggio prove caratteristica' },
  { level: 2, effect: 'Velocità dimezzata' },
  { level: 3, effect: 'Svantaggio attacchi e TS' },
  { level: 4, effect: 'PF massimi dimezzati' },
  { level: 5, effect: 'Velocità 0' },
  { level: 6, effect: 'Morte' },
];

// "Altro..." always at the end
const CUSTOM_STATUS = { name: 'Altro...', description: 'Status personalizzato', color: 'bg-gray-300' };
const ALL_STATUSES = [...DND_STATUSES, CUSTOM_STATUS];

// Status Dropdown Component
const StatusDropdown = ({ combatant, onAddStatus, onRemoveStatus, onLogEvent, t }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [duration, setDuration] = useState('');
  const [statusValue, setStatusValue] = useState('');
  const [customName, setCustomName] = useState('');
  const [showDurationInput, setShowDurationInput] = useState(false);
  const [showExhaustionLevels, setShowExhaustionLevels] = useState(false);

  const statusDict = t.statusDictionary || {};

  const getStatusLabel = (name) => {
    if (!name) return '';
    return statusDict[name]?.name || name;
  };

  const getStatusDescription = (name, fallback) => {
    if (!name) return fallback || '';
    return statusDict[name]?.description || fallback || '';
  };

  const handleSelectStatus = (status) => {
    if (status.hasLevels) {
      // Show exhaustion submenu
      setShowExhaustionLevels(true);
      setSelectedStatus(status);
    } else if (status.name === 'Altro...') {
      setSelectedStatus(status);
      setShowDurationInput(true);
    } else {
      setSelectedStatus(status);
      setShowDurationInput(true);
    }
  };

  const handleSelectExhaustionLevel = (level) => {
    const statusName = `Esausto ${level.level}`;
    onAddStatus(combatant.id, {
      name: statusName,
      duration: null,
    });
    if (onLogEvent) {
      onLogEvent('statusGained', { name: combatant.name, status: statusName });
    }
    // Reset
    setSelectedStatus(null);
    setShowExhaustionLevels(false);
    setIsOpen(false);
  };

  const handleAddStatus = () => {
    const statusName = selectedStatus.name === 'Altro...' ? customName.trim() : selectedStatus.name;
    if (!statusName) return;
    const needsValue = selectedStatus.name === 'Rigenerazione' || selectedStatus.name === 'Sanguinante';
    const valueNum = needsValue && statusValue !== '' ? Math.max(1, parseInt(statusValue, 10) || 0) : undefined;
    if (needsValue && (valueNum === undefined || valueNum < 1)) return;

    onAddStatus(combatant.id, {
      name: statusName,
      duration: duration ? parseInt(duration) : null,
      value: valueNum,
    });

    if (onLogEvent) {
      onLogEvent('statusGained', { name: combatant.name, status: statusName });
    }

    setSelectedStatus(null);
    setDuration('');
    setStatusValue('');
    setCustomName('');
    setShowDurationInput(false);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setSelectedStatus(null);
    setDuration('');
    setStatusValue('');
    setCustomName('');
    setShowDurationInput(false);
    setShowExhaustionLevels(false);
  };

  // Get display text for button
  const getButtonText = () => {
    if (combatant.statuses && combatant.statuses.length > 0) {
      if (combatant.statuses.length === 1) {
        const s = combatant.statuses[0];
        const label = getStatusLabel(s.name);
        return `${label}${s.duration !== null ? ` (${s.duration})` : ''}`;
      }
      return `${combatant.statuses.length} ${t.status || 'Status'}`;
    }
    return t.status || 'Status';
  };

  const hasStatuses = combatant.statuses && combatant.statuses.length > 0;

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant={hasStatuses ? "default" : "outline"} 
          size="sm" 
          className={`h-8 px-2 text-xs max-w-[120px] truncate ${hasStatuses ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
        >
          {getButtonText()}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 max-h-80 overflow-y-auto">
        {/* Current statuses section */}
        {hasStatuses && (
          <>
            <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
              {t.status || 'Status'} {/* simple label, already translated elsewhere */}
            </div>
            {combatant.statuses.map(status => (
              <DropdownMenuItem key={status.id} className="justify-between" onSelect={(e) => e.preventDefault()}>
                <span className="flex items-center gap-2">
                  <span>{getStatusLabel(status.name)}</span>
                  {status.duration !== null && (
                    <span className="text-xs opacity-70">
                      ({status.duration})
                    </span>
                  )}
                </span>
                <button 
                  onClick={(e) => { e.stopPropagation(); onRemoveStatus(combatant.id, status.id); }} 
                  className="ml-2 p-1 hover:bg-destructive/20 rounded"
                >
                  <X className="w-3 h-3 text-destructive" />
                </button>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
          </>
        )}
        
        {/* Exhaustion levels submenu */}
        {showExhaustionLevels ? (
          <div className="p-2 space-y-1">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">
                {t.exhaustionLevelTitle || 'Livello Esaurimento'}
              </span>
              <Button size="sm" variant="ghost" className="h-6 px-2" onClick={handleCancel}>
                <X className="w-3 h-3" />
              </Button>
            </div>
            {EXHAUSTION_LEVELS.map(level => (
              <Button
                key={level.level}
                variant="outline"
                size="sm"
                className="w-full justify-start h-auto py-2 text-left"
                onClick={() => handleSelectExhaustionLevel(level)}
              >
                <div className={`w-2 h-2 rounded-full mr-2 ${level.level <= 2 ? 'bg-amber-500' : level.level <= 4 ? 'bg-orange-500' : 'bg-red-600'}`} />
                <div className="flex-1">
                  <div className="font-medium">
                    {(t.exhaustionLevelPrefix || 'Livello ')}{level.level}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {(t.exhaustionEffects && t.exhaustionEffects[level.level]) || level.effect}
                  </div>
                </div>
              </Button>
            ))}
          </div>
        ) : showDurationInput ? (
          <div className="p-2 space-y-2">
            <div>
              <div className="text-sm font-medium">
                {selectedStatus?.name === 'Altro...'
                  ? (t.customStatusTitle || 'Status Personalizzato')
                  : getStatusLabel(selectedStatus?.name)}
              </div>
              {selectedStatus?.name !== 'Altro...' && (
                <div className="text-xs text-muted-foreground mt-0.5">
                  {getStatusDescription(selectedStatus?.name, selectedStatus?.description)}
                </div>
              )}
            </div>
            {selectedStatus?.name === 'Altro...' && (
              <Input
                placeholder={t.customStatusPlaceholder || 'Nome status...'}
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                className="h-8 text-sm placeholder:text-muted-foreground"
              />
            )}
            {(selectedStatus?.name === 'Rigenerazione' || selectedStatus?.name === 'Sanguinante') && (
              <div>
                <label className="text-xs text-muted-foreground block mb-1">
                  {selectedStatus?.name === 'Rigenerazione' ? (t.statusRegenPerTurn ?? 'PF per turno') : (t.statusBleedPerTurn ?? 'Danno per turno')}
                </label>
                <Input
                  type="number"
                  placeholder={selectedStatus?.name === 'Rigenerazione' ? '5' : '1'}
                  value={statusValue}
                  onChange={(e) => setStatusValue(e.target.value)}
                  className="h-8 text-sm"
                  min="1"
                />
              </div>
            )}
            <div className="flex gap-2 items-center">
              <Input
                type="number"
                placeholder={t.statusDuration || 'Durata (round)'}
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="h-8 text-sm flex-1"
                min="1"
              />
              <Button size="sm" className="h-8" onClick={handleAddStatus} disabled={selectedStatus?.name === 'Altro...' ? !customName.trim() : (selectedStatus?.name === 'Rigenerazione' || selectedStatus?.name === 'Sanguinante') && (!statusValue || parseInt(statusValue, 10) < 1)}>
                <Check className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="ghost" className="h-8" onClick={handleCancel}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
              {t.addStatus || 'Aggiungi Status'}
            </div>
            {ALL_STATUSES.map(status => (
              <DropdownMenuItem 
                key={status.name} 
                onSelect={(e) => { e.preventDefault(); handleSelectStatus(status); }}
                className="cursor-pointer py-2"
              >
                <div className={`w-2 h-2 rounded-full shrink-0 ${status.color} mr-2 mt-0.5 self-start`} />
                <div className="flex-1 min-w-0 text-left">
                  <div className="font-medium">{getStatusLabel(status.name)}</div>
                  {status.name !== 'Altro...' && (
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {getStatusDescription(status.name, status.description)}
                    </div>
                  )}
                </div>
                {status.hasLevels && <ChevronRight className="w-3 h-3 shrink-0 ml-auto opacity-50 self-center" />}
              </DropdownMenuItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// ============ COMBAT SCREEN (SHARED) ============

const CombatantCard = ({ combatant, isActive, onModifyHp, onSetTempHp, onAddStatus, onRemoveStatus, onToggleKO, onEdit, onDelete, onDuplicate, onUpdateDeathSaves, onResetDeathSaves, onUpdateCombatant, onRollInitiative, onShortRest, onLongRest, onLogEvent, t, canEdit = true, isMaster = false, allowDuplicate = false, collapsed = false, onToggleCollapse, onOpenCoinPouch, xpTotal = null, onOpenXp }) => {
  const [customAmount, setCustomAmount] = useState('');
  const [tempHpAmount, setTempHpAmount] = useState('');
  const [showTempHp, setShowTempHp] = useState(false);
  const [showSpellSlots, setShowSpellSlots] = useState(false);
  const [showShortRestDialog, setShowShortRestDialog] = useState(false);
  const [shortRestNumDice, setShortRestNumDice] = useState(1);
  const [shortRestDieType, setShortRestDieType] = useState(8);
  const [shortRestRolls, setShortRestRolls] = useState(null);
  const [shortRestSum, setShortRestSum] = useState(null);
  const [initiativeEdit, setInitiativeEdit] = useState(combatant.initiative);
  const [flashClass, setFlashClass] = useState('');
  const cardRef = useRef(null);

  useEffect(() => {
    setInitiativeEdit(combatant.initiative);
  }, [combatant.id, combatant.initiative]);

  // HP is visible if: it's the master, or hpVisible is true
  const showHp = isMaster || combatant.hpVisible !== false;
  const openDungeonWallet = useCallback(() => {
    const popupFeatures = 'popup=yes,width=480,height=820,left=120,top=80,menubar=no,toolbar=no,location=yes,status=no,resizable=yes,scrollbars=yes';
    const popup = window.open(DUNGEON_WALLET_URL, 'dungeon-wallet', popupFeatures);
    if (!popup) {
      window.open(DUNGEON_WALLET_URL, '_blank', 'noopener,noreferrer');
      return;
    }
    popup.focus();
  }, []);

  useEffect(() => {
    if (isActive && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isActive]);

  const handleQuickHp = (amount) => {
    if (!canEdit) return;
    onModifyHp(combatant.id, amount);
    setFlashClass(amount < 0 ? 'damage-flash' : 'heal-flash');
    setTimeout(() => setFlashClass(''), 300);
    // Log event
    if (onLogEvent) {
      if (amount < 0) {
        onLogEvent('damage', { name: combatant.name, damage: Math.abs(amount) });
      } else {
        onLogEvent('heal', { name: combatant.name, amount: amount });
      }
    }
  };

  const handleCustomApply = (isDamage) => {
    if (!canEdit) return;
    const val = parseInt(customAmount);
    if (!isNaN(val) && val > 0) {
      onModifyHp(combatant.id, isDamage ? -val : val);
      setFlashClass(isDamage ? 'damage-flash' : 'heal-flash');
      setTimeout(() => setFlashClass(''), 300);
      // Log event
      if (onLogEvent) {
        if (isDamage) {
          onLogEvent('damage', { name: combatant.name, damage: val });
        } else {
          onLogEvent('heal', { name: combatant.name, amount: val });
        }
      }
      setCustomAmount('');
    }
  };

  const rollDice = (num, sides) => {
    const rolls = Array.from({ length: num }, () => Math.floor(Math.random() * sides) + 1);
    return { rolls, sum: rolls.reduce((a, b) => a + b, 0) };
  };

  const handleShortRestRoll = () => {
    const num = Math.max(0, Math.min(20, parseInt(shortRestNumDice, 10) || 0));
    if (num === 0) {
      setShortRestRolls([]);
      setShortRestSum(0);
      return;
    }
    const sides = [6, 8, 10, 12].includes(shortRestDieType) ? shortRestDieType : 8;
    const { rolls, sum } = rollDice(num, sides);
    setShortRestRolls(rolls);
    setShortRestSum(sum);
  };

  const handleShortRestApply = () => {
    if (!canEdit) return;
    const numDiceUsed = Math.max(0, Math.min(20, parseInt(shortRestNumDice, 10) || 0));
    if (numDiceUsed > 0) {
      if (shortRestSum == null || !onModifyHp) return;
      const actualHeal = Math.min(shortRestSum, combatant.maxHp - combatant.hp);
      if (actualHeal > 0) {
        onModifyHp(combatant.id, actualHeal);
        setFlashClass('heal-flash');
        setTimeout(() => setFlashClass(''), 300);
        const diceNotation = `${numDiceUsed}d${shortRestDieType}`;
        if (onLogEvent) onLogEvent('shortRest', { name: combatant.name, amount: actualHeal, diceNotation, rolls: shortRestRolls });
        const hd = combatant.hitDice;
        if (onUpdateCombatant && hd && typeof hd.total === 'number' && hd.total > 0 && typeof (hd.remaining ?? hd.total) === 'number') {
          const remaining = hd.remaining ?? hd.total;
          const newRemaining = Math.max(0, remaining - numDiceUsed);
          onUpdateCombatant(combatant.id, { hitDice: { ...hd, remaining: newRemaining } });
        }
      }
    }
    if (onShortRest) onShortRest(combatant.id);
    setShowShortRestDialog(false);
    setShortRestRolls(null);
    setShortRestSum(null);
  };

  const handleLongRestClick = () => {
    if (!canEdit || !onLongRest) return;
    onLongRest(combatant.id);
    if (onLogEvent) onLogEvent('longRest', { name: combatant.name });
    toast.success(t.longRestDone || 'Long rest fatto');
  };

  const handleAddTempHp = () => {
    if (!canEdit || !onSetTempHp) return;
    const val = parseInt(tempHpAmount);
    if (!isNaN(val) && val > 0) {
      // Temp HP don't stack, take max
      const newTempHp = Math.max(combatant.tempHp || 0, val);
      onSetTempHp(combatant.id, newTempHp);
      // Log event
      if (onLogEvent) {
        onLogEvent('tempHp', { name: combatant.name, amount: val });
      }
      setTempHpAmount('');
      setShowTempHp(false);
    }
  };

  const handleStatusAdd = (status) => {
    onAddStatus(combatant.id, status);
    if (onLogEvent) {
      onLogEvent('statusGained', { name: combatant.name, status: status.name });
    }
  };

  const handleStatusRemove = (statusName) => {
    onRemoveStatus(combatant.id, statusName);
    if (onLogEvent) {
      onLogEvent('statusLost', { name: combatant.name, status: statusName });
    }
  };

  const handleDeathSaveUpdate = (type) => {
    // Check current state before update
    const currentSuccesses = combatant.deathSaves?.successes || 0;
    const currentFailures = combatant.deathSaves?.failures || 0;
    
    onUpdateDeathSaves(combatant.id, type);
    
    if (onLogEvent) {
      const isSuccess = type === 'success';
      onLogEvent('deathSave', { 
        name: combatant.name, 
        saveType: isSuccess ? 'success' : 'failure'
      });
      
      // Check for stabilization (3 successes)
      if (isSuccess && currentSuccesses === 2) {
        onLogEvent('stabilized', { name: combatant.name });
      }
      
      // Check for death (3 failures)
      if (!isSuccess && currentFailures === 2) {
        onLogEvent('death', { name: combatant.name });
      }
    }
  };

  const handleToggleKO = () => {
    const willBeKO = !combatant.isKO;
    onToggleKO(combatant.id);
    if (onLogEvent) {
      if (willBeKO) {
        onLogEvent('ko', { name: combatant.name });
      } else {
        onLogEvent('revived', { name: combatant.name });
      }
    }
  };

  const cycleRollMode = () => {
    if (!canEdit || !onUpdateCombatant) return;
    const current = combatant.rollMode || 'normal';
    const next = current === 'normal' ? 'adv' : current === 'adv' ? 'dis' : 'normal';
    onUpdateCombatant(combatant.id, { rollMode: next });
  };

  const toggleConcentration = () => {
    if (!canEdit || !onUpdateCombatant) return;
    onUpdateCombatant(combatant.id, { isConcentrating: !combatant.isConcentrating });
  };

  const toggleTurnFlag = (key) => {
    if (!canEdit || !onUpdateCombatant) return;
    onUpdateCombatant(combatant.id, { [key]: !combatant[key] });
  };

  const toggleDash = () => {
    if (!canEdit || !onUpdateCombatant) return;
    onUpdateCombatant(combatant.id, { dashed: !combatant.dashed });
  };

  const setSpellSlotUsed = (level, used) => {
    if (!canEdit || !onUpdateCombatant) return;
    const current = combatant.spellSlots || {};
    const entry = current[level];
    if (!entry || typeof entry.max !== 'number') return;
    const nextUsed = Math.max(0, Math.min(entry.max, used));
    onUpdateCombatant(combatant.id, { 
      spellSlots: { 
        ...current, 
        [level]: { ...entry, used: nextUsed } 
      } 
    });
  };

  // Determine border color based on enemy/ally
  const getBorderColor = () => {
    if (combatant.isEnemy) return 'border-l-4 border-l-red-500';
    if (combatant.isCompanion || combatant.combatantType === 'companion') return 'border-l-4 border-l-green-500';
    if (combatant.isNPC) return 'border-l-4 border-l-blue-500';
    return '';
  };

  // Check if we should show death saves UI (KO ally, not enemy)
  const showDeathSaves = combatant.isKO && !combatant.isEnemy && combatant.deathSaves && canEdit && onUpdateDeathSaves;

  // Collapsed view: only name, HP, AC, speed, initiative
  if (collapsed && onToggleCollapse) {
    const ac = getEffectiveAC(combatant);
    const showAc = ac != null && (isMaster || combatant.acVisible !== false);
    const speed = combatant.dashed ? getEffectiveSpeed(combatant) * 2 : getEffectiveSpeed(combatant);
    return (
      <Card ref={cardRef} className={`relative overflow-hidden transition-transform duration-200 ${isActive ? 'active-glow scale-[1.02] accent-border-50' : ''} ${combatant.isKO && !showDeathSaves ? 'opacity-60' : ''} ${flashClass} ${getBorderColor()}`}>
        {isActive && <div className="absolute top-0 left-0 right-0 h-1 accent-bg" />}
        <CardContent className="p-2 sm:p-3">
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap min-w-0">
            {(combatant.isNPC || combatant.isEnemy || combatant.isCompanion || combatant.combatantType !== 'player') && (
              <div className={`w-2 h-2 rounded-full shrink-0 ${
                combatant.isCompanion || combatant.combatantType === 'companion' ? 'bg-green-500' :
                combatant.isEnemy || combatant.combatantType === 'enemy' ? 'bg-red-500' :
                combatant.isNPC || combatant.combatantType === 'npc' ? 'bg-blue-500' : 'bg-white border-2 border-gray-400'
              }`} />
            )}
            <span className={`font-semibold text-sm truncate min-w-0 flex-1 ${combatant.isKO ? 'line-through opacity-70' : ''}`} title={combatant.name}>{combatant.name}</span>
            <span className="font-mono text-sm shrink-0 flex items-center gap-0.5">
              {showHp ? (
                <>
                  <span className={combatant.hp <= combatant.maxHp * 0.25 ? 'text-red-500' : combatant.hp <= combatant.maxHp * 0.5 ? 'text-yellow-500' : 'text-green-500'}>{combatant.hp}</span>
                  <span className="text-muted-foreground">/{combatant.maxHp}</span>
                </>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
              {(combatant.tempHp || 0) > 0 && (
                <span className="text-cyan-500 text-xs font-medium" title={t.tempHpShort || 'Temp'}>+{combatant.tempHp} {t.tempHpShort || 'Temp'}</span>
              )}
            </span>
            {showAc && <span className="flex items-center gap-0.5 shrink-0 text-xs text-muted-foreground" title={t.ac || 'CA'}><Shield className="w-3.5 h-3.5 text-blue-500" /><span className="font-mono">{ac}</span></span>}
            <span className="flex items-center gap-0.5 shrink-0 text-xs text-muted-foreground">
              <Footprints className="w-3.5 h-3.5 text-emerald-500" />
              <span className="font-mono">{speed}</span>
              <span className="uppercase">{t.speedUnit || 'm/s'}</span>
            </span>
            <span className="flex items-center gap-0.5 shrink-0 text-xs font-mono accent-text">{combatant.initiative}</span>
            {combatant.isKO && <Skull className="w-4 h-4 text-destructive shrink-0" />}
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={onToggleCollapse} title={t.expandAll}>
              <ChevronDown className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card ref={cardRef} className={`relative overflow-hidden transition-transform duration-200 ${isActive ? 'active-glow scale-[1.02] accent-border-50' : ''} ${combatant.isKO && !showDeathSaves ? 'opacity-60' : ''} ${flashClass} ${getBorderColor()}`}>
      {isActive && <div className="absolute top-0 left-0 right-0 h-1 accent-bg" />}
      <CardContent className="p-3 sm:p-4">
        {/* Mobile: riga nome + iniziativa + CA + velocità (con icone e tasti) */}
        <div className="sm:hidden space-y-2 mb-3 pb-3 border-b border-border/50">
          <div className="flex flex-wrap items-center gap-2">
            {(combatant.isNPC || combatant.isEnemy || combatant.isCompanion || combatant.combatantType !== 'player') && (
              <div className={`w-2 h-2 rounded-full shrink-0 ${
                combatant.isCompanion || combatant.combatantType === 'companion' ? 'bg-green-500' :
                combatant.isEnemy || combatant.combatantType === 'enemy' ? 'bg-red-500' :
                combatant.isNPC || combatant.combatantType === 'npc' ? 'bg-blue-500' : 'bg-white border-2 border-gray-400'
              }`} />
            )}
            <div className="min-w-0">
              {combatant.famiglioDiName && (
                <p className="text-[10px] sm:text-xs text-muted-foreground/90 truncate leading-tight" title={combatant.famiglioDiName}>
                  {t.famiglioOf || 'Famiglio di'} {combatant.famiglioDiName}
                </p>
              )}
              <p className={`text-sm font-semibold text-foreground break-words ${combatant.isKO ? 'line-through opacity-70' : ''}`}>
                {combatant.name}
              </p>
            </div>
            <span className="flex items-center gap-0.5 text-xs text-muted-foreground shrink-0">
              <Zap className="w-3.5 h-3.5 accent-text" />
              <span className="font-mono">{(combatant.initiativeBonus ?? 0) >= 0 ? `+${combatant.initiativeBonus ?? 0}` : (combatant.initiativeBonus ?? 0)}</span>
            </span>
            <span className="flex items-center gap-0.5 shrink-0">
              {canEdit && onUpdateCombatant ? (
                <>
                  <Input
                    type="number"
                    className="h-6 w-9 font-mono text-center text-xs px-0.5"
                    value={initiativeEdit === '' ? '' : initiativeEdit}
                    onChange={(e) => setInitiativeEdit(e.target.value === '' ? '' : (parseInt(e.target.value, 10) || 0))}
                    onBlur={() => {
                      const n = initiativeEdit === '' ? 0 : parseInt(initiativeEdit, 10);
                      if (!isNaN(n)) onUpdateCombatant(combatant.id, { initiative: n });
                      setInitiativeEdit(combatant.initiative);
                    }}
                    title={t.initiativeValue || 'Iniziativa'}
                  />
                  {onRollInitiative && (
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onRollInitiative(combatant.id)} title={t.quickInitiative || 'Tira d20'}>
                      <Dices className="w-3 h-3" />
                    </Button>
                  )}
                </>
              ) : (
                <span className="font-mono text-xs">{combatant.initiative}</span>
              )}
            </span>
            {(getEffectiveAC(combatant) != null && (isMaster || combatant.acVisible !== false)) && (
              <span className="flex items-center gap-0.5 shrink-0 text-xs text-muted-foreground" title={t.ac || 'CA'}>
                <Shield className="w-3.5 h-3.5 text-blue-500" /><span className="font-mono">{getEffectiveAC(combatant)}</span>
              </span>
            )}
            <span className="flex items-center gap-0.5 shrink-0 text-xs text-muted-foreground">
              <Footprints className="w-3.5 h-3.5 text-emerald-500" />
              <span className="font-mono">{combatant.dashed ? getEffectiveSpeed(combatant) * 2 : getEffectiveSpeed(combatant)}</span>
              <span className="uppercase">{t.speedUnit || 'm/s'}</span>
              {canEdit && onUpdateCombatant && (
                <Button variant={combatant.dashed ? 'default' : 'outline'} size="icon" className="h-6 w-6 shrink-0" onClick={toggleDash} disabled={getEffectiveSpeed(combatant) === 0} title={t.dash || 'Scatto'}>
                  <RunningPersonIcon className="w-3 h-3" />
                </Button>
              )}
            </span>
            {combatant.dot && <span className="flex items-center gap-0.5 text-red-500 text-xs"><Flame className="w-3.5 h-3.5" /><span className="font-mono">{combatant.dot.damage}{combatant.dot.duration != null ? `(${combatant.dot.duration})` : ''}</span></span>}
            {combatant.isKO && <Skull className="w-4 h-4 text-destructive shrink-0" />}
          </div>
          {/* Mobile: Status e Concentrazione a sinistra, Edit/Delete/KO a destra */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5">
              {canEdit && onAddStatus && (
                <StatusDropdown combatant={combatant} onAddStatus={onAddStatus} onRemoveStatus={onRemoveStatus} onLogEvent={onLogEvent} t={t} />
              )}
              {canEdit && onUpdateCombatant && (
                <Button variant={combatant.isConcentrating ? 'default' : 'outline'} size="sm" className={`h-7 px-2 text-xs shrink-0 ${combatant.isConcentrating ? 'bg-primary text-primary-foreground hover:bg-primary/90 shadow' : ''}`} onClick={toggleConcentration} title={t.concentration || 'Concentrazione'}>
                  {t.statusDictionary?.['Concentrazione']?.name || (t.concentration || 'Conc.')}
                </Button>
              )}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {canEdit && allowDuplicate && onDuplicate && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onDuplicate(combatant)} title={t.duplicate || 'Duplica'}><Copy className="w-4 h-4" /></Button>}
              {canEdit && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(combatant)} title={t.edit || 'Modifica'}><Edit2 className="w-4 h-4" /></Button>}
              {canEdit && <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:bg-destructive/10" onClick={() => onDelete(combatant.id)} title={t.delete || 'Elimina'}><Trash2 className="w-4 h-4" /></Button>}
              {canEdit && <Button variant={combatant.isKO ? 'destructive' : 'outline'} size="icon" className="h-8 w-8" onClick={handleToggleKO} title={t.toggleKo || 'KO'}><Skull className={`w-4 h-4 ${combatant.isKO ? '' : 'opacity-50'}`} /></Button>}
              {onToggleCollapse && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onToggleCollapse} title={t.collapseAll}><ChevronUp className="w-4 h-4" /></Button>}
            </div>
          </div>
        </div>

        {/* Desktop: layout originale */}
        <div className="hidden sm:flex items-start justify-between gap-2 mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              {(combatant.isNPC || combatant.isEnemy || combatant.isCompanion || combatant.combatantType !== 'player') && (
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  combatant.isCompanion || combatant.combatantType === 'companion' ? 'bg-green-500' :
                  combatant.isEnemy || combatant.combatantType === 'enemy' ? 'bg-red-500' :
                  combatant.isNPC || combatant.combatantType === 'npc' ? 'bg-blue-500' : 
                  'bg-white border-2 border-gray-400'
                }`} />
              )}
              <div className="min-w-0">
                {combatant.famiglioDiName && (
                  <p className="text-[10px] sm:text-xs text-muted-foreground/90 truncate leading-tight" title={combatant.famiglioDiName}>
                    {t.famiglioOf || 'Famiglio di'} {combatant.famiglioDiName}
                  </p>
                )}
                <h3 className={`font-bold text-lg truncate ${combatant.isKO ? 'line-through' : ''}`} title={combatant.name}>{combatant.name}</h3>
              </div>
              {(getEffectiveAC(combatant) != null && (isMaster || combatant.acVisible !== false)) && (
                <span className="flex items-center gap-1 shrink-0 text-sm text-muted-foreground" title={t.ac || 'CA'}>
                  <Shield className="w-4 h-4 text-blue-500" /><span className="font-mono">{getEffectiveAC(combatant)}</span>
                </span>
              )}
              {combatant.isKO && <Skull className="w-5 h-5 text-destructive flex-shrink-0" />}
            </div>
            <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1 flex-wrap">
              <span className="flex items-center gap-1">
                <Zap className="w-4 h-4 accent-text shrink-0" />
                <span className="font-mono text-foreground">{(combatant.initiativeBonus ?? 0) >= 0 ? `+${combatant.initiativeBonus ?? 0}` : (combatant.initiativeBonus ?? 0)}</span>
              </span>
              <span className="flex items-center gap-1">
                {canEdit && onUpdateCombatant ? (
                  <>
                    <Input type="number" className="h-6 w-10 font-mono text-center text-xs px-0.5" value={initiativeEdit === '' ? '' : initiativeEdit} onChange={(e) => setInitiativeEdit(e.target.value === '' ? '' : (parseInt(e.target.value, 10) || 0))} onBlur={() => { const n = initiativeEdit === '' ? 0 : parseInt(initiativeEdit, 10); if (!isNaN(n)) onUpdateCombatant(combatant.id, { initiative: n }); setInitiativeEdit(combatant.initiative); }} title={t.initiativeValue || 'Iniziativa'} />
                    {onRollInitiative && <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onRollInitiative(combatant.id)} title={t.quickInitiative || 'Tira d20'}><Dices className="w-3.5 h-3.5" /></Button>}
                  </>
                ) : (
                  <span className="font-mono">{combatant.initiative}</span>
                )}
              </span>
              <span className="flex items-center gap-1">
                <Footprints className="w-4 h-4 text-emerald-500 shrink-0" />
                <span className="font-mono">{combatant.dashed ? getEffectiveSpeed(combatant) * 2 : getEffectiveSpeed(combatant)}</span>
                <span className="text-[10px] text-muted-foreground uppercase">{t.speedUnit || 'm/s'}</span>
                {canEdit && onUpdateCombatant && (
                  <Button variant={combatant.dashed ? 'default' : 'outline'} size="icon" className="h-7 w-7 shrink-0" onClick={toggleDash} disabled={getEffectiveSpeed(combatant) === 0} title={getEffectiveSpeed(combatant) === 0 ? (t.speed ?? 'Velocità') + ' 0' : (t.dash || 'Scatto')}>
                    <RunningPersonIcon className="w-3.5 h-3.5" />
                  </Button>
                )}
              </span>
              {combatant.dot && <span className="flex items-center gap-1 text-red-500"><Flame className="w-4 h-4" /><span className="font-mono text-xs">{combatant.dot.damage}{combatant.dot.duration !== null && `(${combatant.dot.duration})`}</span></span>}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0 flex-wrap justify-end">
            {canEdit && onAddStatus && <StatusDropdown combatant={combatant} onAddStatus={onAddStatus} onRemoveStatus={onRemoveStatus} onLogEvent={onLogEvent} t={t} />}
            {canEdit && onUpdateCombatant && (
              <Button variant={combatant.isConcentrating ? 'default' : 'outline'} size="sm" className={`h-8 px-2 text-xs ${combatant.isConcentrating ? 'bg-primary text-primary-foreground hover:bg-primary/90 shadow' : ''}`} onClick={toggleConcentration} title={t.statusDictionary?.['Concentrazione']?.name || (t.concentration || 'Concentrazione')}>
                {t.statusDictionary?.['Concentrazione']?.name || (t.concentration || 'Conc.')}
              </Button>
            )}
            {canEdit && allowDuplicate && onDuplicate && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onDuplicate(combatant)} title={t.duplicate || 'Duplica'}><Copy className="w-4 h-4" /></Button>}
            {canEdit && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(combatant)} title={t.edit || 'Modifica'}><Edit2 className="w-4 h-4" /></Button>}
            {canEdit && <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:bg-destructive/10" onClick={() => onDelete(combatant.id)} title={t.delete || 'Elimina'}><Trash2 className="w-4 h-4" /></Button>}
            {canEdit && <Button variant={combatant.isKO ? 'destructive' : 'outline'} size="icon" className="h-8 w-8" onClick={handleToggleKO} title={t.toggleKo || 'Toggle KO'}><Skull className={`w-4 h-4 ${combatant.isKO ? '' : 'opacity-50'}`} /></Button>}
            {onToggleCollapse && (
              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={onToggleCollapse} title={t.collapseAll}>
                <ChevronUp className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Death Saving Throws - Only for KO allies (not enemies) */}
        {showDeathSaves && (
          <div className="mb-3 p-4 bg-gradient-to-br from-[hsl(var(--accent-theme)/0.2)] to-red-500/20 border-2 accent-border-50 rounded-xl shadow-lg">
            <div className="flex items-center justify-between mb-3">
              <span className="text-base font-bold flex items-center gap-2 accent-text">
                <Skull className="w-5 h-5" />
                {t.deathSaves || 'Tiri Salvezza su Morte'}
              </span>
              {onResetDeathSaves && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-7 text-xs opacity-70 hover:opacity-100" 
                  onClick={() => onResetDeathSaves(combatant.id)}
                >
                  {t.reset || 'Reset'}
                </Button>
              )}
            </div>
            
            {combatant.deathSaves.failures >= 3 ? (
              <div className="text-center py-4 bg-red-500/20 rounded-lg border border-red-500/30">
                <Skull className="w-10 h-10 mx-auto mb-2 text-red-600" />
                <p className="text-lg font-bold text-red-600">
                  {t.dead || 'Morto'}
                </p>
              </div>
            ) : combatant.deathSaves.successes >= 3 ? (
              <div className="text-center py-4 bg-green-500/20 rounded-lg border border-green-500/30">
                <Check className="w-10 h-10 mx-auto mb-2 text-green-600" />
                <p className="text-lg font-bold text-green-600">
                  {t.stabilized || 'Stabilizzato!'}
                </p>
                <p className="text-sm text-green-600/80">
                  {t.backTo1HP || 'Torna a 1 PF'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {/* SUCCESS Button */}
                <div className="flex flex-col items-center">
                  {/* 3 checkmarks indicator */}
                  <div className="flex gap-2 mb-2">
                    {[1, 2, 3].map(i => (
                      <div 
                        key={`success-indicator-${i}`}
                        className={`w-6 h-6 rounded-full flex items-center justify-center border-2 transition-all ${
                          combatant.deathSaves.successes >= i 
                            ? 'bg-green-500 border-green-600 text-white shadow-md' 
                            : 'bg-background/50 border-muted-foreground/30'
                        }`}
                      >
                        <Check className={`w-4 h-4 ${combatant.deathSaves.successes >= i ? '' : 'opacity-30'}`} />
                      </div>
                    ))}
                  </div>
                  {/* Big success button */}
                  <Button
                    variant="outline"
                    className="w-full h-14 text-lg font-bold bg-green-500/10 hover:bg-green-500/30 border-green-500/50 text-green-700 dark:text-green-400 transition-all hover:scale-105"
                    onClick={() => handleDeathSaveUpdate('success')}
                    data-testid="death-save-success-btn"
                  >
                    <Check className="w-6 h-6 mr-2" />
                    {t.success || 'Successo'}
                  </Button>
                </div>
                
                {/* FAILURE Button */}
                <div className="flex flex-col items-center">
                  {/* 3 X marks indicator */}
                  <div className="flex gap-2 mb-2">
                    {[1, 2, 3].map(i => (
                      <div 
                        key={`failure-indicator-${i}`}
                        className={`w-6 h-6 rounded-full flex items-center justify-center border-2 transition-all ${
                          combatant.deathSaves.failures >= i 
                            ? 'bg-red-500 border-red-600 text-white shadow-md' 
                            : 'bg-background/50 border-muted-foreground/30'
                        }`}
                      >
                        <X className={`w-4 h-4 ${combatant.deathSaves.failures >= i ? '' : 'opacity-30'}`} />
                      </div>
                    ))}
                  </div>
                  {/* Big failure button */}
                  <Button
                    variant="outline"
                    className="w-full h-14 text-lg font-bold bg-red-500/10 hover:bg-red-500/30 border-red-500/50 text-red-700 dark:text-red-400 transition-all hover:scale-105"
                    onClick={() => handleDeathSaveUpdate('failure')}
                    data-testid="death-save-failure-btn"
                  >
                    <X className="w-6 h-6 mr-2" />
                    {t.failure || 'Fallimento'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* HP Section - hidden for players if hpVisible is false */}
        {showHp ? (
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1 flex-wrap gap-y-1">
              <span className="flex items-center gap-1 text-sm"><Heart className="w-4 h-4 text-red-500" />{t.hp}</span>
              <div className="flex items-center gap-2 flex-wrap">
                {combatant.tempHp > 0 && (
                  <span className="text-sm font-mono text-cyan-500">+{combatant.tempHp} {t.tempHpShort || 'Temp'}</span>
                )}
                <span className="font-mono font-bold text-xl">
                  <span className={combatant.hp <= combatant.maxHp * 0.25 ? 'text-red-500' : combatant.hp <= combatant.maxHp * 0.5 ? 'text-yellow-500' : 'text-green-500'}>{combatant.hp}</span>
                  <span className="text-muted-foreground text-base"> / {combatant.maxHp}</span>
                </span>
                {canEdit && (
                  <div className="flex flex-col items-end gap-0.5 ml-1">
                    {/* +Temp subito sopra all'indicatore − [input] + */}
                    {onSetTempHp && (
                      <div className="flex items-center gap-1">
                        {showTempHp ? (
                          <>
                            <Input type="number" placeholder="0" value={tempHpAmount} onChange={(e) => setTempHpAmount(e.target.value)} className="h-6 w-10 font-mono text-center text-xs px-0.5" min="1" />
                            <Button size="sm" className="h-6 px-1.5 text-xs bg-cyan-600 hover:bg-cyan-700" onClick={handleAddTempHp} disabled={!tempHpAmount}>+</Button>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setShowTempHp(false)}><X className="w-2.5 h-2.5" /></Button>
                          </>
                        ) : (
                          <Button variant="outline" size="sm" className="h-6 px-1.5 text-[10px] text-cyan-600 dark:text-cyan-400" onClick={() => setShowTempHp(true)} title={t.addTempHp || 'PF Temp'}>+Temp</Button>
                        )}
                      </div>
                    )}
                    <span className="flex items-center gap-0.5">
                      <Button variant="outline" size="icon" className="h-6 w-6 text-red-600 hover:text-red-700 hover:bg-red-500/15 dark:text-red-400" onClick={() => { const v = parseInt(customAmount); if (!isNaN(v) && v > 0) handleCustomApply(true); }} disabled={!customAmount} title={t.damage}>−</Button>
                      <Input type="number" placeholder="0" value={customAmount} onChange={(e) => setCustomAmount(e.target.value)} className="h-6 w-9 font-mono text-center text-xs px-0.5" min="1" />
                      <Button variant="outline" size="icon" className="h-6 w-6 text-green-600 hover:text-green-700 hover:bg-green-500/15 dark:text-green-400" onClick={() => { const v = parseInt(customAmount); if (!isNaN(v) && v > 0) handleCustomApply(false); }} disabled={!customAmount} title={t.heal}>+</Button>
                    </span>
                  </div>
                )}
              </div>
            </div>
            <HpBar current={combatant.hp} max={combatant.maxHp} tempHp={combatant.tempHp || 0} />
          </div>
        ) : (
          <div className="mb-3 p-3 bg-muted/30 rounded text-center">
            <EyeOff className="w-5 h-5 mx-auto mb-1 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">{t.hpHidden || 'PF nascosti'}</span>
          </div>
        )}

        {canEdit && (
          <>
            {/* Pulsanti PF: su mobile 2 righe (3+3), su desktop 1 riga (6); touch target min 44px */}
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-1 mb-3">
              {[-10, -5, -1].map(amt => (
                <Button key={amt} variant="outline" size="sm" className="h-9 text-red-600 dark:text-red-400 font-mono font-bold text-sm" onClick={() => handleQuickHp(amt)}>{amt}</Button>
              ))}
              {[1, 5, 10].map(amt => (
                <Button key={amt} variant="outline" size="sm" className="h-9 text-green-600 dark:text-green-400 font-mono font-bold text-sm" onClick={() => handleQuickHp(amt)}>+{amt}</Button>
              ))}
            </div>

            {/* Riga funzioni: su mobile wrap, pulsanti più grandi e distanziati */}
            <div className="mb-3 space-y-2">
              {(() => {
                const hasSpellSlots = canEdit && combatant.spellSlots && Object.keys(combatant.spellSlots).length > 0;
                return (
                  <>
                    {/* MOBILE: layout invariato */}
                    <div className="flex flex-wrap items-center gap-2 w-full justify-center sm:hidden">
                      <div className="flex items-center shrink-0 gap-1">
                        {onOpenCoinPouch && (
                          <Button variant="ghost" size="sm" className="h-7 px-2.5 text-xs font-medium min-w-0 text-amber-700 dark:text-amber-200 hover:bg-amber-200/20 dark:hover:bg-amber-100/10 border border-amber-300/40 dark:border-amber-200/20" onClick={openDungeonWallet} title="Wallet">
                            <img
                              src={DUNGEON_WALLET_ICON}
                              alt=""
                              className="w-3.5 h-3.5 mr-1 shrink-0 rounded-sm object-cover"
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                            <span className="truncate">Wallet</span>
                          </Button>
                        )}
                        {onOpenXp && (
                          <Button variant="outline" size="sm" className="h-7 px-2.5 text-xs accent-border-50 accent-text hover:accent-bg-10 min-w-0" onClick={() => onOpenXp(combatant)} title={t.xp || 'XP'}>
                            <Coins className="w-3.5 h-3.5 mr-1 text-amber-500 shrink-0" />
                            <span className="truncate">{t.xp || 'XP'}{typeof xpTotal === 'number' ? `: ${xpTotal}` : ''}</span>
                          </Button>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 shrink-0">
                        {canEdit && onUpdateCombatant && (
                          <>
                            <Button variant={combatant.actionUsed ? 'default' : 'outline'} size="sm" className="h-7 px-2.5 min-w-0 text-xs font-bold truncate max-w-[5rem]" onClick={() => onUpdateCombatant(combatant.id, { actionUsed: !combatant.actionUsed })} title={t.actionTooltip ?? t.action}>{t.actionShort ?? t.action}</Button>
                            <Button variant={combatant.bonusActionUsed ? 'default' : 'outline'} size="sm" className="h-7 px-2.5 min-w-0 text-xs font-bold truncate max-w-[5rem]" onClick={() => onUpdateCombatant(combatant.id, { bonusActionUsed: !combatant.bonusActionUsed })} title={t.bonusActionTooltip ?? t.bonusAction}>{t.bonusActionShort ?? t.bonusAction}</Button>
                            <Button variant={combatant.reactionUsed ? 'default' : 'outline'} size="sm" className="h-7 px-2.5 min-w-0 text-xs font-bold truncate max-w-[5rem]" onClick={() => onUpdateCombatant(combatant.id, { reactionUsed: !combatant.reactionUsed })} title={t.reactionTooltip ?? t.reaction}>{t.reactionShort ?? t.reaction}</Button>
                            <Button variant={combatant.rollMode === 'adv' || hasAdvantageFromStatuses(combatant) ? 'default' : 'outline'} size="sm" className={`h-7 px-2.5 min-w-0 text-xs font-bold truncate max-w-[5rem] ${(combatant.rollMode === 'adv' || hasAdvantageFromStatuses(combatant)) ? 'bg-green-600 hover:bg-green-700 text-white border-green-600' : 'text-green-600 dark:text-green-400'}`} onClick={() => onUpdateCombatant(combatant.id, { rollMode: combatant.rollMode === 'adv' ? 'normal' : 'adv' })} title={t.advantage}>{t.advantageShort ?? t.advantage}</Button>
                            <Button variant={combatant.rollMode === 'dis' || hasDisadvantageFromStatuses(combatant) ? 'default' : 'outline'} size="sm" className={`h-7 px-2.5 min-w-0 text-xs font-bold truncate max-w-[5rem] ${(combatant.rollMode === 'dis' || hasDisadvantageFromStatuses(combatant)) ? 'bg-red-600 hover:bg-red-700 text-white border-red-600' : 'text-red-600 dark:text-red-400'}`} onClick={() => onUpdateCombatant(combatant.id, { rollMode: combatant.rollMode === 'dis' ? 'normal' : 'dis' })} title={t.disadvantage}>{t.disadvantageShort ?? t.disadvantage}</Button>
                          </>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 shrink-0">
                        {canEdit && onShortRest && onLongRest && (
                          <>
                            <Button variant="outline" size="sm" className="h-7 px-2.5 text-xs accent-text" onClick={() => { setShortRestRolls(null); setShortRestSum(null); const hd = combatant.hitDice; if (hd && hd.total > 0) { setShortRestDieType([6, 8, 10, 12].includes(hd.sides) ? hd.sides : 8); setShortRestNumDice(Math.min(20, Math.max(0, hd.remaining ?? hd.total))); } else { setShortRestNumDice(0); } setShowShortRestDialog(true); }} title={t.shortRest}><Heart className="w-3 h-3 mr-0.5 shrink-0" />{t.shortRestShort ?? t.shortRest}</Button>
                            {hasSpellSlots && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 px-2.5 justify-start gap-1 text-muted-foreground text-xs min-w-0"
                                onClick={() => setShowSpellSlots(prev => !prev)}
                              >
                                <Sparkles className="w-3 h-3 text-purple-500 shrink-0" />
                                <span className="truncate">{t.spellSlots || 'Slot'}</span>
                                {showSpellSlots ? <ChevronUp className="w-2.5 h-2.5 ml-auto shrink-0" /> : <ChevronDown className="w-2.5 h-2.5 ml-auto shrink-0" />}
                              </Button>
                            )}
                            <Button variant="outline" size="sm" className="h-7 px-2.5 text-xs text-indigo-600 dark:text-indigo-400" onClick={handleLongRestClick} title={t.longRest}><Moon className="w-3 h-3 mr-0.5 shrink-0" />{t.longRestShort ?? t.longRest}</Button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* DESKTOP: riposi al posto del blocco Az, blocco Az in basso centrato */}
                    <div className="hidden sm:flex flex-col gap-2 w-full">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center shrink-0 gap-1">
                          {onOpenCoinPouch && (
                            <Button variant="ghost" size="sm" className="h-7 px-2 text-[10px] font-medium min-w-0 text-amber-700 dark:text-amber-200 hover:bg-amber-200/20 dark:hover:bg-amber-100/10 border border-amber-300/40 dark:border-amber-200/20" onClick={openDungeonWallet} title="Wallet">
                              <img
                                src={DUNGEON_WALLET_ICON}
                                alt=""
                                className="w-3.5 h-3.5 mr-1 shrink-0 rounded-sm object-cover"
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              />
                              <span className="truncate">Wallet</span>
                            </Button>
                          )}
                          {onOpenXp && (
                            <Button variant="outline" size="sm" className="h-7 px-2 text-[10px] accent-border-50 accent-text hover:accent-bg-10 min-w-0" onClick={() => onOpenXp(combatant)} title={t.xp || 'XP'}>
                              <Coins className="w-3.5 h-3.5 mr-1 text-amber-500 shrink-0" />
                              <span className="truncate">{t.xp || 'XP'}{typeof xpTotal === 'number' ? `: ${xpTotal}` : ''}</span>
                            </Button>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 shrink-0">
                          {canEdit && onShortRest && onLongRest && (
                            <>
                              <Button variant="outline" size="sm" className="h-7 px-1.5 text-[10px] accent-text" onClick={() => { setShortRestRolls(null); setShortRestSum(null); const hd = combatant.hitDice; if (hd && hd.total > 0) { setShortRestDieType([6, 8, 10, 12].includes(hd.sides) ? hd.sides : 8); setShortRestNumDice(Math.min(20, Math.max(0, hd.remaining ?? hd.total))); } else { setShortRestNumDice(0); } setShowShortRestDialog(true); }} title={t.shortRest}><Heart className="w-3 h-3 mr-0.5 shrink-0" />{t.shortRestShort ?? t.shortRest}</Button>
                              <Button variant="outline" size="sm" className="h-7 px-1.5 text-[10px] text-indigo-600 dark:text-indigo-400" onClick={handleLongRestClick} title={t.longRest}><Moon className="w-3 h-3 mr-0.5 shrink-0" />{t.longRestShort ?? t.longRest}</Button>
                            </>
                          )}
                        </div>
                      </div>
                      {hasSpellSlots && (
                        <div className="flex justify-center">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-1.5 justify-start gap-1 text-muted-foreground text-[10px] min-w-0"
                            onClick={() => setShowSpellSlots(prev => !prev)}
                          >
                            <Sparkles className="w-3 h-3 text-purple-500 shrink-0" />
                            <span className="truncate">{t.spellSlots || 'Slot'}</span>
                            {showSpellSlots ? <ChevronUp className="w-2.5 h-2.5 ml-auto shrink-0" /> : <ChevronDown className="w-2.5 h-2.5 ml-auto shrink-0" />}
                          </Button>
                        </div>
                      )}
                      <div className="flex justify-center">
                        <div className="flex flex-wrap items-center gap-2 shrink-0">
                          {canEdit && onUpdateCombatant && (
                            <>
                              <Button variant={combatant.actionUsed ? 'default' : 'outline'} size="sm" className="h-7 px-1.5 min-w-0 text-[10px] font-bold truncate max-w-[4.25rem]" onClick={() => onUpdateCombatant(combatant.id, { actionUsed: !combatant.actionUsed })} title={t.actionTooltip ?? t.action}>{t.actionShort ?? t.action}</Button>
                              <Button variant={combatant.bonusActionUsed ? 'default' : 'outline'} size="sm" className="h-7 px-1.5 min-w-0 text-[10px] font-bold truncate max-w-[4.25rem]" onClick={() => onUpdateCombatant(combatant.id, { bonusActionUsed: !combatant.bonusActionUsed })} title={t.bonusActionTooltip ?? t.bonusAction}>{t.bonusActionShort ?? t.bonusAction}</Button>
                              <Button variant={combatant.reactionUsed ? 'default' : 'outline'} size="sm" className="h-7 px-1.5 min-w-0 text-[10px] font-bold truncate max-w-[4.25rem]" onClick={() => onUpdateCombatant(combatant.id, { reactionUsed: !combatant.reactionUsed })} title={t.reactionTooltip ?? t.reaction}>{t.reactionShort ?? t.reaction}</Button>
                              <Button variant={combatant.rollMode === 'adv' || hasAdvantageFromStatuses(combatant) ? 'default' : 'outline'} size="sm" className={`h-7 px-1.5 min-w-0 text-[10px] font-bold truncate max-w-[4.25rem] ${(combatant.rollMode === 'adv' || hasAdvantageFromStatuses(combatant)) ? 'bg-green-600 hover:bg-green-700 text-white border-green-600' : 'text-green-600 dark:text-green-400'}`} onClick={() => onUpdateCombatant(combatant.id, { rollMode: combatant.rollMode === 'adv' ? 'normal' : 'adv' })} title={t.advantage}>{t.advantageShort ?? t.advantage}</Button>
                              <Button variant={combatant.rollMode === 'dis' || hasDisadvantageFromStatuses(combatant) ? 'default' : 'outline'} size="sm" className={`h-7 px-1.5 min-w-0 text-[10px] font-bold truncate max-w-[4.25rem] ${(combatant.rollMode === 'dis' || hasDisadvantageFromStatuses(combatant)) ? 'bg-red-600 hover:bg-red-700 text-white border-red-600' : 'text-red-600 dark:text-red-400'}`} onClick={() => onUpdateCombatant(combatant.id, { rollMode: combatant.rollMode === 'dis' ? 'normal' : 'dis' })} title={t.disadvantage}>{t.disadvantageShort ?? t.disadvantage}</Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                );
              })()}
              {showSpellSlots && canEdit && combatant.spellSlots && Object.keys(combatant.spellSlots).length > 0 && (
                <div className="p-1.5 bg-muted/30 rounded-md grid grid-cols-2 gap-1.5 w-full">
                  {Object.entries(combatant.spellSlots)
                    .map(([lvl, v]) => [Number(lvl), v])
                    .filter(([lvl, v]) => v && typeof v.max === 'number' && v.max > 0)
                    .sort((a, b) => a[0] - b[0])
                    .map(([lvl, v]) => {
                      const used = v.used || 0;
                      const remaining = Math.max(0, v.max - used);
                      return (
                        <div key={lvl} className="flex items-center gap-0.5">
                          <span className="text-[10px] text-muted-foreground w-5 shrink-0" title={(t.spellSlots || 'Slot') + ' Lv.' + lvl}>L{lvl}</span>
                          <Button variant="outline" size="sm" className="h-6 w-6 p-0 shrink-0 text-xs" onClick={() => setSpellSlotUsed(lvl, used + 1)} disabled={used >= v.max}>−</Button>
                          <span className="font-mono text-[11px] w-7 text-center shrink-0">{remaining}/{v.max}</span>
                          <Button variant="outline" size="sm" className="h-6 w-6 p-0 shrink-0 text-xs" onClick={() => setSpellSlotUsed(lvl, used - 1)} disabled={used <= 0}>+</Button>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </>
        )}

        {/* Dialog Riposo breve: dadi vita */}
        <Dialog open={showShortRestDialog} onOpenChange={setShowShortRestDialog}>
          <DialogContent className="sm:max-w-xs">
            <DialogHeader>
              <DialogTitle>{t.shortRestTitle || 'Riposo breve – Dadi vita'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              {combatant.hitDice && combatant.hitDice.total > 0 && (
                <p className="text-sm text-muted-foreground">
                  {t.hitDiceAvailable || 'Dadi disponibili'}: <span className="font-mono font-medium text-foreground">{combatant.hitDice.remaining ?? combatant.hitDice.total}/{combatant.hitDice.total}</span>
                </p>
              )}
              <div>
                <label className="text-sm font-medium mb-1 block">{t.shortRestNumDice || 'Numero dadi'}</label>
                <Input
                  type="number"
                  min={0}
                  max={combatant.hitDice && combatant.hitDice.total > 0 ? Math.min(20, combatant.hitDice.remaining ?? combatant.hitDice.total) : 20}
                  value={shortRestNumDice}
                  onChange={(e) => {
                    const max = combatant.hitDice && combatant.hitDice.total > 0 ? Math.min(20, combatant.hitDice.remaining ?? combatant.hitDice.total) : 20;
                    const parsed = e.target.value === '' ? 0 : parseInt(e.target.value, 10);
                    const val = isNaN(parsed) ? 0 : Math.max(0, Math.min(max, parsed));
                    setShortRestNumDice(val);
                  }}
                  className="h-9 font-mono"
                />
                <p className="text-xs text-muted-foreground mt-0.5">{t.shortRestZeroDice || '0 = riposo senza spendere dadi vita'}</p>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">{t.shortRestDieType || 'Tipo dado'}</label>
                <div className="flex gap-1 flex-wrap">
                  {[6, 8, 10, 12].map((sides) => (
                    <Button key={sides} variant={shortRestDieType === sides ? 'default' : 'outline'} size="sm" className="h-8 font-mono" onClick={() => setShortRestDieType(sides)}>d{sides}</Button>
                  ))}
                </div>
              </div>
              {shortRestSum != null && (
                <div className="rounded-md bg-muted/50 p-2 font-mono text-sm">
                  <span className="text-muted-foreground">{t.shortRestResult || 'Risultato'}: </span>
                  {shortRestRolls.join(' + ')} = <span className="font-bold text-green-600 dark:text-green-400">{shortRestSum}</span> {t.shortRestRecovered || 'PF recuperati'}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={handleShortRestRoll} disabled={parseInt(shortRestNumDice, 10) === 0}>{t.shortRestRoll || 'Tira'}</Button>
              <Button size="sm" onClick={handleShortRestApply} disabled={parseInt(shortRestNumDice, 10) > 0 && shortRestSum == null}>{t.shortRestApply || 'Applica'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {combatant.statuses?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {combatant.statuses.map(status => (
              <StatusTag key={status.id} status={status} onRemove={canEdit ? () => handleStatusRemove(status.id) : null} t={t} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const CombatantModal = ({ isOpen, onClose, onSave, editingCombatant, t, isNPC = false, isMasterAdding = false, showSaveAsPreset = false, onSaveAsPreset }) => {
  const [form, setForm] = useState({ name: '', maxHp: 10, hp: 10, ac: '', initiative: 10, initiativeBonus: 0, statuses: [], dot: null, combatantType: 'player', isEnemy: false, hpVisible: true, acVisible: true });
  const [saveAsPreset, setSaveAsPreset] = useState(false);
  const [dotDamage, setDotDamage] = useState('');
  const [dotDuration, setDotDuration] = useState('');
  const namePlaceholderRef = useRef(getRandomNamePlaceholder(t));
  const [, setPlaceholderTick] = useState(0);

  useEffect(() => {
    if (isOpen) {
      namePlaceholderRef.current = getRandomNamePlaceholder(t);
      setPlaceholderTick(tick => tick + 1);
    }
  }, [isOpen, t]);

  useEffect(() => {
    if (editingCombatant) {
      setForm({ 
        name: editingCombatant.name, 
        maxHp: editingCombatant.maxHp, 
        hp: editingCombatant.hp, 
        ac: editingCombatant.ac || '',
        initiative: editingCombatant.initiative, 
        initiativeBonus: typeof editingCombatant.initiativeBonus === 'number' ? editingCombatant.initiativeBonus : 0,
        statuses: editingCombatant.statuses || [], 
        dot: editingCombatant.dot,
        combatantType: editingCombatant.combatantType || 'player',
        isEnemy: editingCombatant.isEnemy || false,
        hpVisible: editingCombatant.hpVisible !== false,
        acVisible: editingCombatant.acVisible !== false,
      });
      if (editingCombatant.dot) { setDotDamage(editingCombatant.dot.damage.toString()); setDotDuration(editingCombatant.dot.duration?.toString() || ''); }
    } else {
      const defaultType = isNPC ? 'npc' : 'player';
      setForm({ name: '', maxHp: 10, hp: 10, ac: '', initiative: 10, initiativeBonus: 0, statuses: [], dot: null, combatantType: defaultType, isEnemy: isNPC, hpVisible: !isNPC, acVisible: !isNPC });
      setDotDamage(''); setDotDuration('');
    }
  }, [editingCombatant, isOpen, isNPC]);

  const handleSave = () => {
    if (!form.name.trim()) return;
    const maxHp = parseInt(form.maxHp) || 1;
    const hp = parseInt(form.hp) || maxHp;
    const initiative = parseInt(form.initiative) || 0;
    const initiativeBonus = parseInt(form.initiativeBonus, 10);
    const ac = form.ac ? parseInt(form.ac) : null;
    const dot = dotDamage ? { damage: parseInt(dotDamage), duration: dotDuration ? parseInt(dotDuration) : null } : null;
    onSave({ 
      ...form, 
      name: form.name.trim(), 
      maxHp, 
      hp: Math.min(hp, maxHp), 
      ac,
      initiative, 
      initiativeBonus: !isNaN(initiativeBonus) ? initiativeBonus : 0,
      dot, 
      isNPC: form.combatantType !== 'player',
      isEnemy: form.combatantType === 'enemy',
      isCompanion: form.combatantType === 'companion',
      combatantType: form.combatantType,
      hpVisible: form.hpVisible,
      acVisible: form.acVisible,
    });
    if (saveAsPreset && showSaveAsPreset && onSaveAsPreset) {
      const ac = form.ac ? parseInt(form.ac) : null;
      onSaveAsPreset({
        name: form.name.trim(),
        maxHp: parseInt(form.maxHp) || 1,
        ac,
        initiativeBonus: !isNaN(parseInt(form.initiativeBonus, 10)) ? parseInt(form.initiativeBonus, 10) : 0,
        combatantType: form.combatantType || 'npc',
        speed: 30,
      });
    }
    setSaveAsPreset(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{editingCombatant ? t.editCombatantTitle : (isNPC ? (t.addNPC || 'Aggiungi NPC/Mostro') : t.addCombatantTitle)}</DialogTitle>
          <DialogDescription className="sr-only">{t?.combatantFormDesc || 'Combatant form'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1.5 block">{t.name}</label>
            <Input value={form.name} onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))} placeholder={namePlaceholderRef.current} className="placeholder:text-muted-foreground" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium mb-1.5 block">{t.maxHp}</label>
              <Input type="number" value={form.maxHp} onChange={(e) => { const v = e.target.value; setForm(prev => ({ ...prev, maxHp: v === '' ? '' : parseInt(v) || 1 })); }} min="1" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">{t.currentHp}</label>
              <Input type="number" value={form.hp} onChange={(e) => { const v = e.target.value; setForm(prev => ({ ...prev, hp: v === '' ? '' : Math.min(parseInt(v) || 0, prev.maxHp || 999) })); }} min="0" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium mb-1.5 block">{t.initiativeValue}</label>
              <Input type="number" value={form.initiative} onChange={(e) => { const v = e.target.value; setForm(prev => ({ ...prev, initiative: v === '' ? '' : parseInt(v) || 0 })); }} placeholder="10" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">{t.initiativeBonus || 'Bonus iniziativa'}</label>
              <Input type="number" value={form.initiativeBonus} onChange={(e) => { const v = e.target.value; setForm(prev => ({ ...prev, initiativeBonus: v === '' ? '' : parseInt(v, 10) || 0 })); }} placeholder="0" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium mb-1.5 block">CA ({t.optional})</label>
            <Input type="number" value={form.ac} onChange={(e) => { const v = e.target.value; setForm(prev => ({ ...prev, ac: v })); }} placeholder="--" min="1" />
          </div>
          
          {/* Combatant Type Selection - only for Master adding */}
          {(isNPC || isMasterAdding) && (
            <div className="space-y-3 p-3 bg-muted/50 rounded-lg">
              <label className="text-sm font-medium block mb-2">Tipo di Combattente</label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant={form.combatantType === 'player' ? 'default' : 'outline'}
                  className="justify-start h-auto py-3"
                  onClick={() => setForm(prev => ({ ...prev, combatantType: 'player', isEnemy: false, isCompanion: false, hpVisible: true, acVisible: true }))}
                >
                  <div className="flex items-center gap-2 w-full">
                    <div className="w-3 h-3 rounded-full bg-white border-2 border-gray-400" />
                    <span className="text-xs">Giocatore</span>
                  </div>
                </Button>
                <Button
                  type="button"
                  variant={form.combatantType === 'npc' ? 'default' : 'outline'}
                  className="justify-start h-auto py-3"
                  onClick={() => setForm(prev => ({ ...prev, combatantType: 'npc', isEnemy: false, isCompanion: false, hpVisible: true }))}
                >
                  <div className="flex items-center gap-2 w-full">
                    <div className="w-3 h-3 rounded-full bg-blue-500" />
                    <span className="text-xs">NPC Alleato</span>
                  </div>
                </Button>
                <Button
                  type="button"
                  variant={form.combatantType === 'enemy' ? 'default' : 'outline'}
                  className="justify-start h-auto py-3"
                  onClick={() => setForm(prev => ({ ...prev, combatantType: 'enemy', isEnemy: true, isCompanion: false, hpVisible: false }))}
                >
                  <div className="flex items-center gap-2 w-full">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <span className="text-xs">Nemico</span>
                  </div>
                </Button>
                <Button
                  type="button"
                  variant={form.combatantType === 'companion' ? 'default' : 'outline'}
                  className="justify-start h-auto py-3"
                  onClick={() => setForm(prev => ({ ...prev, combatantType: 'companion', isEnemy: false, isCompanion: true, hpVisible: true }))}
                >
                  <div className="flex items-center gap-2 w-full">
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                    <span className="text-xs">Famiglio</span>
                  </div>
                </Button>
              </div>
              
              {/* HP Visibility */}
              {form.combatantType !== 'player' && (
                <div className="flex items-center justify-between pt-2">
                  <div className="flex items-center gap-2">
                    {form.hpVisible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4 opacity-50" />}
                    <span className="text-sm font-medium">{form.hpVisible ? 'PF visibili' : 'PF nascosti'}</span>
                  </div>
                  <Switch 
                    checked={form.hpVisible} 
                    onCheckedChange={(checked) => setForm(prev => ({ ...prev, hpVisible: checked }))}
                  />
                </div>
              )}
              
              {/* AC Visibility */}
              {form.combatantType !== 'player' && form.ac && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    <span className="text-sm font-medium">{form.acVisible ? 'CA visibile' : 'CA nascosta'}</span>
                  </div>
                  <Switch 
                    checked={form.acVisible} 
                    onCheckedChange={(checked) => setForm(prev => ({ ...prev, acVisible: checked }))}
                  />
                </div>
              )}
            </div>
          )}
          
          <div>
            <label className="text-sm font-medium mb-1.5 block flex items-center gap-2"><Flame className="w-4 h-4 text-red-500" />{t.dotLabel ?? 'Danno per turno'} ({t.optional})</label>
            <div className="grid grid-cols-2 gap-3">
              <Input type="number" placeholder={t.dotDamage} value={dotDamage} onChange={(e) => setDotDamage(e.target.value)} min="1" />
              <Input type="number" placeholder={t.dotDuration} value={dotDuration} onChange={(e) => setDotDuration(e.target.value)} min="1" />
            </div>
          </div>
          {showSaveAsPreset && (
            <div className="flex items-center justify-between rounded-lg border p-3 bg-muted/30">
              <span className="text-sm font-medium">{t.saveToPresets || 'Salva nei preset'}</span>
              <Switch checked={saveAsPreset} onCheckedChange={setSaveAsPreset} />
            </div>
          )}
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>{t.cancel}</Button>
          <Button onClick={handleSave} disabled={!form.name.trim()}>{t.save}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CombatScreen = ({ combat, onBack, t, theme, toggleTheme, accent, setAccent, lang, setLang, onExport, onImport, fileInputRef, handleImport, isMaster = true, isPersonal = false, currentUserPeerId = null, savedCharacters = null, onUpdateSavedCharacterXp = null, onAddSavedCharacter = null, onGoHome = null, coinPouchAddOnly = false }) => {
  const { presets: npcPresetsList, addPreset, removePreset } = useNpcPresets();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNPCModal, setIsNPCModal] = useState(false);
  const [isMasterAdding, setIsMasterAdding] = useState(false);
  const [editingCombatant, setEditingCombatant] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showQuickInitiativePrompt, setShowQuickInitiativePrompt] = useState(false);
  const [concentrationCheckPending, setConcentrationCheckPending] = useState(null); // { combatantId, amount } quando un concentrato subisce danno
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [presetPopoverOpen, setPresetPopoverOpen] = useState(false);
  const [showPresetModal, setShowPresetModal] = useState(false);
  const [presetForm, setPresetForm] = useState({ name: '', maxHp: 10, ac: '', initiativeBonus: 0, combatantType: 'enemy' });
  const [showEventHistory, setShowEventHistory] = useState(false);
  const [eventHistory, setEventHistory] = useState([]);
  const [showCharacterPicker, setShowCharacterPicker] = useState(false); // Solo in "Sono un player": aggiungi dai personaggi salvati
  const [coinPouchCombatantId, setCoinPouchCombatantId] = useState(null); // Borsa monete: quale combattente è aperto
  const [collapsedIds, setCollapsedIds] = useState(() => new Set());
  const [xpCombatantId, setXpCombatantId] = useState(null);
  const [xpToAdd, setXpToAdd] = useState('');
  const [isEditingTotalXp, setIsEditingTotalXp] = useState(false);
  const [xpTotalDraft, setXpTotalDraft] = useState('');

  const getLinkedCharacterByCombatant = useCallback((combatant) => {
    if (!combatant || !Array.isArray(savedCharacters)) return null;
    return savedCharacters.find((character) => character.name === combatant.name) || null;
  }, [savedCharacters]);

  const xpDialogCombatant = useMemo(
    () => combat.combatants.find((combatant) => combatant.id === xpCombatantId) || null,
    [combat.combatants, xpCombatantId]
  );

  const xpDialogCharacter = useMemo(
    () => getLinkedCharacterByCombatant(xpDialogCombatant),
    [getLinkedCharacterByCombatant, xpDialogCombatant]
  );

  const openXpDialog = useCallback((combatant) => {
    const linkedCharacter = getLinkedCharacterByCombatant(combatant);
    if (!linkedCharacter) {
      toast.info(t.noSavedCharacters || 'Nessun personaggio salvato');
      return;
    }
    const currentXp = Math.max(0, parseInt(linkedCharacter.xp, 10) || 0);
    setXpCombatantId(combatant.id);
    setXpToAdd('');
    setIsEditingTotalXp(false);
    setXpTotalDraft(String(currentXp));
  }, [getLinkedCharacterByCombatant, t.noSavedCharacters]);

  const closeXpDialog = useCallback(() => {
    setXpCombatantId(null);
    setXpToAdd('');
    setIsEditingTotalXp(false);
    setXpTotalDraft('');
  }, []);

  const handleApplyXpGain = useCallback(() => {
    if (!xpDialogCharacter || !onUpdateSavedCharacterXp) return;
    const gainedXp = parseInt(xpToAdd, 10);
    if (!Number.isFinite(gainedXp) || gainedXp <= 0) return;
    const currentXp = Math.max(0, parseInt(xpDialogCharacter.xp, 10) || 0);
    const nextXp = currentXp + gainedXp;
    onUpdateSavedCharacterXp(xpDialogCharacter.id, { xp: nextXp });
    setXpToAdd('');
    setXpTotalDraft(String(nextXp));
    toast.success(`${t.xpGained || 'XP aggiunta'}: +${gainedXp}`);
  }, [xpDialogCharacter, onUpdateSavedCharacterXp, xpToAdd, t.xpGained]);

  const handleSaveXpTotal = useCallback(() => {
    if (!xpDialogCharacter || !onUpdateSavedCharacterXp) return;
    const parsedTotal = parseInt(xpTotalDraft, 10);
    const safeTotal = Number.isFinite(parsedTotal) ? Math.max(0, parsedTotal) : 0;
    onUpdateSavedCharacterXp(xpDialogCharacter.id, { xp: safeTotal });
    setXpTotalDraft(String(safeTotal));
    setIsEditingTotalXp(false);
    toast.success(t.xpTotalUpdated || 'XP totale aggiornata');
  }, [xpDialogCharacter, onUpdateSavedCharacterXp, xpTotalDraft, t.xpTotalUpdated]);

  // Add event to history
  const addEvent = useCallback((type, data) => {
    const event = {
      id: Date.now(),
      timestamp: new Date().toLocaleTimeString(),
      round: combat.round,
      type,
      ...data
    };

    setEventHistory(prev => {
      const latestEvent = prev[0];
      const canMergeWithLatest =
        latestEvent &&
        latestEvent.round === event.round &&
        latestEvent.name === event.name &&
        latestEvent.type === event.type &&
        (event.type === 'damage' || event.type === 'heal');

      if (canMergeWithLatest) {
        const mergedEvent = {
          ...latestEvent,
          id: event.id,
          timestamp: event.timestamp,
          damage: event.type === 'damage' ? (latestEvent.damage || 0) + (event.damage || 0) : latestEvent.damage,
          amount: event.type === 'heal' ? (latestEvent.amount || 0) + (event.amount || 0) : latestEvent.amount,
        };
        return [mergedEvent, ...prev.slice(1)].slice(0, 100);
      }

      return [event, ...prev].slice(0, 100);
    }); // Keep last 100 events
  }, [combat.round]);

  // Wrapper modifyHp: se è danno e il combattente è in concentrazione, mostra dialog TS concentrazione
  const handleModifyHp = useCallback((id, amount) => {
    if (amount >= 0) {
      combat.modifyHp(id, amount);
      return;
    }
    const comb = combat.combatants.find(c => c.id === id);
    if (comb?.isConcentrating) {
      setConcentrationCheckPending({ combatantId: id, amount });
      return;
    }
    combat.modifyHp(id, amount);
  }, [combat]);

  // Determine if user can edit a specific combatant
  const canEditCombatant = (combatant) => {
    if (isMaster) return true; // Master can edit ALL combatants
    if (!isMaster && combatant.peerId === currentUserPeerId) return true; // Player can edit their own characters
    if (isPersonal) return true; // In personal mode, can edit all
    return false;
  };

  const handleAddCombatant = (data) => {
    if (editingCombatant) {
      combat.updateCombatant(editingCombatant.id, data);
      toast.success(t.combatantUpdated);
    } else {
      // Add owner info
      const combatantData = {
        ...data,
        ownerId: isMaster ? 'master' : currentUserPeerId,
      };
      combat.addCombatant(combatantData);
      toast.success(t.combatantAdded);
      addEvent('added', { name: data.name });
    }
    setEditingCombatant(null);
    setIsMasterAdding(false);
  };

  const handleDuplicateCombatant = (combatant) => {
    if (!combat?.addCombatant) return;
    const baseName = (combatant.name || '').replace(/\s+\d+$/, '').trim() || (t.duplicate || 'Duplica');
    let maxNum = 0;
    for (const c of combat.combatants) {
      if (c.name === baseName) maxNum = Math.max(maxNum, 1);
      else if (c.name.startsWith(baseName + ' ')) {
        const num = parseInt(c.name.slice(baseName.length + 1), 10);
        if (!isNaN(num)) maxNum = Math.max(maxNum, num);
      }
    }
    const nextNum = maxNum + 1;
    const newName = nextNum === 1 ? baseName : `${baseName} ${nextNum}`;
    const { id, ...rest } = combatant;
    combat.addCombatant({
      ...rest,
      name: newName,
      hp: combatant.maxHp ?? combatant.hp,
      tempHp: 0,
      statuses: Array.isArray(combatant.statuses) ? [...combatant.statuses] : [],
      deathSaves: { successes: 0, failures: 0 },
      dot: null,
      isConcentrating: false,
      actionUsed: false,
      bonusActionUsed: false,
      reactionUsed: false,
      dashed: false,
    });
    toast.success(t.duplicate || 'Duplica');
    addEvent('duplicate', { name: combatant.name });
  };

  const handleNextTurn = () => {
    const events = combat.nextTurn();
    events.forEach(event => {
      if (event.type === 'turnChange') {
        toast.info(`${t.turnChanged} ${event.combatant}`);
        addEvent('turn', { name: event.combatant });
      }
      else if (event.type === 'dot') {
        toast.warning(`${t.dotApplied} ${event.damage} ${t.damagePoints} (${event.combatant})`);
        addEvent('dot', { name: event.combatant, damage: event.damage });
      }
      else if (event.type === 'statusExpired') {
        toast.info(`${t.statusExpired} ${event.status} (${event.combatant})`);
        addEvent('statusExpired', { name: event.combatant, status: event.status });
      }
      else if (event.type === 'regen') {
        toast.success(`${event.combatant}: +${event.amount} ${t.hpText || 'PF'} (${t.statusDictionary?.['Rigenerazione']?.name || 'Rigenerazione'})`);
        addEvent('regen', { name: event.combatant, amount: event.amount });
      }
      else if (event.type === 'bleed') {
        toast.warning(`${event.combatant}: ${event.damage} ${t.damageText || 'danni'} (${t.statusDictionary?.['Sanguinante']?.name || 'Sanguinante'})`);
        addEvent('bleed', { name: event.combatant, damage: event.damage });
      }
    });
  };

  const handleNextRound = () => {
    if (combat.combatants.length === 0) return;
    const stepsToNextRound = Math.max(1, combat.combatants.length - (combat.currentTurnIndex || 0));
    for (let i = 0; i < stepsToNextRound; i += 1) {
      combat.nextTurn();
    }
    toast.success(`${t.round || 'Round'} ${combat.round + 1}`);
  };

  return (
    <div className="coco-v2-screen min-h-[100svh] sm:min-h-[100dvh] flex flex-col min-w-0">
      <Toaster theme={theme} />
      <input ref={fileInputRef} type="file" accept=".json" onChange={handleImport} className="hidden" />
      
      <header className="sticky top-0 z-50 glass coco-v2-header shrink-0">
        <div className="w-full max-w-lg sm:max-w-2xl lg:max-w-6xl xl:max-w-7xl mx-auto min-w-0 px-4 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1 sm:gap-2 min-w-0 flex-1">
            <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={onBack} aria-label={t.back || 'Indietro'}><ArrowLeft className="w-5 h-5" /></Button>
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="text-xs sm:text-sm text-muted-foreground shrink-0">{t.round}</span>
              <span className="font-mono font-bold text-xl sm:text-2xl tabular-nums">{combat.round}</span>
            </div>
          </div>
          <button type="button" onClick={() => (onGoHome ?? onBack)()} className="absolute left-1/2 -translate-x-1/2 flex items-center justify-center w-11 h-11 sm:w-12 sm:h-12 shrink-0 rounded-md hover:opacity-90 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background" aria-label={t.backToHome ?? 'Torna alla home'}>
            <img src={logoUrl(theme)} alt="CoCo" className="w-full h-full object-contain select-none" />
          </button>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0 flex-1 justify-end">
            <LanguageDropdown lang={lang} setLang={setLang} t={t} />
            <AppearanceDropdown theme={theme} toggleTheme={toggleTheme} accent={accent} setAccent={setAccent} t={t} />
          </div>
        </div>
      </header>

      <main className="w-full max-w-lg sm:max-w-2xl lg:max-w-6xl xl:max-w-7xl mx-auto min-w-0 px-4 sm:px-6 lg:px-8 pt-3 sm:pt-4 pad-main-above-fixed">
        {combat.combatants.length === 0 ? (
          <div className="text-center py-20">
            <Zap className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-bold mb-2">{t.noCombatants}</h2>
            <p className="text-muted-foreground">{t.addFirst}</p>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row lg:items-start gap-4 lg:gap-6 xl:gap-8">
            <aside className="w-full lg:w-[360px] xl:w-[400px] lg:shrink-0 space-y-4">
              {/* Initiative Order Bar */}
              <div>
                  <Card className="border-2 accent-border-30 shadow-lg bg-gradient-to-r from-[hsl(var(--accent-theme)/0.24)] via-[hsl(var(--accent-theme)/0.14)] to-[hsl(var(--accent-theme)/0.08)]">
                    <CardContent className="p-2.5 sm:p-3">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="min-w-0 text-center w-full">
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-7 h-7 rounded-full accent-bg flex items-center justify-center shrink-0">
                              <Zap className="w-3.5 h-3.5 text-white" />
                            </div>
                            <span className="font-bold text-sm">{t.initiativeOrder || 'Ordine di Iniziativa'}</span>
                            <Badge variant="default" className="accent-bg text-white border-0 h-6 px-2 text-[11px]">{(t.round || 'Round')} {combat.round}</Badge>
                          </div>
                          <div className="mt-1.5 flex flex-wrap items-center justify-center gap-1 text-xs text-muted-foreground">
                            <Badge variant="outline" className="accent-border-30 h-5 px-1.5 text-[10px]">{combat.combatants.length} {t.combatants || 'combattenti'}</Badge>
                            {combat.combatants[combat.currentTurnIndex] && (
                              <Badge variant="outline" className="accent-border-30 h-5 px-1.5 text-[10px]">
                                {t.currentTurn || 'Turno'}: <span className="ml-1 font-medium">{combat.combatants[combat.currentTurnIndex].name}</span>
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {combat.combatants.map((c, i) => (
                          <div key={c.id} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs border transition-all min-h-[32px] ${i === combat.currentTurnIndex ? 'accent-bg text-white accent-border-50 font-semibold shadow-sm ring-1 ring-accent/50' : 'bg-background/90 border-border/50 hover:accent-border-30'}`}>
                            <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full font-mono text-[11px] font-black ${i === combat.currentTurnIndex ? 'bg-white/20 text-white' : 'accent-bg-10 accent-text'}`}>{i + 1}</span>
                            <span className={`font-mono font-bold ${i === combat.currentTurnIndex ? 'text-white' : 'text-black dark:text-black'}`}>{c.initiative}</span>
                            {(c.isNPC || c.isCompanion || c.combatantType !== 'player') && (<div className={`w-2 h-2 rounded-full ${c.isCompanion || c.combatantType === 'companion' ? 'bg-green-500' : c.isEnemy || c.combatantType === 'enemy' ? 'bg-red-500' : c.isNPC || c.combatantType === 'npc' ? 'bg-blue-500' : ''}`} />)}
                            <span className={`font-medium truncate max-w-[110px] sm:max-w-[130px] ${c.isKO ? 'line-through opacity-50' : ''}`} title={c.name}>{c.name}</span>
                            {c.isKO && <Skull className="w-2.5 h-2.5 opacity-70 shrink-0" />}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="mt-2 border accent-border-30 bg-background/55 backdrop-blur-sm">
                    <CardContent className="p-2 sm:p-2.5">
                      <div className="grid grid-cols-2 sm:flex sm:flex-row items-stretch gap-2">
                        <Button variant="ghost" size="sm" className="h-9 sm:h-8 text-xs accent-text hover:accent-bg-20 px-3 justify-center gap-1.5 rounded-md border border-transparent hover:accent-border-30 min-w-0" onClick={() => setShowEventHistory(true)} title={t.eventHistory || 'Cronologia'}><ScrollText className="w-4 h-4 shrink-0" /><span className="truncate">{t.eventHistory || 'Cronologia'}</span></Button>
                        {isMaster && (<Button variant="ghost" size="sm" className="h-9 sm:h-8 text-xs accent-text hover:accent-bg-20 px-3 justify-center gap-1.5 rounded-md border border-transparent hover:accent-border-30 min-w-0" onClick={() => setShowQuickInitiativePrompt(true)} title={t.quickInitiative || 'Iniziativa rapida'}><RotateCcw className="w-4 h-4 shrink-0" /><span className="truncate">{t.quickInitiative || 'Iniziativa rapida'}</span></Button>)}
                      </div>
                    </CardContent>
                  </Card>
              </div>

              {/* Note di battaglia */}
              <Card className="bg-muted/20 border-border/50">
                <Button
                  variant="ghost"
                  className="w-full h-auto p-3 sm:p-4 justify-start gap-2 hover:bg-muted/30"
                  onClick={() => setNotesExpanded(prev => !prev)}
                >
                  <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="text-sm font-medium text-muted-foreground">{t.battleNotes || 'Note di battaglia'}</span>
                  {notesExpanded ? <ChevronUp className="w-4 h-4 ml-auto shrink-0 opacity-70" /> : <ChevronDown className="w-4 h-4 ml-auto shrink-0 opacity-70" />}
                </Button>
                {notesExpanded && (
                  <CardContent className="pt-0 px-3 sm:px-4 pb-3 sm:pb-4">
                    <Textarea
                      placeholder={t.battleNotesPlaceholder || 'Appunti, obiettivi, reminder...'}
                      value={combat.notes ?? ''}
                      onChange={(e) => combat.setNotes(e.target.value)}
                      className="min-h-[72px] text-sm resize-y max-h-[180px] placeholder:text-muted-foreground/80"
                      rows={3}
                    />
                  </CardContent>
  )}
              </Card>
              {combat.combatants.length > 0 && (
                <div className="flex justify-end lg:hidden">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 text-xs accent-text hover:accent-bg-20 px-3 justify-center gap-1.5 rounded-md border border-transparent hover:accent-border-30"
                    onClick={() => {
                      const allCollapsed = collapsedIds.size >= combat.combatants.length;
                      setCollapsedIds(allCollapsed ? new Set() : new Set(combat.combatants.map(c => c.id)));
                    }}
                    title={collapsedIds.size >= combat.combatants.length ? t.expandAll : t.collapseAll}
                  >
                    {collapsedIds.size >= combat.combatants.length ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronUp className="w-4 h-4 shrink-0" />}
                    <span className="truncate">{collapsedIds.size >= combat.combatants.length ? t.expandAll : t.collapseAll}</span>
                  </Button>
                </div>
              )}
              <div className="hidden lg:flex flex-col gap-2">
                {(isMaster && !isPersonal) && (
                  <Button
                    className="w-full h-10 px-4 text-sm font-medium rounded-lg"
                    onClick={() => { setEditingCombatant(null); setIsNPCModal(false); setIsMasterAdding(true); setIsModalOpen(true); }}
                    data-testid="add-combatant-btn-desktop"
                  >
                    <Plus className="w-4 h-4 mr-2 shrink-0" />
                    <span className="truncate min-w-0">{t.addCombatant}</span>
                  </Button>
                )}
                {isPersonal && savedCharacters?.length > 0 && (
                  <Button
                    className="w-full h-10 px-4 text-sm font-medium rounded-lg"
                    onClick={() => setShowCharacterPicker(true)}
                    data-testid="add-character-from-list-btn-desktop"
                  >
                    <Plus className="w-4 h-4 mr-2 shrink-0" />
                    <span className="truncate min-w-0">{t.addCharacter || 'Inserisci un personaggio'}</span>
                  </Button>
                )}
                {isMaster && (
                  <Button
                    variant="secondary"
                    onClick={handleNextTurn}
                    disabled={combat.combatants.length === 0}
                    className="w-full h-10 px-4 text-sm font-semibold text-foreground rounded-lg inline-flex items-center justify-center gap-2"
                    data-testid="next-turn-btn-desktop"
                  >
                    <span className="truncate">{t.nextTurn}</span>
                    <ChevronRight className="w-4 h-4 shrink-0 flex-shrink-0" aria-hidden />
                  </Button>
                )}
                {isMaster && (
                  <Button
                    variant="outline"
                    onClick={handleNextRound}
                    disabled={combat.combatants.length === 0}
                    className="w-full h-10 px-4 text-sm font-semibold rounded-lg inline-flex items-center justify-center gap-2"
                    data-testid="next-round-btn-desktop"
                  >
                    <span className="truncate">{t.nextRound || 'Prossimo round'}</span>
                    <RotateCw className="w-4 h-4 shrink-0" />
                  </Button>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="w-full h-10 px-4 text-sm font-medium rounded-lg inline-flex items-center justify-center gap-2">
                      <MoreVertical className="w-4 h-4" />
                      <span>Opzioni</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    <DropdownMenuItem onClick={() => setShowEventHistory(true)}><ScrollText className="w-4 h-4 mr-2" />{t.eventHistory || 'Cronologia Eventi'}</DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => { if (combat.currentCombatant) { combat.shortRest(combat.currentCombatant.id); toast.success(t.shortRestDone || 'Short rest'); } }}
                      disabled={!combat.currentCombatant}
                    >
                      <Heart className="w-4 h-4 mr-2" />{t.shortRest || 'Short Rest'} ({t.currentTurn || 'Turno attivo'})
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => { if (combat.currentCombatant) { combat.longRest(combat.currentCombatant.id); toast.success(t.longRestDone || 'Long rest'); } }}
                      disabled={!combat.currentCombatant}
                    >
                      <Moon className="w-4 h-4 mr-2" />{t.longRest || 'Long Rest'} ({t.currentTurn || 'Turno attivo'})
                    </DropdownMenuItem>
                    {isMaster && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => { combat.shortRest(); toast.success(t.shortRestAllDone || 'Short rest (tutti)'); }}>
                          <Heart className="w-4 h-4 mr-2" />{t.shortRestAll || 'Short Rest (tutti)'}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { combat.longRest(); toast.success(t.longRestAllDone || 'Long rest (tutti)'); }}>
                          <Moon className="w-4 h-4 mr-2" />{t.longRestAll || 'Long Rest (tutti)'}
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={onExport}><Download className="w-4 h-4 mr-2" />{t.exportJson}</DropdownMenuItem>
                    <DropdownMenuItem onClick={onImport}><Upload className="w-4 h-4 mr-2" />{t.importJson}</DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="text-destructive" onClick={() => setConfirmReset(true)}><RotateCcw className="w-4 h-4 mr-2" />{t.reset}</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </aside>

            <section className="flex-1 min-w-0 space-y-4">
              {combat.combatants.length > 0 && (
                <div className="hidden lg:flex justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 text-xs accent-text hover:accent-bg-20 px-3 justify-center gap-1.5 rounded-md border border-transparent hover:accent-border-30"
                    onClick={() => {
                      const allCollapsed = collapsedIds.size >= combat.combatants.length;
                      setCollapsedIds(allCollapsed ? new Set() : new Set(combat.combatants.map(c => c.id)));
                    }}
                    title={collapsedIds.size >= combat.combatants.length ? t.expandAll : t.collapseAll}
                  >
                    {collapsedIds.size >= combat.combatants.length ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronUp className="w-4 h-4 shrink-0" />}
                    <span className="truncate">{collapsedIds.size >= combat.combatants.length ? t.expandAll : t.collapseAll}</span>
                  </Button>
                </div>
              )}
              {combat.combatants.map((c, i) => (
                <CombatantCard 
                  key={c.id} 
                  combatant={c} 
                  isActive={i === combat.currentTurnIndex}
                  onModifyHp={canEditCombatant(c) ? handleModifyHp : null}
                  onSetTempHp={canEditCombatant(c) ? combat.setTempHp : null}
                  onAddStatus={canEditCombatant(c) ? combat.addStatus : null}
                  onRemoveStatus={canEditCombatant(c) ? combat.removeStatus : null}
                  onToggleKO={canEditCombatant(c) ? combat.toggleKO : null}
                  onEdit={canEditCombatant(c) ? (c) => { setEditingCombatant(c); setIsModalOpen(true); } : null}
                  onDelete={canEditCombatant(c) ? setDeleteTarget : null}
                  onDuplicate={isMaster ? handleDuplicateCombatant : null}
                  onUpdateDeathSaves={canEditCombatant(c) ? combat.updateDeathSaves : null}
                  onResetDeathSaves={canEditCombatant(c) ? combat.resetDeathSaves : null}
                  onUpdateCombatant={canEditCombatant(c) ? combat.updateCombatant : null}
                  onRollInitiative={canEditCombatant(c) ? combat.rollInitiative : null}
                  onShortRest={canEditCombatant(c) ? combat.shortRest : null}
                  onLongRest={canEditCombatant(c) ? combat.longRest : null}
                  onLogEvent={addEvent}
                  t={t}
                  canEdit={canEditCombatant(c)}
                  isMaster={isMaster}
                  allowDuplicate={isMaster && !isPersonal}
                  collapsed={collapsedIds.has(c.id)}
                  onToggleCollapse={() => setCollapsedIds(prev => {
                    const next = new Set(prev);
                    if (next.has(c.id)) next.delete(c.id);
                    else next.add(c.id);
                    return next;
                  })}
                  onOpenCoinPouch={c.combatantType === 'player' && canEditCombatant(c) ? () => setCoinPouchCombatantId(c.id) : null}
                  xpTotal={(() => {
                    const linkedCharacter = getLinkedCharacterByCombatant(c);
                    return linkedCharacter ? Math.max(0, parseInt(linkedCharacter.xp, 10) || 0) : null;
                  })()}
                  onOpenXp={isPersonal ? openXpDialog : null}
                />
              ))}
            </section>
          </div>
        )}
      </main>

      {/* Modal borsa monete (solo personaggi) */}
      {coinPouchCombatantId && (
        <CoinPouchModal
          isOpen={!!coinPouchCombatantId}
          onClose={() => setCoinPouchCombatantId(null)}
          combatant={combat.combatants.find(c => c.id === coinPouchCombatantId)}
          onSave={(coinPouch) => combat.updateCombatant(coinPouchCombatantId, { coinPouch })}
          t={t}
          addOnly={coinPouchAddOnly}
        />
      )}

      <Dialog open={!!xpDialogCombatant} onOpenChange={(open) => { if (!open) closeXpDialog(); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t.xp || 'XP'}{xpDialogCombatant ? ` · ${xpDialogCombatant.name}` : ''}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border accent-border-30 p-3 bg-muted/20">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-muted-foreground">{t.xpTotal || 'XP totale'}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => {
                    const currentXp = Math.max(0, parseInt(xpDialogCharacter?.xp, 10) || 0);
                    setXpTotalDraft(String(currentXp));
                    setIsEditingTotalXp(true);
                  }}
                  title={t.editTotalXp || 'Modifica XP totale'}
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
              </div>
              {!isEditingTotalXp ? (
                <p className="text-2xl font-bold tracking-tight mt-1">{Math.max(0, parseInt(xpDialogCharacter?.xp, 10) || 0)}</p>
              ) : (
                <div className="mt-2 space-y-2">
                  <Input
                    type="number"
                    min="0"
                    value={xpTotalDraft}
                    onChange={(e) => setXpTotalDraft(e.target.value)}
                    placeholder="0"
                  />
                  <div className="flex items-center justify-end gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => setIsEditingTotalXp(false)}>
                      {t.cancel || 'Annulla'}
                    </Button>
                    <Button type="button" size="sm" onClick={handleSaveXpTotal}>
                      {t.save || 'Salva'}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">{t.addXp || 'Aggiungi XP guadagnata'}</label>
              <Input
                type="number"
                min="1"
                value={xpToAdd}
                onChange={(e) => setXpToAdd(e.target.value)}
                placeholder="0"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeXpDialog}>{t.close || 'Chiudi'}</Button>
            <Button onClick={handleApplyXpGain} disabled={!xpToAdd || (parseInt(xpToAdd, 10) || 0) <= 0 || !xpDialogCharacter}>
              {t.addXpAction || 'Aggiungi XP'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="fixed bottom-0 left-0 right-0 z-50 glass border-t border-border/50 safe-area-pb lg:hidden">
        <div className="w-full max-w-lg sm:max-w-2xl lg:max-w-6xl xl:max-w-7xl mx-auto min-w-0 px-4 sm:px-6 lg:px-8 py-2 sm:py-3 flex items-center justify-center gap-3">
          {/* Master Offline: aggiungi combattente libero. Sono un player: aggiungi solo dai personaggi salvati. */}
          {(isMaster && !isPersonal) && (
            <>
              <Button 
                className="h-10 px-3 sm:px-4 flex-1 min-w-0 text-sm order-1 whitespace-nowrap rounded-lg lg:hidden" 
                onClick={() => { setEditingCombatant(null); setIsNPCModal(false); setIsMasterAdding(true); setIsModalOpen(true); }}
                data-testid="add-combatant-btn"
              >
                <Plus className="w-4 h-4 mr-1.5 shrink-0" /><span className="truncate min-w-0">{t.addCombatant}</span>
              </Button>
              <Popover open={presetPopoverOpen} onOpenChange={setPresetPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="icon" className="h-10 w-10 shrink-0 order-1 rounded-lg" title={t.npcPresetsTitle || 'Preset NPC'}>
                    <Users className="w-4 h-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 max-h-[42vh] overflow-hidden flex flex-col p-0" align="center" side="top">
                  <div className="py-1.5 px-2.5 border-b font-medium text-xs text-muted-foreground">{t.npcPresetsTitle || 'Preset NPC'}</div>
                  <div className="overflow-y-auto flex-1 p-1.5 space-y-2 min-h-0">
                    <Button variant="outline" size="sm" className="w-full h-7 text-xs justify-center gap-1.5" onClick={() => { setPresetForm({ name: '', maxHp: 10, ac: '', initiativeBonus: 0, combatantType: 'enemy' }); setShowPresetModal(true); setPresetPopoverOpen(false); }}>
                      <Plus className="w-3.5 h-3.5" />
                      {t.npcPresetNew || 'Nuovo preset'}
                    </Button>
                    {npcPresetsList.length > 0 && (
                      <div className="space-y-1">
                        <span className="text-[11px] font-medium text-muted-foreground px-0.5 block">{t.presetsList || 'Preset salvati'}</span>
                        {npcPresetsList.map((p) => (
                          <div key={p.id} className="flex items-center gap-1.5 py-1 px-1.5 rounded-md bg-muted/40 border border-border/40">
                            <div className="min-w-0 flex-1">
                              <span className="font-medium text-xs truncate block">{p.name}</span>
                              <span className="text-[11px] text-muted-foreground">{p.maxHp} PF{p.ac != null && p.ac !== '' ? ` · CA ${p.ac}` : ''}</span>
                            </div>
                            <div className="flex items-center gap-0.5 shrink-0">
                              <Button variant="default" size="sm" className="h-6 px-1.5 text-[11px]" onClick={() => {
                                combat.addCombatant({
                                  name: p.name, hp: p.maxHp, maxHp: p.maxHp,
                                  ac: p.ac != null && p.ac !== '' ? parseInt(p.ac, 10) : null,
                                  initiative: 10, initiativeBonus: typeof p.initiativeBonus === 'number' ? p.initiativeBonus : 0,
                                  speed: typeof p.speed === 'number' ? p.speed : 30,
                                  isNPC: p.combatantType !== 'player', isEnemy: p.combatantType === 'enemy', isCompanion: p.combatantType === 'companion',
                                  combatantType: p.combatantType || 'npc', ownerId: 'master',
                                });
                                toast.success(t.combatantAdded);
                              }}>{t.addToBattleShort || 'In battaglia'}</Button>
                              <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive hover:bg-destructive/10" onClick={() => removePreset(p.id)} title={t.delete}><Trash2 className="w-3 h-3" /></Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {combat.combatants.length > 0 && (
                      <div className="space-y-1 pt-1.5 border-t border-border/50">
                        <span className="text-[11px] font-medium text-muted-foreground px-0.5 block">{t.saveFromBattle || 'Da battaglia'}</span>
                        {combat.combatants.map((c) => (
                          <div key={c.id} className="flex items-center gap-1.5 py-1 px-1.5 rounded-md bg-background/80 border border-border/40">
                            <span className="font-medium text-xs truncate flex-1 min-w-0">{c.name}</span>
                            <Button variant="outline" size="sm" className="h-6 px-1.5 text-[11px] shrink-0" onClick={() => {
                              addPreset({
                                name: c.name,
                                maxHp: c.maxHp,
                                ac: c.ac != null && c.ac !== '' ? (typeof c.ac === 'number' ? c.ac : parseInt(c.ac, 10)) : null,
                                initiativeBonus: typeof c.initiativeBonus === 'number' ? c.initiativeBonus : 0,
                                combatantType: c.combatantType || (c.isEnemy ? 'enemy' : c.isCompanion ? 'companion' : 'npc'),
                                speed: typeof c.speed === 'number' ? c.speed : 30,
                              });
                              toast.success(t.npcPresetSaved || 'Preset salvato');
                            }}>{t.saveAsPresetShort || 'Salva preset'}</Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </>
          )}
          {isPersonal && savedCharacters?.length > 0 && (
            <Button 
              className="min-h-[48px] h-12 px-4 sm:px-5 flex-1 min-w-0 text-sm sm:text-base order-1 whitespace-nowrap lg:hidden"
              onClick={() => setShowCharacterPicker(true)}
              data-testid="add-character-from-list-btn"
            >
              <Plus className="w-5 h-5 mr-1.5 sm:mr-2 shrink-0" /><span className="truncate min-w-0">{t.addCharacter || 'Aggiungi personaggio'}</span>
            </Button>
          )}
          {isMaster && (
            <Button variant="secondary" onClick={handleNextTurn} disabled={combat.combatants.length === 0} className="h-10 px-4 sm:px-5 flex-1 min-w-0 text-sm font-semibold text-foreground order-2 whitespace-nowrap rounded-lg inline-flex items-center justify-center gap-2 lg:hidden">
              <span className="truncate">{t.nextTurn}</span>
              <ChevronRight className="w-4 h-4 shrink-0 flex-shrink-0" aria-hidden />
            </Button>
          )}
          <div className="lg:hidden">
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button variant="outline" size="icon" className="h-10 w-10 shrink-0 order-3 rounded-lg"><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {isMaster && (
                  <>
                    <DropdownMenuItem onClick={handleNextRound} disabled={combat.combatants.length === 0}>
                      <RotateCw className="w-4 h-4 mr-2" />{t.nextRound || 'Prossimo round'}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem onClick={() => setShowEventHistory(true)}><ScrollText className="w-4 h-4 mr-2" />{t.eventHistory || 'Cronologia Eventi'}</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => { if (combat.currentCombatant) { combat.shortRest(combat.currentCombatant.id); toast.success(t.shortRestDone || 'Short rest'); } }}
                  disabled={!combat.currentCombatant}
                >
                  <Heart className="w-4 h-4 mr-2" />{t.shortRest || 'Short Rest'} ({t.currentTurn || 'Turno attivo'})
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => { if (combat.currentCombatant) { combat.longRest(combat.currentCombatant.id); toast.success(t.longRestDone || 'Long rest'); } }}
                  disabled={!combat.currentCombatant}
                >
                  <Moon className="w-4 h-4 mr-2" />{t.longRest || 'Long Rest'} ({t.currentTurn || 'Turno attivo'})
                </DropdownMenuItem>
                {isMaster && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => { combat.shortRest(); toast.success(t.shortRestAllDone || 'Short rest (tutti)'); }}>
                      <Heart className="w-4 h-4 mr-2" />{t.shortRestAll || 'Short Rest (tutti)'}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => { combat.longRest(); toast.success(t.longRestAllDone || 'Long rest (tutti)'); }}>
                      <Moon className="w-4 h-4 mr-2" />{t.longRestAll || 'Long Rest (tutti)'}
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onExport}><Download className="w-4 h-4 mr-2" />{t.exportJson}</DropdownMenuItem>
                <DropdownMenuItem onClick={onImport}><Upload className="w-4 h-4 mr-2" />{t.importJson}</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-destructive" onClick={() => setConfirmReset(true)}><RotateCcw className="w-4 h-4 mr-2" />{t.reset}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <CombatantModal isOpen={isModalOpen} onClose={() => { setIsModalOpen(false); setEditingCombatant(null); setIsMasterAdding(false); }} onSave={handleAddCombatant} editingCombatant={editingCombatant} t={t} isNPC={isNPCModal} isMasterAdding={isMasterAdding} showSaveAsPreset={isMaster && !isPersonal && !editingCombatant} onSaveAsPreset={isMaster ? (data) => { addPreset(data); toast.success(t.npcPresetSaved || 'Preset salvato'); } : undefined} />
      
      <Dialog open={confirmReset} onOpenChange={() => setConfirmReset(false)}>
        <DialogContent className="max-w-sm"><DialogHeader><DialogTitle>{t.confirmReset}</DialogTitle></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setConfirmReset(false)}>{t.no}</Button><Button variant="destructive" onClick={() => { combat.resetCombat(); setConfirmReset(false); toast.success(t.combatReset); }}>{t.yes}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="max-w-sm"><DialogHeader><DialogTitle>{t.confirmDelete}</DialogTitle></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setDeleteTarget(null)}>{t.no}</Button><Button variant="destructive" onClick={() => { combat.deleteCombatant(deleteTarget); setDeleteTarget(null); toast.success(t.combatantDeleted); }}>{t.yes}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iniziativa rapida: scelta Per tutti / Solo NPC e Nemici (solo Master) */}
      <Dialog open={showQuickInitiativePrompt} onOpenChange={setShowQuickInitiativePrompt}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t.quickInitiative || 'Iniziativa rapida'}</DialogTitle>
            <DialogDescription>
              {t.quickInitiativePromptDesc || 'Tira l\'iniziativa per:'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 py-2">
            <Button
              variant="default"
              className="w-full justify-start"
              onClick={() => {
                combat.rollAllInitiative();
                setShowQuickInitiativePrompt(false);
                toast.success(t.initiativeRolled || 'Iniziativa tirata');
              }}
            >
              {t.quickInitiativeForAll || 'Per tutti'}
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => {
                combat.rollInitiativeNpcAndEnemies();
                setShowQuickInitiativePrompt(false);
                toast.success(t.initiativeRolled || 'Iniziativa tirata');
              }}
            >
              {t.quickInitiativeNpcEnemiesOnly || 'Solo NPC e Nemici'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tiro salvezza concentrazione: dopo danno a un personaggio in concentrazione */}
      <Dialog open={!!concentrationCheckPending} onOpenChange={(open) => { if (!open) setConcentrationCheckPending(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-center">{t.concentrationCheckTitle || 'Tiro salvezza concentrazione'}</DialogTitle>
            <DialogDescription className="text-center whitespace-pre-line">
              {t.concentrationCheckDesc || 'Tira un d20 su Costituzione.\n10+: Successo. 10-: Fallimento.'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 pt-2">
            <Button
              variant="default"
              className="w-full"
              onClick={() => {
                if (!concentrationCheckPending) return;
                combat.modifyHp(concentrationCheckPending.combatantId, concentrationCheckPending.amount);
                setConcentrationCheckPending(null);
                toast.success(t.concentrationSuccess || 'Concentrazione mantenuta');
              }}
            >
              {t.concentrationSuccessBtn || 'Successo'}
            </Button>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                if (!concentrationCheckPending) return;
                combat.modifyHp(concentrationCheckPending.combatantId, concentrationCheckPending.amount);
                combat.updateCombatant(concentrationCheckPending.combatantId, { isConcentrating: false });
                setConcentrationCheckPending(null);
                toast.info(t.concentrationLost || 'Concentrazione persa');
              }}
            >
              {t.concentrationFailureBtn || 'Fallimento'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Nuovo preset NPC (solo Master) */}
      <Dialog open={showPresetModal} onOpenChange={setShowPresetModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t.npcPresetNew || 'Nuovo preset NPC'}</DialogTitle>
            <DialogDescription className="sr-only">Crea un preset da riutilizzare in battaglia</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-sm font-medium mb-1.5 block">{t.name}</label>
              <Input value={presetForm.name} onChange={(e) => setPresetForm(prev => ({ ...prev, name: e.target.value }))} placeholder={getRandomNamePlaceholder(t)} className="placeholder:text-muted-foreground" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1.5 block">{t.maxHp}</label>
                <Input type="number" value={presetForm.maxHp} onChange={(e) => setPresetForm(prev => ({ ...prev, maxHp: e.target.value === '' ? '' : parseInt(e.target.value, 10) || 10 }))} min="1" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">CA ({t.optional})</label>
                <Input type="number" value={presetForm.ac} onChange={(e) => setPresetForm(prev => ({ ...prev, ac: e.target.value }))} placeholder="--" min="1" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">{t.initiativeBonus || 'Bonus iniziativa'}</label>
              <Input type="number" value={presetForm.initiativeBonus} onChange={(e) => setPresetForm(prev => ({ ...prev, initiativeBonus: e.target.value === '' ? 0 : parseInt(e.target.value, 10) || 0 }))} placeholder="0" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">{t.combatantType || 'Tipo'}</label>
              <div className="flex gap-2">
                <Button type="button" variant={presetForm.combatantType === 'npc' ? 'default' : 'outline'} size="sm" onClick={() => setPresetForm(prev => ({ ...prev, combatantType: 'npc' }))}>{t.npcAlly || 'NPC'}</Button>
                <Button type="button" variant={presetForm.combatantType === 'enemy' ? 'default' : 'outline'} size="sm" onClick={() => setPresetForm(prev => ({ ...prev, combatantType: 'enemy' }))}>{t.enemyType || 'Nemico'}</Button>
                <Button type="button" variant={presetForm.combatantType === 'companion' ? 'default' : 'outline'} size="sm" onClick={() => setPresetForm(prev => ({ ...prev, combatantType: 'companion' }))}>{t.companion || 'Compagno'}</Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPresetModal(false)}>{t.cancel || 'Annulla'}</Button>
            <Button onClick={() => {
              if (!presetForm.name.trim()) return;
              const maxHp = parseInt(presetForm.maxHp) || 10;
              const ac = presetForm.ac !== '' && presetForm.ac != null ? parseInt(presetForm.ac, 10) : null;
              addPreset({ name: presetForm.name.trim(), maxHp, ac, initiativeBonus: parseInt(presetForm.initiativeBonus, 10) || 0, combatantType: presetForm.combatantType || 'npc', speed: 30 });
              setShowPresetModal(false);
              toast.success(t.npcPresetSaved || 'Preset salvato');
            }} disabled={!presetForm.name.trim()}>
              {t.save || 'Salva'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Event History Dialog */}
      <Dialog open={showEventHistory} onOpenChange={setShowEventHistory}>
        <DialogContent className="max-w-md max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScrollText className="w-5 h-5" />
              {t.eventHistory || 'Cronologia Eventi'}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[50vh] space-y-2">
            {eventHistory.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">{t.noEvents || 'Nessun evento registrato'}</p>
            ) : (
              eventHistory.map(event => (
                <div key={event.id} className="flex items-start gap-3 p-2 rounded-lg bg-muted/50 text-sm">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{event.timestamp}</span>
                  <Badge variant="outline" className="text-xs">R{event.round}</Badge>
                  <span className="flex-1">
                    {event.type === 'turn' && (
                      <span><Zap className="w-3 h-3 inline mr-1 accent-text" />{t.turnOf || 'Turno di'} <strong>{event.name}</strong></span>
                    )}
                    {event.type === 'damage' && (
                      <span><Sword className="w-3 h-3 inline mr-1 text-red-500" /><strong>{event.name}</strong> {t.tookDamage || 'ha subito'} <span className="text-red-500 font-bold">{event.damage}</span> {t.damageText || 'danni'}</span>
                    )}
                    {event.type === 'heal' && (
                      <span><Heart className="w-3 h-3 inline mr-1 text-green-500" /><strong>{event.name}</strong> {t.wasHealed || 'è stato curato di'} <span className="text-green-500 font-bold">{event.amount}</span> {t.hpText || 'PF'}</span>
                    )}
                    {event.type === 'shortRest' && (
                      <span><Heart className="w-3 h-3 inline mr-1 accent-text" /><strong>{event.name}</strong> {t.shortRestLog || 'ha fatto riposo breve'}: {event.diceNotation}{event.rolls ? ` (${event.rolls.join('+')})` : ''} = <span className="text-green-500 font-bold">+{event.amount}</span> {t.hpText || 'PF'}</span>
                    )}
                    {event.type === 'longRest' && (
                      <span><Moon className="w-3 h-3 inline mr-1 text-indigo-500" /><strong>{event.name}</strong> {t.longRestLog || 'ha fatto riposo lungo'}</span>
                    )}
                    {event.type === 'dot' && (
                      <span><Flame className="w-3 h-3 inline mr-1 text-orange-500" /><strong>{event.name}</strong> {t.tookDamage || 'ha subito'} <span className="text-orange-500 font-bold">{event.damage}</span> {t.dotLabel ?? 'danno per turno'}</span>
                    )}
                    {event.type === 'regen' && (
                      <span><Heart className="w-3 h-3 inline mr-1 text-green-500" /><strong>{event.name}</strong> {t.wasHealed || 'curato'} <span className="text-green-500 font-bold">+{event.amount}</span> {t.hpText || 'PF'} ({t.statusDictionary?.['Rigenerazione']?.name || 'Rigenerazione'})</span>
                    )}
                    {event.type === 'bleed' && (
                      <span><Flame className="w-3 h-3 inline mr-1 text-red-500" /><strong>{event.name}</strong> {t.tookDamage || 'ha subito'} <span className="text-red-500 font-bold">{event.damage}</span> {t.damageText || 'danni'} ({t.statusDictionary?.['Sanguinante']?.name || 'Sanguinante'})</span>
                    )}
                    {event.type === 'tempHp' && (
                      <span><Shield className="w-3 h-3 inline mr-1 text-cyan-500" /><strong>{event.name}</strong> {t.tempHpGained || 'ha ottenuto'} <span className="text-cyan-500 font-bold">{event.amount}</span> {t.tempHpText || 'PF temporanei'}</span>
                    )}
                    {event.type === 'statusGained' && (
                      <span>
                        <AlertCircle className="w-3 h-3 inline mr-1 text-purple-500" />
                        <strong>{event.name}</strong> {t.gainedStatus || 'ha ottenuto'}{' '}
                        <Badge variant="secondary" className="text-xs">
                          {(t.statusDictionary && t.statusDictionary[event.status]?.name) || event.status}
                        </Badge>
                      </span>
                    )}
                    {event.type === 'statusLost' && (
                      <span>
                        <CheckCircle className="w-3 h-3 inline mr-1 text-blue-500" />
                        <strong>{event.name}</strong> {t.lostStatus || 'ha perso'}{' '}
                        <Badge variant="outline" className="text-xs">
                          {(t.statusDictionary && t.statusDictionary[event.status]?.name) || event.status}
                        </Badge>
                      </span>
                    )}
                    {event.type === 'statusExpired' && (
                      <span>
                        <CheckCircle className="w-3 h-3 inline mr-1 text-blue-500" />
                        <strong>{event.name}</strong> {t.lostStatus || 'ha perso'}{' '}
                        <Badge variant="outline" className="text-xs">
                          {(t.statusDictionary && t.statusDictionary[event.status]?.name) || event.status}
                        </Badge>
                      </span>
                    )}
                    {event.type === 'added' && (
                      <span><UserPlus className="w-3 h-3 inline mr-1 text-green-500" /><strong>{event.name}</strong> {t.wasAdded || 'è entrato in combattimento'}</span>
                    )}
                    {event.type === 'removed' && (
                      <span><UserMinus className="w-3 h-3 inline mr-1 text-red-500" /><strong>{event.name}</strong> {t.wasRemoved || 'è stato rimosso'}</span>
                    )}
                    {event.type === 'ko' && (
                      <span><Skull className="w-3 h-3 inline mr-1 text-red-600" /><strong>{event.name}</strong> {t.koText || 'è andato KO'}</span>
                    )}
                    {event.type === 'revived' && (
                      <span><Heart className="w-3 h-3 inline mr-1 text-pink-500" /><strong>{event.name}</strong> {t.revivedText || 'è stato rianimato'}</span>
                    )}
                    {event.type === 'deathSave' && (
                      <span>
                        {event.saveType === 'success' ? (
                          <><Check className="w-3 h-3 inline mr-1 text-green-500" /><strong>{event.name}</strong> {t.deathSaveSuccess || 'ha superato un tiro salvezza morte'}</>
                        ) : (
                          <><X className="w-3 h-3 inline mr-1 text-red-500" /><strong>{event.name}</strong> {t.deathSaveFailure || 'ha fallito un tiro salvezza morte'}</>
                        )}
                      </span>
                    )}
                    {event.type === 'stabilized' && (
                      <span><Shield className="w-3 h-3 inline mr-1 text-cyan-500" /><strong>{event.name}</strong> {t.stabilizedText || 'si è stabilizzato'}</span>
                    )}
                    {event.type === 'death' && (
                      <span><Skull className="w-3 h-3 inline mr-1 text-gray-500" /><strong>{event.name}</strong> {t.diedText || 'è morto'}</span>
                    )}
                  </span>
                </div>
              ))
            )}
          </div>
          {eventHistory.length > 0 && (
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => { setEventHistory([]); toast.success(t.historyCleared || 'Cronologia cancellata'); }}>
                <Trash2 className="w-4 h-4 mr-2" />{t.clearHistory || 'Cancella cronologia'}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Picker personaggi salvati - solo in "Sono un player": aggiungi solo da Personaggi */}
      <Dialog open={showCharacterPicker} onOpenChange={setShowCharacterPicker}>
        <DialogContent className="max-w-md max-h-[85vh]">
          <DialogHeader>
            <DialogTitle>{t.addCharacter || 'Aggiungi personaggio'}</DialogTitle>
            <p className="text-sm text-muted-foreground">{t.addCharacterFromListDesc || 'Scegli un personaggio creato in Personaggi'}</p>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[60vh] space-y-2 pr-1">
            {savedCharacters?.map((char) => {
              const namesInBattle = combat.combatants.map(c => c.name);
              const isInBattle = namesInBattle.includes(char.name);
              return (
                <button
                  key={char.id}
                  type="button"
                  disabled={isInBattle}
                  onClick={() => { if (!isInBattle) { onAddSavedCharacter?.(char); setShowCharacterPicker(false); } }}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                    isInBattle ? 'border-border/30 bg-muted/30 opacity-70 cursor-not-allowed' : 'border-border/50 hover:accent-border-50 hover:accent-bg-10'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isInBattle ? 'bg-muted' : 'accent-bg-10'}`}>
                    <User className={`w-5 h-5 ${isInBattle ? 'text-muted-foreground' : 'accent-text'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold flex items-center gap-2 flex-wrap">
                      <span className="truncate">{char.name}</span>
                      {isInBattle && <span className="text-xs font-normal text-muted-foreground whitespace-nowrap">({t.alreadyInBattle || 'già in battaglia'})</span>}
                    </p>
                    {char.isFamiglio && char.famiglioDi && (
                      <p className="text-[11px] text-muted-foreground truncate">
                        {t.famiglioOf || 'Famiglio di'} {savedCharacters?.find(c => c.id === char.famiglioDi)?.name ?? '—'}
                      </p>
                    )}
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Heart className="w-3 h-3 text-red-500" />{char.maxHp}</span>
                      {char.ac != null && char.ac !== '' && <span className="flex items-center gap-1"><Shield className="w-3 h-3 text-blue-500" />{char.ac}</span>}
                      {char.baseInitiative != null && char.baseInitiative !== '' && <span className="flex items-center gap-1"><Zap className="w-3 h-3 accent-text" />{char.baseInitiative}</span>}
                    </div>
                  </div>
                  {!isInBattle && <Plus className="w-5 h-5 text-muted-foreground shrink-0" />}
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============ MASTER HUB (3 schede) ============

const MasterHubScreen = ({ onBack, onEnterBattle, onManageNpc, onSessionPrep, t, theme, toggleTheme, accent, setAccent, lang, setLang }) => {
  const title = t.masterHubTitle || 'Hub del Master';
  return (
    <div className="coco-v2-screen min-h-[100svh] sm:min-h-[100dvh] flex flex-col min-w-0">
      <Toaster theme={theme} />
      <header className="sticky top-0 z-50 glass coco-v2-header shrink-0">
        <div className="w-full max-w-lg sm:max-w-2xl lg:max-w-6xl xl:max-w-7xl mx-auto min-w-0 px-4 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between gap-2">
          <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={onBack} aria-label={t.back || 'Indietro'}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-full accent-bg flex items-center justify-center shrink-0">
              <Crown className="w-5 h-5 text-white" />
            </div>
            <h1 className="font-bold text-lg truncate accent-text">{title}</h1>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <LanguageDropdown lang={lang} setLang={setLang} t={t} />
            <AppearanceDropdown theme={theme} toggleTheme={toggleTheme} accent={accent} setAccent={setAccent} t={t} />
          </div>
        </div>
      </header>
      <main className="flex-1 w-full max-w-lg sm:max-w-2xl lg:max-w-6xl xl:max-w-7xl mx-auto min-w-0 px-4 sm:px-6 lg:px-8 py-6 pb-12">
        <div className="flex flex-col lg:flex-row lg:items-start gap-4 lg:gap-6 xl:gap-8">
          <aside className="w-full lg:w-[280px] xl:w-[320px] lg:shrink-0">
            <p className="text-sm text-muted-foreground">
              {t.masterHubDesc || 'Scegli come prepararti o entrare in battaglia.'}
            </p>
          </aside>
          <section className="flex-1 min-w-0">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">
          <Card
            className="cursor-pointer hover:accent-border-50 hover:accent-bg-10 active:scale-[0.99] transition-all border-2 accent-border-30 flex flex-col min-h-[140px] rounded-xl shadow-card bg-card"
            onClick={onEnterBattle}
          >
            <CardContent className="p-4 sm:p-5 flex flex-col flex-1 gap-3">
              <div className="w-11 h-11 rounded-xl accent-bg flex items-center justify-center shrink-0">
                <Swords className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="font-bold text-base accent-text">{t.enterBattle || 'Entra in battaglia'}</h2>
                <p className="text-sm text-muted-foreground mt-0.5">{t.enterBattleDesc || 'Lobby e combattimento in tempo reale'}</p>
              </div>
              <span className="text-xs font-medium accent-text flex items-center gap-1 mt-auto pt-2">
                <ChevronRight className="w-4 h-4" />
                {t.open || 'Apri'}
              </span>
            </CardContent>
          </Card>
          <Card
            className="cursor-pointer hover:accent-border-50 hover:accent-bg-10 active:scale-[0.99] transition-all border-2 accent-border-30 flex flex-col min-h-[140px] rounded-xl shadow-card bg-card"
            onClick={onManageNpc}
          >
            <CardContent className="p-4 sm:p-5 flex flex-col flex-1 gap-3">
              <div className="w-11 h-11 rounded-xl accent-bg-20 flex items-center justify-center shrink-0">
                <User className="w-6 h-6 accent-text" />
              </div>
              <div>
                <h2 className="font-bold text-base accent-text">{t.manageNpcEnemies || 'Gestisci NPC/Nemici'}</h2>
                <p className="text-sm text-muted-foreground mt-0.5">{t.manageNpcDesc || 'Crea e modifica preset di NPC, nemici e compagni: PF, CA, iniziativa e note per usarli rapidamente in battaglia.'}</p>
              </div>
              <span className="text-xs font-medium accent-text flex items-center gap-1 mt-auto pt-2">
                <ChevronRight className="w-4 h-4" />
                {t.open || 'Apri'}
              </span>
            </CardContent>
          </Card>
          <Card
            className="cursor-pointer hover:accent-border-50 hover:accent-bg-10 active:scale-[0.99] transition-all border-2 accent-border-30 flex flex-col min-h-[140px] rounded-xl shadow-card bg-card"
            onClick={onSessionPrep}
          >
            <CardContent className="p-4 sm:p-5 flex flex-col flex-1 gap-3">
              <div className="w-11 h-11 rounded-xl accent-bg-20 flex items-center justify-center shrink-0">
                <BookOpen className="w-6 h-6 accent-text" />
              </div>
              <div>
                <h2 className="font-bold text-base accent-text">{t.sessionPrep || 'Preparazione alla sessione'}</h2>
                <p className="text-sm text-muted-foreground mt-0.5">{t.sessionPrepDesc || 'Organizza sessioni con nome: appunti, timeline e luoghi con incontri, dialoghi, oggetti, personaggi e missioni per ogni luogo.'}</p>
              </div>
              <span className="text-xs font-medium accent-text flex items-center gap-1 mt-auto pt-2">
                <ChevronRight className="w-4 h-4" />
                {t.open || 'Apri'}
              </span>
            </CardContent>
          </Card>
        </div>
          </section>
        </div>
      </main>
    </div>
  );
};

// ============ MASTER LOBBY ============

// Lobby online: combat separato (storage 'master-lobby') così non si mescola con Master Offline
const MasterLobbyScreen = ({ onBack, t, theme, toggleTheme, accent, setAccent, lang, setLang }) => {
  const { peer, peerId, isConnected, error: peerError } = usePeer();
  const combat = useCombat('master-lobby');
  const onUpdateCombatantFromPlayer = useCallback((combatantId, updates) => {
    combat.updateCombatant(combatantId, updates);
  }, [combat]);
  const onModifyHpFromPlayer = useCallback((combatantId, amount) => {
    combat.modifyHp(combatantId, amount);
  }, [combat]);
  const onUpdateDeathSavesFromPlayer = useCallback((combatantId, type) => {
    combat.updateDeathSaves(combatantId, type);
  }, [combat]);
  const onResetDeathSavesFromPlayer = useCallback((combatantId) => {
    combat.resetDeathSaves(combatantId);
  }, [combat]);
  const { lobbyCode, players, combatState, createLobby, resumeLobby, broadcastState, closeLobby, getShareableLink, getFullLobbyCode, saveLobbyState } = useMasterLobby(peer, peerId, {
    onUpdateCombatantFromPlayer,
    onModifyHpFromPlayer,
    onUpdateDeathSavesFromPlayer,
    onResetDeathSavesFromPlayer,
  });
  const [view, setView] = useState('lobby'); // 'lobby' | 'combat'
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const fileInputRef = useRef(null);

  // Riprendi stato combattimento salvato nella lobby (solo da dnd-master-lobby)
  useEffect(() => {
    const saved = localStorage.getItem('dnd-master-lobby');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        if (data.lobbyCode && data.combatState && data.combatState.combatants) {
          combat.importData(JSON.stringify(data.combatState));
        }
      } catch (e) {
        console.error('Error loading master lobby combat:', e);
      }
    }
  }, [combat]);

  // Sync combat state to lobby - using ref to prevent infinite loops
  const prevStateRef = useRef(null);
  useEffect(() => {
    if (lobbyCode && combat.combatants.length > 0) {
      const state = {
        combatants: combat.combatants,
        currentTurnIndex: combat.currentTurnIndex,
        round: combat.round,
      };
      
      // Only broadcast if state actually changed
      const stateString = JSON.stringify(state);
      if (prevStateRef.current !== stateString) {
        prevStateRef.current = stateString;
        broadcastState(state);
      }
    }
  }, [combat.combatants, combat.currentTurnIndex, combat.round, lobbyCode, broadcastState]);

  const handleCreateLobby = () => {
    createLobby();
  };

  const handleResumeLobby = () => {
    const code = resumeLobby();
    if (code) {
      toast.success(t.lobbyResumed || 'Lobby ripresa');
    }
  };

  const handleCopyCode = () => {
    const fullCode = getFullLobbyCode();
    navigator.clipboard.writeText(fullCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success(t.codeCopied || 'Codice copiato');
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(getShareableLink());
    toast.success(t.linkCopied || 'Link copiato');
  };

  const handleCloseLobby = () => {
    closeLobby();
    combat.resetCombat();
    localStorage.removeItem('dnd-master-lobby'); // Ensure it's removed
    toast.success(t.lobbyClosed || 'Lobby chiusa');
  };

  // Add player's character to combat
  const addPlayerToCombat = (player) => {
    if (player.character) {
      combat.addCombatant({
        ...player.character,
        peerId: player.peerId,
        playerName: player.playerName,
      });
      toast.success(`${player.character.name} ${t.addedToCombat || 'aggiunto'}`);
    }
  };

  if (!isConnected) {
    return (
      <div className="coco-v2-screen min-h-[100svh] sm:min-h-[100dvh] flex items-center justify-center">
        <div className="text-center p-6">
          <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-primary" />
          <p className="text-muted-foreground">{t.connecting || 'Connessione in corso...'}</p>
          {peerError && <p className="text-destructive mt-2">{peerError}</p>}
        </div>
      </div>
    );
  }

  if (view === 'combat') {
    return (
      <CombatScreen 
        combat={combat}
        onBack={() => setView('lobby')}
        t={t}
        theme={theme}
        toggleTheme={toggleTheme}
        accent={accent}
        setAccent={setAccent}
        lang={lang}
        setLang={setLang}
        onExport={() => {
          const data = combat.exportData();
          const blob = new Blob([data], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `dnd-combat-${new Date().toISOString().slice(0,10)}.json`;
          a.click();
          toast.success(t.dataExported);
        }}
        onImport={() => fileInputRef.current?.click()}
        fileInputRef={fileInputRef}
        handleImport={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
              const success = combat.importData(event.target?.result);
              toast[success ? 'success' : 'error'](success ? t.dataImported : t.importError);
            };
            reader.readAsText(file);
          }
          e.target.value = '';
        }}
        isMaster={true}
        onGoHome={onBack}
        coinPouchAddOnly={true}
      />
    );
  }

  return (
    <div className="coco-v2-screen min-h-[100svh] sm:min-h-[100dvh]">
      <Toaster theme={theme} />
      
      <header className="sticky top-0 z-50 glass coco-v2-header">
        <div className="w-full max-w-lg sm:max-w-2xl lg:max-w-6xl xl:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={onBack}><ArrowLeft className="w-5 h-5" /></Button>
            <div className="flex items-center gap-2">
              <Crown className="w-5 h-5 accent-text" />
              <h1 className="font-bold text-lg">{t.masterMode || 'Modalità Master'}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <LanguageDropdown lang={lang} setLang={setLang} t={t} />
            <AppearanceDropdown theme={theme} toggleTheme={toggleTheme} accent={accent} setAccent={setAccent} t={t} />
          </div>
        </div>
      </header>

      <main className="w-full max-w-lg sm:max-w-2xl lg:max-w-6xl xl:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {!lobbyCode ? (
          <div className="space-y-6 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-6">
            <Card>
              <CardContent className="p-6 text-center">
                <Crown className="w-16 h-16 mx-auto mb-4 accent-text" />
                <h2 className="text-xl font-bold mb-4">{t.createOrResume || 'Crea o Riprendi Lobby'}</h2>
                <div className="flex flex-col gap-3">
                  <Button onClick={handleCreateLobby} className="w-full h-12">
                    <Plus className="w-5 h-5 mr-2" />{t.createNewLobby || 'Crea Nuova Lobby'}
                  </Button>
                  <Button variant="outline" onClick={handleResumeLobby} className="w-full h-12">
                    <RotateCcw className="w-5 h-5 mr-2" />{t.resumeLobby || 'Riprendi Lobby'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="space-y-6 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-6">
            {/* Lobby: codice breve + Link/QR per far entrare */}
            <Card>
              <CardContent className="p-5">
                <div className="text-center mb-3">
                  <p className="text-xs text-muted-foreground mb-1">{t.lobbyCode || 'Codice'}</p>
                  <div className="flex items-center justify-center gap-2">
                    <span className="font-mono text-3xl font-bold tracking-[0.3em]">{lobbyCode}</span>
                    <Button variant="ghost" size="icon" onClick={handleCopyCode} aria-label={t.copyLink || 'Copia'}>
                      {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">{t.lobbyShareHint || 'Condividi link o QR per far entrare i giocatori'}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={() => setShowQR(!showQR)} className="flex-1 min-w-[120px]">
                    <QrCode className="w-4 h-4 mr-1.5" />{showQR ? (t.hideQR || 'Nascondi QR') : (t.showQR || 'QR')}
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleCopyLink} className="flex-1 min-w-[120px]">
                    <LinkIcon className="w-4 h-4 mr-1.5" />{t.copyLink || 'Copia link'}
                  </Button>
                </div>
                {showQR && getShareableLink() && (
                  <div className="mt-3 p-3 bg-white dark:bg-muted rounded-lg flex justify-center">
                    <QRCodeSVG value={getShareableLink()} size={160} />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Players List */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  {t.connectedPlayers || 'Giocatori Connessi'} ({players.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {players.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">{t.waitingForPlayers || 'In attesa di giocatori...'}</p>
                ) : (
                  <div className="space-y-3">
                    {players.map(player => (
                      <div key={player.peerId} className="p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-medium">{player.playerName}</p>
                          {player.characters && player.characters.length > 0 && (
                            <Button size="sm" onClick={() => {
                              player.characters.forEach(char => {
                                combat.addCombatant({
                                  ...char,
                                  peerId: player.peerId,
                                  playerName: player.playerName,
                                });
                              });
                              toast.success(`${player.characters.length} ${t.charactersAdded || 'personaggi aggiunti'}`);
                            }}>
                              <Plus className="w-4 h-4 mr-1" />{t.addAll || 'Aggiungi tutti'}
                            </Button>
                          )}
                        </div>
                        {player.characters && player.characters.length > 0 ? (
                          <div className="space-y-1">
                            {player.characters.map((char, idx) => (
                              <div key={idx} className="flex items-center justify-between text-sm pl-2 border-l-2 border-primary/30">
                                <span>{char.name}</span>
                                <span className="text-muted-foreground">HP: {char.maxHp}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">{t.noCharacterSelected || 'Nessun personaggio selezionato'}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Combat Preview */}
            {combat.combatants.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2">
                    <Swords className="w-5 h-5" />
                    {t.combat || 'Combattimento'} - {t.round} {combat.round}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 mb-4">
                    {combat.combatants.slice(0, 3).map((c, i) => (
                      <div key={c.id} className={`flex items-center justify-between p-2 rounded ${i === combat.currentTurnIndex ? 'accent-bg-10 border accent-border-30' : 'bg-muted/50'}`}>
                        <span className="font-medium">{c.name}</span>
                        <span className="font-mono text-sm">{c.hp}/{c.maxHp}</span>
                      </div>
                    ))}
                    {combat.combatants.length > 3 && (
                      <p className="text-sm text-muted-foreground text-center">+{combat.combatants.length - 3} {t.more || 'altri'}</p>
                    )}
                  </div>
                  <Button className="w-full" onClick={() => setView('combat')}>
                    <Swords className="w-4 h-4 mr-2" />{t.goToCombat || 'Vai al Combattimento'}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <Button variant="destructive" className="flex-1" onClick={handleCloseLobby}>
                <X className="w-4 h-4 mr-2" />{t.closeLobby || 'Chiudi Lobby'}
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

// ============ JOIN LOBBY ============

const JoinLobbyScreen = ({ onBack, t, theme, toggleTheme, accent, setAccent, lang, setLang }) => {
  const { peer, peerId, isConnected, error: peerError } = usePeer();
  const { isConnected: lobbyConnected, combatState, error: lobbyError, joinLobby, sendUpdate, sendCombatUpdate, sendCombatModifyHp, sendDeathSavesUpdate, sendDeathSavesReset, leaveLobby } = usePlayerLobby(peer, peerId);
  const { characters } = useCharacters();
  const combat = useCombat('player');

  const [fullLobbyCode, setFullLobbyCode] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [selectedCharacters, setSelectedCharacters] = useState([]); // Array di personaggi selezionati
  const [step, setStep] = useState('code'); // 'code' | 'character' | 'lobby'
  const [playerView, setPlayerView] = useState('lobby'); // 'lobby' | 'combat' (when in lobby, show full battlefield or static lobby card)
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [qrScanError, setQrScanError] = useState('');
  const [qrCameraStarting, setQrCameraStarting] = useState(false);
  const [codeError, setCodeError] = useState('');
  const namePlaceholderRef = useRef(getRandomNamePlaceholder(t));
  const playerFileInputRef = useRef(null);
  const qrScannerRef = useRef(null);
  const [, setPlaceholderTick] = useState(0);

  // Sync combat state from master so player sees the same battlefield in real time
  useEffect(() => {
    if (combatState && Array.isArray(combatState.combatants)) {
      const payload = {
        combatants: combatState.combatants,
        currentTurnIndex: combatState.currentTurnIndex ?? 0,
        round: combatState.round ?? 1,
        notes: combatState.notes ?? '',
      };
      combat.importData(JSON.stringify(payload));
    }
  }, [combatState]);

  // Wrapper: modifiche player (HP, status, tiri salvezza) vanno al master e vengono ribroadcastate a tutti
  const playerCombat = useMemo(() => ({
    ...combat,
    modifyHp(id, amount) {
      combat.modifyHp(id, amount);
      sendCombatModifyHp(id, amount);
    },
    updateCombatant(id, updates) {
      combat.updateCombatant(id, updates);
      sendCombatUpdate(id, updates);
    },
    updateDeathSaves(id, type) {
      combat.updateDeathSaves(id, type);
      sendDeathSavesUpdate(id, type);
    },
    resetDeathSaves(id) {
      combat.resetDeathSaves(id);
      sendDeathSavesReset(id);
    },
  }), [combat, sendCombatUpdate, sendCombatModifyHp, sendDeathSavesUpdate, sendDeathSavesReset]);

  // When master starts combat, show full battlefield to player
  useEffect(() => {
    if (combatState?.combatants?.length > 0) setPlayerView('combat');
  }, [combatState?.combatants?.length]);

  // Extract lobby code from scanned QR (full URL with ?lobby=... or raw code)
  const extractLobbyFromScan = useCallback((text) => {
    if (!text || typeof text !== 'string') return '';
    const t = text.trim();
    try {
      if (t.startsWith('http://') || t.startsWith('https://')) {
        const url = new URL(t);
        const lobby = url.searchParams.get('lobby');
        return lobby || t;
      }
      return t;
    } catch {
      return t;
    }
  }, []);

  // QR scanner: start/stop camera when dialog opens/closes. Delay so Dialog content is mounted.
  useEffect(() => {
    if (!showQRScanner) return;
    setQrScanError('');
    setQrCameraStarting(true);
    const readerId = 'player-qr-reader';
    const timer = setTimeout(() => {
      const container = document.getElementById(readerId);
      if (!container) {
        setQrCameraStarting(false);
        return;
      }
      const html5QrCode = new Html5Qrcode(readerId);
      qrScannerRef.current = html5QrCode;
      Html5Qrcode.getCameras()
        .then((cameras) => {
          if (!cameras || cameras.length === 0) {
            setQrCameraStarting(false);
            setQrScanError(t.qrNoCamera || 'Nessuna fotocamera disponibile');
            return;
          }
          // Preferisci fotocamera posteriore (environment) per scansionare QR su mobile
          const backCamera = cameras.find((c) => /back|environment|posterior|rear/i.test(c.label || ''));
          const cameraIdOrConfig = backCamera ? backCamera.id : { facingMode: 'environment' };
          const config = {
            fps: 12,
            qrbox: (viewfinderWidth, viewfinderHeight) => {
              const size = Math.min(260, Math.min(viewfinderWidth, viewfinderHeight) * 0.85);
              return { width: size, height: size };
            },
            aspectRatio: 1,
            videoConstraints: { facingMode: 'environment' },
          };
          const onScanSuccess = (decodedText) => {
            const code = extractLobbyFromScan(decodedText);
            if (code) {
              setFullLobbyCode(code);
              setCodeError('');
              toast.success(t.qrCodeScanned || 'Codice letto!');
              setShowQRScanner(false);
            }
          };
          const startWithConfig = (idOrConfig, cfg) =>
            html5QrCode.start(idOrConfig, cfg, onScanSuccess, () => {});

          startWithConfig(cameraIdOrConfig, config)
            .then(() => setQrCameraStarting(false))
            .catch((err) => {
              console.warn('QR start with back camera failed, trying first camera:', err);
              const fallbackConfig = { fps: config.fps, qrbox: config.qrbox, aspectRatio: config.aspectRatio };
              startWithConfig(cameras[0].id, fallbackConfig)
                .then(() => setQrCameraStarting(false))
                .catch((fallbackErr) => {
                  console.error('QR start error:', fallbackErr);
                  setQrCameraStarting(false);
                  setQrScanError(t.qrCameraError || 'Impossibile avviare la fotocamera');
                });
            });
        })
        .catch((err) => {
          console.error('QR getCameras error:', err);
          setQrCameraStarting(false);
          setQrScanError(t.qrNoCamera || 'Nessuna fotocamera disponibile');
        });
    }, 150);
    return () => {
      clearTimeout(timer);
      if (qrScannerRef.current) {
        if (qrScannerRef.current.isScanning) {
          qrScannerRef.current.stop().catch(() => {}).finally(() => {
            if (qrScannerRef.current) {
              qrScannerRef.current.clear();
              qrScannerRef.current = null;
            }
          });
        } else {
          qrScannerRef.current.clear();
          qrScannerRef.current = null;
        }
      }
    };
  }, [showQRScanner, extractLobbyFromScan]);

  useEffect(() => {
    if (step === 'code') {
      namePlaceholderRef.current = getRandomNamePlaceholder(t);
      setPlaceholderTick(tick => tick + 1);
    }
  }, [step, t]);

  // Parse lobby code: "XXXX-encodedMasterId" (codice 4 char + id master)
  const parseLobbyCode = (code) => {
    const trimmed = (code || '').trim();
    const parts = trimmed.split('-');
    if (parts.length >= 2) {
      const lobbyCode = parts[0];
      const encodedMaster = parts.slice(1).join('-');
      const masterId = decodeMasterId(encodedMaster);
      if (masterId) return { lobbyCode, masterId, valid: true };
    }
    return { lobbyCode: trimmed, masterId: '', valid: false };
  };

  // Check URL params for direct join
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const lobbyParam = params.get('lobby');
    if (lobbyParam) {
      setFullLobbyCode(lobbyParam);
      // Clear URL params
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleJoin = () => {
    if (!fullLobbyCode || !playerName) return;
    
    const { lobbyCode, masterId, valid } = parseLobbyCode(fullLobbyCode);
    
    if (!valid || !masterId) {
      setCodeError(t.invalidCode || 'Codice lobby non valido');
      return;
    }
    
    setCodeError('');
    
    // Convert selected characters to combat format
    const charactersData = selectedCharacters.map(char => ({
      name: char.name,
      hp: char.maxHp,
      maxHp: char.maxHp,
      initiative: char.baseInitiative || 10,
      statuses: char.defaultStatuses?.map(s => ({ ...s, id: Math.random().toString(36).substr(2, 9), duration: null })) || [],
    }));
    
    joinLobby(lobbyCode, masterId, playerName, charactersData);
    setStep('lobby');
  };

  const toggleCharacterSelection = (char) => {
    setSelectedCharacters(prev => {
      const isSelected = prev.find(c => c.id === char.id);
      if (isSelected) {
        return prev.filter(c => c.id !== char.id);
      } else {
        return [...prev, char];
      }
    });
  };

  if (!isConnected) {
    return (
      <div className="coco-v2-screen min-h-[100svh] sm:min-h-[100dvh] flex items-center justify-center">
        <div className="text-center p-6">
          <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-primary" />
          <p className="text-muted-foreground">{t.connecting || 'Connessione in corso...'}</p>
          {peerError && <p className="text-destructive mt-2">{peerError}</p>}
        </div>
      </div>
    );
  }

  // Full battlefield in real time when master has started combat
  if (step === 'lobby' && lobbyConnected && playerView === 'combat' && combatState?.combatants?.length > 0) {
    return (
      <div className="coco-v2-screen min-h-[100svh] sm:min-h-[100dvh]">
        <Toaster theme={theme} />
        <CombatScreen
          combat={playerCombat}
          onBack={() => setPlayerView('lobby')}
          t={t}
          theme={theme}
          toggleTheme={toggleTheme}
          accent={accent}
          setAccent={setAccent}
          lang={lang}
          setLang={setLang}
          onExport={() => {}}
          onImport={() => playerFileInputRef.current?.click()}
          fileInputRef={playerFileInputRef}
          handleImport={() => {}}
          isMaster={false}
          isPersonal={false}
          currentUserPeerId={peerId}
          onGoHome={onBack}
        />
      </div>
    );
  }

  return (
    <div className="coco-v2-screen min-h-[100svh] sm:min-h-[100dvh] flex flex-col">
      <Toaster theme={theme} />
      
      <header className="sticky top-0 z-50 glass coco-v2-header shrink-0">
        <div className="max-w-2xl mx-auto px-4 h-14 sm:h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={step === 'code' ? onBack : () => { leaveLobby(); setStep('code'); }}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-bold text-lg">{t.joinLobby || 'Entra in Lobby'}</h1>
          </div>
          <div className="flex items-center gap-2">
            <LanguageDropdown lang={lang} setLang={setLang} t={t} />
            <AppearanceDropdown theme={theme} toggleTheme={toggleTheme} accent={accent} setAccent={setAccent} t={t} />
          </div>
        </div>
      </header>

      <main className="w-full max-w-lg sm:max-w-2xl lg:max-w-6xl xl:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 pb-8 overflow-y-auto flex-1 min-h-0">
        {lobbyError && (
          <Card className="mb-4 border-destructive">
            <CardContent className="p-4 text-destructive">{lobbyError}</CardContent>
          </Card>
        )}

        {step === 'code' && (
          <Card>
            <CardContent className="p-4 sm:p-6 space-y-4">
              <div className="text-center mb-2">
                <Users className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 text-green-500" />
                <h2 className="text-lg sm:text-xl font-bold">{t.enterLobbyCode || 'Inserisci Codice Lobby'}</h2>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">{t.pasteFullCode || 'Incolla il link o il codice ricevuto dal Master'}</p>
              </div>

              {/* QR in evidenza in alto, visibile subito su mobile */}
              <Button variant="default" className="w-full h-12 sm:h-11 text-base sm:text-sm font-medium" onClick={() => setShowQRScanner(true)} type="button">
                <QrCode className="w-5 h-5 sm:w-4 sm:h-4 mr-2 shrink-0" />
                {t.enterWithQRCode || 'Entra con QR code'}
              </Button>
              <p className="text-xs text-center text-muted-foreground -mt-2">{t.orEnterManually || 'Oppure inserisci il codice sotto'}</p>
              
              <div>
                <label className="text-sm font-medium mb-1.5 block">{t.yourName || 'Il tuo Nome'}</label>
                <Input value={playerName} onChange={(e) => setPlayerName(e.target.value)} placeholder={namePlaceholderRef.current} className="placeholder:text-muted-foreground" data-testid="player-name-input" />
              </div>
              
              <div>
                <label className="text-sm font-medium mb-1.5 block">{t.lobbyCode || 'Codice Lobby'}</label>
                <Input 
                  value={fullLobbyCode} 
                  onChange={(e) => { setFullLobbyCode(e.target.value); setCodeError(''); }} 
                  placeholder="Link o ABCD-xxx..." 
                  className="font-mono text-center tracking-wider" 
                  data-testid="lobby-code-input"
                />
                {codeError && <p className="text-sm text-destructive mt-1">{codeError}</p>}
              </div>
              
              <Button className="w-full h-12 sm:h-11" onClick={() => setStep('character')} disabled={!fullLobbyCode || !playerName} data-testid="continue-btn">
                {t.continue || 'Continua'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Dialog lettore QR integrato */}
        <Dialog open={showQRScanner} onOpenChange={(open) => { if (!open) setShowQRScanner(false); }}>
          <DialogContent className="sm:max-w-[340px] p-0 gap-0 overflow-hidden" onPointerDownOutside={(e) => e.preventDefault()}>
            <div className="p-4 pb-2">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <QrCode className="w-5 h-5" />
                  {t.scanQRCode || 'Scansiona il QR code'}
                </DialogTitle>
                <DialogDescription>
                  {t.scanQRCodeDesc || 'Inquadra il QR code mostrato dal Master per inserire automaticamente il codice lobby.'}
                </DialogDescription>
              </DialogHeader>
            </div>
            <div className="relative w-full bg-black/5 dark:bg-black/20 flex justify-center items-center min-h-[280px]">
              <div id="player-qr-reader" className="w-full max-w-[300px] aspect-square rounded-xl overflow-hidden [&>div]:!rounded-xl [&_video]:!rounded-xl" />
              {qrCameraStarting && !qrScanError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background/80 rounded-xl">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  <span className="text-sm text-muted-foreground">{t.qrStartingCamera || 'Avvio fotocamera...'}</span>
                </div>
              )}
            </div>
            {qrScanError && (
              <div className="px-4 py-2">
                <p className="text-sm text-destructive flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {qrScanError}
                </p>
              </div>
            )}
            <div className="p-4 pt-2 flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowQRScanner(false)}>
                {t.cancel || 'Annulla'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {step === 'character' && (
          <div className="space-y-4">
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-bold mb-2">{t.selectCharacters || 'Seleziona Personaggi'}</h2>
                <p className="text-sm text-muted-foreground mb-4">{t.selectMultiple || 'Puoi selezionare più personaggi'}</p>
                
                {characters.length > 0 ? (
                  <div className="space-y-2 mb-4">
                    {characters.map(char => {
                      const isSelected = selectedCharacters.find(c => c.id === char.id);
                      return (
                        <div 
                          key={char.id}
                          className={`p-3 rounded-lg cursor-pointer transition-colors flex items-center gap-3 ${isSelected ? 'bg-primary/10 border-2 border-primary' : 'bg-muted/50 hover:bg-muted border-2 border-transparent'}`}
                          onClick={() => toggleCharacterSelection(char)}
                        >
                          <Checkbox checked={!!isSelected} className="pointer-events-none" />
                          <div className="flex-1">
                            <span className="font-medium">{char.name}</span>
                            <span className="text-sm text-muted-foreground ml-2">HP: {char.maxHp}</span>
                          </div>
                          {isSelected && <Check className="w-5 h-5 text-primary" />}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-4">{t.noSavedCharacters || 'Nessun personaggio salvato'}</p>
                )}
                
                {selectedCharacters.length > 0 && (
                  <div className="mb-4 p-3 bg-primary/5 rounded-lg">
                    <p className="text-sm font-medium">{t.selectedCount || 'Selezionati'}: {selectedCharacters.length}</p>
                    <p className="text-xs text-muted-foreground">{selectedCharacters.map(c => c.name).join(', ')}</p>
                  </div>
                )}
                
                <Button variant="outline" className="w-full mb-4" onClick={() => setSelectedCharacters([])}>
                  {selectedCharacters.length > 0 ? (t.clearSelection || 'Deseleziona tutti') : (t.joinWithoutCharacter || 'Entra senza personaggio')}
                </Button>
                
                <Button className="w-full h-12" onClick={handleJoin} data-testid="join-lobby-btn">
                  <Users className="w-5 h-5 mr-2" />{t.joinLobby || 'Entra in Lobby'}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {step === 'lobby' && (
          <div className="space-y-4">
            <Card>
              <CardContent className="p-6 text-center">
                {lobbyConnected ? (
                  <>
                    <Check className="w-12 h-12 mx-auto mb-2 text-green-500" />
                    <h2 className="text-xl font-bold mb-2">{t.connected || 'Connesso!'}</h2>
                    <p className="text-muted-foreground">{t.waitingForMaster || 'In attesa del Master...'}</p>
                  </>
                ) : (
                  <>
                    <Loader2 className="w-12 h-12 mx-auto mb-2 animate-spin text-primary" />
                    <p className="text-muted-foreground">{t.connecting || 'Connessione in corso...'}</p>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Show selected characters */}
            {selectedCharacters.length > 0 && lobbyConnected && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">{t.yourCharacters || 'I tuoi Personaggi'}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {selectedCharacters.map((char, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                        <span className="font-medium">{char.name}</span>
                        <span className="text-sm text-muted-foreground">HP: {char.maxHp}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {combatState && combatState.combatants && combatState.combatants.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2">
                    <Swords className="w-5 h-5" />
                    {t.combat || 'Combattimento'} - {t.round} {combatState.round || 1}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Button className="w-full" onClick={() => setPlayerView('combat')}>
                    <Swords className="w-4 h-4 mr-2" />
                    {t.viewBattlefield || 'Vai al campo di battaglia'}
                  </Button>
                  <div className="space-y-2">
                    {combatState.combatants.map((c, i) => {
                      const isMyCharacter = selectedCharacters.find(char => char.name === c.name);
                      const showHpForThis = c.hpVisible !== false || isMyCharacter;
                      
                      return (
                        <div 
                          key={c.id} 
                          className={`p-3 rounded-lg border-l-4 ${
                            i === combatState.currentTurnIndex ? 'accent-bg-10 accent-border' : 
                            c.isCompanion || c.combatantType === 'companion' ? 'bg-green-500/5 border-green-500' :
                            c.isNPC ? (c.isEnemy ? 'bg-red-500/5 border-red-500' : 'bg-blue-500/5 border-blue-500') : 
                            'bg-muted/50 border-transparent'
                          } ${isMyCharacter ? 'ring-2 ring-primary/30' : ''}`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              {(c.isNPC || c.isCompanion || c.combatantType === 'companion') && (
                                <div className={`w-2 h-2 rounded-full ${
                                  c.isCompanion || c.combatantType === 'companion' ? 'bg-green-500' :
                                  c.isEnemy ? 'bg-red-500' : 'bg-blue-500'
                                }`} />
                              )}
                              {c.isKO && <Skull className="w-4 h-4 text-destructive" />}
                              <span className={`font-medium ${c.isKO ? 'line-through opacity-50' : ''}`}>{c.name}</span>
                              {isMyCharacter && <Badge variant="outline" className="text-xs">{t.you || 'Tu'}</Badge>}
                            </div>
                            {showHpForThis ? (
                              <div className="flex items-center gap-1">
                                <span className="font-mono text-sm">{c.hp}/{c.maxHp}</span>
                                {c.tempHp > 0 && <span className="font-mono text-xs text-cyan-500">+{c.tempHp}</span>}
                              </div>
                            ) : (
                              <EyeOff className="w-4 h-4 text-muted-foreground" />
                            )}
                          </div>
                          {showHpForThis && (
                            <HpBar current={c.hp} max={c.maxHp} tempHp={c.tempHp || 0} />
                          )}
                          {c.statuses && c.statuses.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {c.statuses.map(s => (
                                <Badge key={s.id} variant="outline" className={`${getStatusClass(s.name)} text-xs`}>
                                  {s.name}{s.duration && ` (${s.duration})`}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            <Button variant="outline" className="w-full" onClick={() => { leaveLobby(); setStep('code'); }}>
              <ArrowLeft className="w-4 h-4 mr-2" />{t.leaveLobby || 'Esci dalla Lobby'}
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

// ============ MASTER NPC / NEMICI ============

const SESSION_PREP_STORAGE = 'dnd-session-prep';

const MasterNpcScreen = ({ onBack, t, theme, toggleTheme, accent, setAccent, lang, setLang }) => {
  const { presets, addPreset, removePreset, updatePreset } = useNpcPresets();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPreset, setEditingPreset] = useState(null);
  const [form, setForm] = useState({ name: '', maxHp: 10, ac: '', initiativeBonus: 0, combatantType: 'enemy', speed: 30, notes: '' });
  const [deleteTarget, setDeleteTarget] = useState(null);

  const openCreate = () => {
    setEditingPreset(null);
    setForm({ name: '', maxHp: 10, ac: '', initiativeBonus: 0, combatantType: 'enemy', speed: 30, notes: '' });
    setModalOpen(true);
  };
  const openEdit = (p) => {
    setEditingPreset(p);
    setForm({
      name: p.name || '',
      maxHp: p.maxHp ?? 10,
      ac: p.ac != null && p.ac !== '' ? String(p.ac) : '',
      initiativeBonus: p.initiativeBonus ?? 0,
      combatantType: p.combatantType || 'enemy',
      speed: p.speed ?? 30,
      notes: p.notes || '',
    });
    setModalOpen(true);
  };

  const handleSave = () => {
    if (!form.name.trim()) return;
    const maxHp = parseInt(form.maxHp, 10) || 10;
    const ac = form.ac !== '' && form.ac != null ? parseInt(form.ac, 10) : null;
    const payload = { name: form.name.trim(), maxHp, ac, initiativeBonus: parseInt(form.initiativeBonus, 10) || 0, combatantType: form.combatantType || 'enemy', speed: parseInt(form.speed, 10) || 30, notes: form.notes?.trim() || '' };
    if (editingPreset) {
      updatePreset(editingPreset.id, payload);
      toast.success(t.saved || 'Salvato');
    } else {
      addPreset(payload);
      toast.success(t.npcPresetSaved || 'Preset salvato');
    }
    setModalOpen(false);
  };

  return (
    <div className="coco-v2-screen min-h-[100svh] sm:min-h-[100dvh] flex flex-col min-w-0">
      <Toaster theme={theme} />
      <header className="sticky top-0 z-50 glass coco-v2-header shrink-0">
        <div className="w-full max-w-lg sm:max-w-2xl lg:max-w-6xl xl:max-w-7xl mx-auto min-w-0 px-4 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between gap-2">
          <Button variant="ghost" size="icon" className="h-10 w-10 shrink-0" onClick={onBack} aria-label={t.back || 'Indietro'}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-bold text-lg">{t.manageNpcEnemies || 'Gestisci NPC/Nemici'}</h1>
          <div className="flex items-center gap-1 shrink-0">
            <LanguageDropdown lang={lang} setLang={setLang} t={t} />
            <AppearanceDropdown theme={theme} toggleTheme={toggleTheme} accent={accent} setAccent={setAccent} t={t} />
          </div>
        </div>
      </header>
      <main className="flex-1 w-full max-w-lg sm:max-w-2xl lg:max-w-6xl xl:max-w-7xl mx-auto min-w-0 px-4 sm:px-6 lg:px-8 py-6 pb-12">
        <div className="flex flex-col lg:flex-row lg:items-start gap-4 lg:gap-6 xl:gap-8">
          <aside className="w-full lg:w-[280px] xl:w-[320px] lg:shrink-0 space-y-4">
            <p className="text-sm text-muted-foreground">
              {t.manageNpcDescLong || 'Prepara i tuoi incontri in anticipo: crea preset per NPC alleati, nemici e compagni con nome, PF, CA, bonus iniziativa, velocità e note (tratti, abilità speciali). Ogni preset potrà essere aggiunto in un clic dalla barra in battaglia.'}
            </p>
            <Button className="w-full" onClick={openCreate}>
              <Plus className="w-4 h-4 mr-2" />
              {t.addPreset || 'Nuovo preset'}
            </Button>
          </aside>
          <section className="flex-1 min-w-0">
        {presets.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center text-muted-foreground">
              <User className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">{t.npcPresetsEmpty || 'Nessun preset ancora. Clicca "Nuovo preset" per creare il primo: potrai definire nome, PF, CA, bonus iniziativa, tipo (NPC / Nemico / Compagno) e note. I preset creati qui saranno disponibili in battaglia per aggiungere combattenti rapidamente.'}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3 lg:grid lg:grid-cols-2 lg:gap-3 lg:space-y-0">
            {presets.map((p) => (
              <Card key={p.id} className="overflow-hidden">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold">{p.name}</span>
                        <Badge variant={p.combatantType === 'enemy' ? 'destructive' : p.combatantType === 'companion' ? 'default' : 'secondary'} className="text-xs">
                          {p.combatantType === 'enemy' ? (t.enemyType || 'Nemico') : p.combatantType === 'companion' ? (t.companion || 'Compagno') : (t.npcAlly || 'NPC')}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground">
                        <span>{t.maxHp || 'PF'}: {p.maxHp}</span>
                        {p.ac != null && <span>CA: {p.ac}</span>}
                        <span>{t.initiativeBonus || 'Bonus init.'}: {p.initiativeBonus}</span>
                        <span>{t.speed || 'Velocità'}: {p.speed ?? 30}</span>
                      </div>
                      {p.notes && <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{p.notes}</p>}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0"><MoreVertical className="w-4 h-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(p)}><Edit2 className="w-4 h-4 mr-2" />{t.edit || 'Modifica'}</DropdownMenuItem>
                        <DropdownMenuItem className="text-destructive" onClick={() => setDeleteTarget(p)}><Trash2 className="w-4 h-4 mr-2" />{t.delete || 'Elimina'}</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
          </section>
        </div>
      </main>
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPreset ? (t.edit || 'Modifica') : (t.npcPresetNew || 'Nuovo preset NPC')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-sm font-medium mb-1.5 block">{t.name}</label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder={t.name} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1.5 block">{t.maxHp}</label>
                <Input type="number" value={form.maxHp} onChange={(e) => setForm((f) => ({ ...f, maxHp: e.target.value }))} min={1} />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">CA ({t.optional || 'opz.'})</label>
                <Input type="number" value={form.ac} onChange={(e) => setForm((f) => ({ ...f, ac: e.target.value }))} placeholder="--" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1.5 block">{t.initiativeBonus || 'Bonus iniziativa'}</label>
                <Input type="number" value={form.initiativeBonus} onChange={(e) => setForm((f) => ({ ...f, initiativeBonus: e.target.value }))} />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">{t.speed || 'Velocità'}</label>
                <Input type="number" value={form.speed} onChange={(e) => setForm((f) => ({ ...f, speed: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">{t.combatantType || 'Tipo'}</label>
              <div className="flex gap-2 flex-wrap">
                <Button type="button" variant={form.combatantType === 'npc' ? 'default' : 'outline'} size="sm" onClick={() => setForm((f) => ({ ...f, combatantType: 'npc' }))}>{t.npcAlly || 'NPC'}</Button>
                <Button type="button" variant={form.combatantType === 'enemy' ? 'default' : 'outline'} size="sm" onClick={() => setForm((f) => ({ ...f, combatantType: 'enemy' }))}>{t.enemyType || 'Nemico'}</Button>
                <Button type="button" variant={form.combatantType === 'companion' ? 'default' : 'outline'} size="sm" onClick={() => setForm((f) => ({ ...f, combatantType: 'companion' }))}>{t.companion || 'Compagno'}</Button>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">{t.notes || 'Note'}</label>
              <Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder={t.notesPlaceholder || 'Tratti, abilità, descrizione...'} className="min-h-[80px]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>{t.cancel || 'Annulla'}</Button>
            <Button onClick={handleSave} disabled={!form.name.trim()}>{t.save || 'Salva'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.delete || 'Elimina'}</DialogTitle>
            <DialogDescription>{deleteTarget ? (t.deletePresetConfirm || 'Eliminare questo preset?') : ''}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>{t.cancel || 'Annulla'}</Button>
            <Button variant="destructive" onClick={() => { if (deleteTarget) { removePreset(deleteTarget.id); setDeleteTarget(null); toast.success(t.deleted || 'Eliminato'); } }}>{t.delete || 'Elimina'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============ PREPARAZIONE SESSIONE ============

const SESSION_PREP_LIST_KEY = 'dnd-session-prep-list';

const defaultPlace = () => ({ id: Math.random().toString(36).substr(2, 9), name: '', dialoghi: [], oggetti: [], personaggi: [], missioni: [], incontri: [] });

const defaultPrepSections = () => ({
  appunti: [],
  timeline: [],
  luoghi: [], // [{ id, name, dialoghi: [], oggetti: [], personaggi: [], missioni: [], incontri: [] }]
});

/** Normalizza dati sessione: root dialoghi/oggetti/personaggi → luogo "Generale"; luoghi in formato nuovo */
const normalizeSessionData = (data) => {
  const base = defaultPrepSections();
  const merged = { ...base, ...data };
  let luoghi = Array.isArray(merged.luoghi) ? merged.luoghi : [];
  if (merged.dialoghi?.length || merged.oggetti?.length || merged.personaggi?.length || merged.missioni?.length || merged.incontri?.length) {
    luoghi = [{ ...defaultPlace(), name: 'Generale', dialoghi: merged.dialoghi || [], oggetti: merged.oggetti || [], personaggi: merged.personaggi || [], missioni: merged.missioni || [], incontri: merged.incontri || [] }, ...luoghi];
  }
  luoghi = luoghi.map((p) => (typeof p === 'object' && Array.isArray(p?.dialoghi) ? { ...defaultPlace(), ...p, missioni: p.missioni ?? [], incontri: p.incontri ?? [] } : { ...defaultPlace(), name: typeof p === 'object' && p?.name != null ? p.name : (typeof p === 'object' && p?.text != null ? p.text : String(p || '')), dialoghi: p?.dialoghi || [], oggetti: p?.oggetti || [], personaggi: p?.personaggi || [], missioni: p?.missioni || [], incontri: p?.incontri || [] }));
  return { ...base, appunti: merged.appunti || [], timeline: merged.timeline || [], luoghi };
};

const loadSavedSessions = () => {
  try {
    const raw = localStorage.getItem(SESSION_PREP_LIST_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const saveSessionToList = (sessions, session) => {
  const updated = session.id
    ? sessions.map((s) => (s.id === session.id ? { ...session, savedAt: new Date().toISOString() } : s))
    : [...sessions, { ...session, id: Math.random().toString(36).substr(2, 9), savedAt: new Date().toISOString() }];
  try {
    localStorage.setItem(SESSION_PREP_LIST_KEY, JSON.stringify(updated));
  } catch (_) {}
  return updated;
};

const deleteSessionFromList = (sessions, id) => {
  const updated = sessions.filter((s) => s.id !== id);
  try {
    localStorage.setItem(SESSION_PREP_LIST_KEY, JSON.stringify(updated));
  } catch (_) {}
  return updated;
};

/** Migra vecchio blob singolo (se esiste) in una sessione salvata */
const migrateOldPrep = () => {
  try {
    const raw = localStorage.getItem(SESSION_PREP_STORAGE);
    if (!raw) return;
    const data = { ...defaultPrepSections(), ...JSON.parse(raw) };
    const list = loadSavedSessions();
    if (list.length === 0) {
      saveSessionToList([], { id: null, name: 'Sessione migrata', data });
      localStorage.removeItem(SESSION_PREP_STORAGE);
    }
  } catch (_) {}
};

const SectionEditor = ({ title, icon: Icon, items, onAdd, onEdit, onDelete, onUpdateItem, t, placeholder, defaultExpanded = false, compact = false }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [newText, setNewText] = useState('');
  const [asChecklist, setAsChecklist] = useState(false);
  const [checklistInputs, setChecklistInputs] = useState({}); // index -> input value for adding sub-item
  const add = () => {
    const v = newText.trim();
    if (v) { onAdd(v, asChecklist); setNewText(''); }
  };
  const addChecklistSub = (itemIndex, text) => {
    const item = items[itemIndex];
    if (!item || !item.checklist) return;
    const sub = { id: Math.random().toString(36).substr(2, 9), text: text.trim(), checked: false };
    onUpdateItem(itemIndex, { ...item, items: [...(item.items || []), sub] });
    setChecklistInputs((prev) => ({ ...prev, [itemIndex]: '' }));
  };
  const toggleChecklistSub = (itemIndex, subIndex) => {
    const item = items[itemIndex];
    if (!item?.checklist || !item.items) return;
    const itemsCopy = item.items.map((s, j) => j === subIndex ? { ...s, checked: !s.checked } : s);
    onUpdateItem(itemIndex, { ...item, items: itemsCopy });
  };
  const deleteChecklistSub = (itemIndex, subIndex) => {
    const item = items[itemIndex];
    if (!item?.checklist || !item.items) return;
    const itemsCopy = item.items.filter((_, j) => j !== subIndex);
    onUpdateItem(itemIndex, { ...item, items: itemsCopy });
  };
  return (
    <Card className={compact ? 'border-border/60' : ''}>
      <CardHeader className={compact ? 'py-2 px-3' : 'pb-2'}>
        <Button variant="ghost" className={`w-full justify-start gap-2 h-auto p-0 hover:bg-transparent ${compact ? 'py-1' : ''}`} onClick={() => setExpanded((e) => !e)}>
          {Icon && <Icon className={`text-muted-foreground shrink-0 ${compact ? 'w-4 h-4' : 'w-5 h-5'}`} />}
          <CardTitle className={compact ? 'text-sm' : 'text-base'}>{title}</CardTitle>
          <ChevronDown className={`w-4 h-4 ml-auto shrink-0 transition-transform ${expanded ? '' : '-rotate-90'}`} />
        </Button>
      </CardHeader>
      {expanded && (
        <CardContent className={compact ? 'pt-0 px-3 pb-3 space-y-2' : 'pt-0 space-y-3'}>
          <div className={`flex flex-wrap gap-2 items-center ${compact ? 'gap-1.5' : ''}`}>
            <Input placeholder={placeholder} value={newText} onChange={(e) => setNewText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} className={`flex-1 min-w-[120px] ${compact ? 'h-8 text-sm' : ''}`} />
            <label className={`flex items-center gap-1.5 text-muted-foreground cursor-pointer whitespace-nowrap ${compact ? 'text-xs' : 'text-sm'}`}>
              <Checkbox checked={asChecklist} onCheckedChange={(c) => setAsChecklist(!!c)} className={compact ? 'scale-90' : ''} />
              {t.checklist || 'Checklist'}
            </label>
            <Button size="sm" onClick={add} disabled={!newText.trim()} className={compact ? 'h-8 w-8 p-0' : ''}><Plus className="w-4 h-4" /></Button>
          </div>
          <ul className={compact ? 'space-y-1' : 'space-y-2'}>
            {items.length === 0 ? (
              <li className="text-sm text-muted-foreground py-2">{t.empty || 'Vuoto'}</li>
            ) : (
              items.map((item, i) => {
                const isChecklist = typeof item === 'object' && item?.checklist;
                const text = typeof item === 'object' && item?.text != null ? item.text : item;
                const subItems = isChecklist ? (item.items || []) : [];
                const subInputVal = checklistInputs[i] ?? '';
                return (
                  <li key={item.id} className={`rounded-md hover:bg-muted/50 group ${compact ? 'py-1 px-1.5' : 'py-1.5 px-2'}`}>
                    {isChecklist ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <span className={`font-medium flex-1 min-w-0 break-words ${compact ? 'text-xs' : 'text-sm'}`}>{text}</span>
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100">
                            <Button variant="ghost" size="icon" className={compact ? 'h-6 w-6' : 'h-7 w-7'} onClick={() => onEdit(i, item)}><Edit2 className="w-3.5 h-3.5" /></Button>
                            <Button variant="ghost" size="icon" className={`text-destructive ${compact ? 'h-6 w-6' : 'h-7 w-7'}`} onClick={() => onDelete(i)}><Trash2 className="w-3.5 h-3.5" /></Button>
                          </div>
                        </div>
                        <ul className={`pl-4 border-l-2 border-border/50 ml-1 ${compact ? 'space-y-1' : 'space-y-1.5'}`}>
                          {subItems.map((sub, j) => (
                            <li key={sub.id} className="flex items-center gap-2 text-sm">
                              <Checkbox checked={!!sub.checked} onCheckedChange={() => toggleChecklistSub(i, j)} className="shrink-0" />
                              <span className={`flex-1 min-w-0 break-words ${sub.checked ? 'line-through text-muted-foreground' : ''}`}>{sub.text}</span>
                              <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive shrink-0 opacity-0 group-hover:opacity-100" onClick={() => deleteChecklistSub(i, j)}><X className="w-3 h-3" /></Button>
                            </li>
                          ))}
                          <li className="flex gap-2 items-center pl-0">
                            <Input placeholder={t.addChecklistItem || 'Aggiungi voce...'} value={subInputVal} onChange={(e) => setChecklistInputs((p) => ({ ...p, [i]: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && (addChecklistSub(i, subInputVal), e.preventDefault())} className="h-8 text-sm flex-1 min-w-0" />
                            <Button size="sm" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => addChecklistSub(i, subInputVal)} disabled={!subInputVal.trim()}><Plus className="w-3.5 h-3.5" /></Button>
                          </li>
                        </ul>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between gap-2">
                        <span className={`flex-1 min-w-0 break-words ${compact ? 'text-xs' : 'text-sm'}`}>{text}</span>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100">
                          <Button variant="ghost" size="icon" className={compact ? 'h-6 w-6' : 'h-7 w-7'} onClick={() => onEdit(i, item)}><Edit2 className="w-3.5 h-3.5" /></Button>
                          <Button variant="ghost" size="icon" className={`text-destructive ${compact ? 'h-6 w-6' : 'h-7 w-7'}`} onClick={() => onDelete(i)}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })
            )}
          </ul>
        </CardContent>
      )}
    </Card>
  );
};

/** Sottocategorie di un luogo: Dialoghi, Oggetti, Personaggi */
const PlaceSubSection = ({ title, icon: Icon, items, onAdd, onEdit, onDelete, onUpdateItem, t, placeholder }) => (
  <SectionEditor title={title} icon={Icon} items={items} onAdd={onAdd} onEdit={onEdit} onDelete={onDelete} onUpdateItem={onUpdateItem} t={t} placeholder={placeholder} defaultExpanded={false} compact />
);

/** Card di un luogo con sottocategorie Dialoghi, Oggetti, Personaggi */
const PlaceCard = ({ place, onUpdate, onDelete, onEditSubItem, t }) => {
  const [expanded, setExpanded] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameVal, setNameVal] = useState(place.name);
  const update = (updates) => onUpdate({ ...place, ...updates });
  const updateSub = (key) => (index, updatedItem) => {
    const arr = [...(place[key] || [])];
    arr[index] = updatedItem;
    update({ [key]: arr });
  };
  const addSub = (key) => (text, isChecklist = false) => {
    const id = Math.random().toString(36).substr(2, 9);
    const item = isChecklist ? { id, text: text.trim(), checklist: true, items: [] } : { id, text: text.trim() };
    update({ [key]: [...(place[key] || []), item] });
  };
  const deleteSub = (key) => (index) => update({ [key]: (place[key] || []).filter((_, i) => i !== index) });
  const displayName = (place.name || '').trim() || (t.unnamedPlace || 'Senza nome');
  return (
    <Card className="border-border/60 overflow-hidden">
      <CardHeader className="py-2 px-3">
        <div className="flex items-center gap-2 w-full">
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => setExpanded((e) => !e)}>
            <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? '' : '-rotate-90'}`} />
          </Button>
          {editingName ? (
            <Input value={nameVal} onChange={(e) => setNameVal(e.target.value)} onBlur={() => { update({ name: nameVal.trim() || displayName }); setEditingName(false); }} onKeyDown={(e) => e.key === 'Enter' && (update({ name: nameVal.trim() || displayName }), setEditingName(false))} className="h-8 text-sm flex-1" autoFocus />
          ) : (
            <button type="button" onClick={() => { setEditingName(true); setNameVal(place.name || ''); }} className="flex-1 min-w-0 text-left font-medium text-sm truncate">
              <MapPin className="w-4 h-4 inline mr-1.5 text-muted-foreground align-middle" />
              {displayName}
            </button>
          )}
          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive shrink-0" onClick={() => onDelete()}><Trash2 className="w-4 h-4" /></Button>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="pt-0 px-3 pb-3 space-y-3 border-t border-border/50">
          <PlaceSubSection title={t.sessionPrepDialogues || 'Dialoghi'} icon={MessageSquare} items={place.dialoghi || []} onAdd={addSub('dialoghi')} onEdit={(i, item) => onEditSubItem(place.id, 'dialoghi', i, typeof item?.text === 'string' ? item.text : item)} onDelete={deleteSub('dialoghi')} onUpdateItem={updateSub('dialoghi')} t={t} placeholder={t.addDialogue || 'Aggiungi dialogo...'} />
          <PlaceSubSection title={t.sessionPrepEncounters || 'Incontri'} icon={Swords} items={place.incontri || []} onAdd={addSub('incontri')} onEdit={(i, item) => onEditSubItem(place.id, 'incontri', i, typeof item?.text === 'string' ? item.text : item)} onDelete={deleteSub('incontri')} onUpdateItem={updateSub('incontri')} t={t} placeholder={t.addEncounter || 'Aggiungi incontro...'} />
          <PlaceSubSection title={t.sessionPrepObjects || 'Oggetti'} icon={Package} items={place.oggetti || []} onAdd={addSub('oggetti')} onEdit={(i, item) => onEditSubItem(place.id, 'oggetti', i, typeof item?.text === 'string' ? item.text : item)} onDelete={deleteSub('oggetti')} onUpdateItem={updateSub('oggetti')} t={t} placeholder={t.addObject || 'Aggiungi oggetto...'} />
          <PlaceSubSection title={t.sessionPrepCharacters || 'Personaggi'} icon={User} items={place.personaggi || []} onAdd={addSub('personaggi')} onEdit={(i, item) => onEditSubItem(place.id, 'personaggi', i, typeof item?.text === 'string' ? item.text : item)} onDelete={deleteSub('personaggi')} onUpdateItem={updateSub('personaggi')} t={t} placeholder={t.addCharacter || 'Aggiungi personaggio...'} />
          <PlaceSubSection title={t.sessionPrepMissions || 'Missioni'} icon={ListChecks} items={place.missioni || []} onAdd={addSub('missioni')} onEdit={(i, item) => onEditSubItem(place.id, 'missioni', i, typeof item?.text === 'string' ? item.text : item)} onDelete={deleteSub('missioni')} onUpdateItem={updateSub('missioni')} t={t} placeholder={t.addMission || 'Aggiungi missione...'} />
        </CardContent>
      )}
    </Card>
  );
};

const SECTIONS_CONFIG = [
  { key: 'appunti', titleKey: 'sessionPrepNotes', titleFallback: 'Appunti', icon: BookOpen, placeholderKey: 'addNote', placeholderFallback: 'Aggiungi appunto...' },
  { key: 'timeline', titleKey: 'sessionPrepTimeline', titleFallback: 'Timeline', icon: Calendar, placeholderKey: 'addEvent', placeholderFallback: 'Aggiungi evento...' },
];

const SessionPrepScreen = ({ onBack, t, theme, toggleTheme, accent, setAccent, lang, setLang }) => {
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'editor'
  const [savedSessions, setSavedSessions] = useState(loadSavedSessions);
  const [currentSession, setCurrentSession] = useState(null); // { id, name, data }
  const [editingItem, setEditingItem] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [deleteConfirmSession, setDeleteConfirmSession] = useState(null);

  useEffect(() => {
    migrateOldPrep();
    setSavedSessions(loadSavedSessions());
  }, []);

  const data = currentSession?.data ?? defaultPrepSections();
  const setData = useCallback((next) => {
    setCurrentSession((prev) => (prev ? { ...prev, data: next } : null));
  }, []);

  const persist = useCallback((next) => {
    setCurrentSession((prev) => (prev ? { ...prev, data: next } : null));
  }, []);

  const addItem = (section) => (text, isChecklist = false) => {
    const id = Math.random().toString(36).substr(2, 9);
    const item = isChecklist ? { id, text: text.trim(), checklist: true, items: [] } : { id, text: text.trim() };
    persist({ ...data, [section]: [...(data[section] || []), item] });
  };
  const updateItem = (section, index, text) => {
    const arr = [...(data[section] || [])];
    const item = arr[index];
    arr[index] = typeof item === 'object' && item.id ? { ...item, text } : { id: item?.id || Math.random().toString(36).substr(2, 9), text };
    persist({ ...data, [section]: arr });
    setEditingItem(null);
  };
  const updateItemFull = (section, index, updatedItem) => {
    const arr = [...(data[section] || [])];
    arr[index] = updatedItem;
    persist({ ...data, [section]: arr });
  };
  const deleteItem = (section, index) => {
    const arr = (data[section] || []).filter((_, i) => i !== index);
    persist({ ...data, [section]: arr });
  };

  const luoghi = data.luoghi || [];
  const addPlace = () => persist({ ...data, luoghi: [...luoghi, { ...defaultPlace(), name: t.newPlace || 'Nuovo luogo' }] });
  const updatePlace = (placeIndex, updated) => {
    const next = [...luoghi];
    next[placeIndex] = { ...defaultPlace(), ...next[placeIndex], ...updated };
    persist({ ...data, luoghi: next });
  };
  const deletePlace = (placeIndex) => persist({ ...data, luoghi: luoghi.filter((_, i) => i !== placeIndex) });
  const updatePlaceSub = (placeId, key, index, text) => {
    const placeIndex = luoghi.findIndex((p) => p.id === placeId);
    if (placeIndex < 0) return;
    const place = luoghi[placeIndex];
    const arr = [...(place[key] || [])];
    const item = arr[index];
    arr[index] = typeof item === 'object' && item.id ? { ...item, text } : { id: Math.random().toString(36).substr(2, 9), text };
    updatePlace(placeIndex, { [key]: arr });
    setEditingItem(null);
  };

  const openNewSession = () => {
    setCurrentSession({ id: null, name: '', data: defaultPrepSections() });
    setViewMode('editor');
  };
  const openSession = (session) => {
    setCurrentSession({ id: session.id, name: session.name, data: normalizeSessionData(session.data || {}) });
    setViewMode('editor');
  };
  const backToList = () => {
    setViewMode('list');
    setCurrentSession(null);
  };
  const saveCurrentSession = () => {
    const name = (currentSession?.name || '').trim() || (t.sessionUnnamed || 'Sessione senza nome');
    const updated = saveSessionToList(savedSessions, { ...currentSession, id: currentSession?.id || undefined, name, data });
    setSavedSessions(updated);
    const newId = !currentSession?.id && updated.length > 0 ? updated[updated.length - 1].id : currentSession?.id;
    setCurrentSession((prev) => (prev ? { ...prev, id: newId ?? prev.id, name, data: prev.data } : null));
    toast.success(t.saved || 'Salvato');
  };
  const deleteSession = (session) => {
    setSavedSessions((prev) => deleteSessionFromList(prev, session.id));
    setDeleteConfirmSession(null);
    if (currentSession?.id === session.id) {
      setCurrentSession(null);
      setViewMode('list');
    }
    toast.success(t.deleted || 'Eliminato');
  };

  const sections = SECTIONS_CONFIG.map(({ key, titleKey, titleFallback, icon, placeholderKey, placeholderFallback }) => ({
    key,
    title: t[titleKey] || titleFallback,
    icon,
    placeholder: t[placeholderKey] || placeholderFallback,
  }));

  if (viewMode === 'list') {
    return (
      <div className="coco-v2-screen min-h-[100svh] sm:min-h-[100dvh] flex flex-col min-w-0">
        <Toaster theme={theme} />
        <header className="sticky top-0 z-50 glass coco-v2-header shrink-0">
          <div className="w-full max-w-lg sm:max-w-2xl lg:max-w-6xl xl:max-w-7xl mx-auto min-w-0 px-4 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between gap-2">
            <Button variant="ghost" size="icon" className="h-10 w-10 shrink-0" onClick={onBack} aria-label={t.back || 'Indietro'}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-bold text-lg">{t.sessionPrep || 'Preparazione alla sessione'}</h1>
            <div className="flex items-center gap-1 shrink-0">
              <LanguageDropdown lang={lang} setLang={setLang} t={t} />
              <AppearanceDropdown theme={theme} toggleTheme={toggleTheme} accent={accent} setAccent={setAccent} t={t} />
            </div>
          </div>
        </header>
        <main className="flex-1 w-full max-w-lg sm:max-w-2xl lg:max-w-6xl xl:max-w-7xl mx-auto min-w-0 px-4 sm:px-6 lg:px-8 py-6 pb-12">
          <div className="flex flex-col lg:flex-row lg:items-start gap-4 lg:gap-6 xl:gap-8">
            <aside className="w-full lg:w-[280px] xl:w-[320px] lg:shrink-0 space-y-4">
              <p className="text-sm text-muted-foreground">
                {t.sessionPrepDescLong || 'Prepara le tue sessioni con un kit completo: crea una sessione con un nome (es. "Città del Re"), salvala e riaprila quando serve. In ogni sessione avrai appunti e timeline generali, più i luoghi: per ogni luogo potrai organizzare incontri, dialoghi, oggetti, personaggi e missioni, anche con checklist. Ideale per non dimenticare nulla prima del tavolo.'}
              </p>
              <Button className="w-full accent-bg hover:opacity-90 text-white border-0" onClick={openNewSession}>
                <Plus className="w-4 h-4 mr-2" />
                {t.newSession || 'Nuova sessione'}
              </Button>
            </aside>
            <section className="flex-1 min-w-0">
          <h2 className="text-sm font-semibold text-muted-foreground mb-3">{t.savedSessions || 'Sessioni salvate'}</h2>
          {savedSessions.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-8 text-center text-muted-foreground">
                <BookOpen className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>{t.noSavedSessions || 'Non hai ancora salvato nessuna sessione. Clicca "Nuova sessione" per crearne una: potrai dare un nome, compilare appunti, timeline e aggiungere luoghi con i relativi incontri, dialoghi, oggetti, personaggi e missioni. Salva con un nome per recuperarla qui in seguito.'}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {savedSessions.map((session) => (
                <Card key={session.id} className="overflow-hidden">
                  <CardContent className="p-4 flex items-center justify-between gap-3">
                    <button type="button" className="flex-1 min-w-0 text-left" onClick={() => openSession(session)}>
                      <span className="font-medium block truncate">{session.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {session.savedAt ? new Date(session.savedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                      </span>
                    </button>
                    <div className="flex gap-1 shrink-0">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openSession(session)} title={t.open || 'Apri'}>
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setDeleteConfirmSession(session)} title={t.delete || 'Elimina'}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
            </section>
          </div>
        </main>
        <Dialog open={!!deleteConfirmSession} onOpenChange={(open) => !open && setDeleteConfirmSession(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t.delete || 'Elimina'}</DialogTitle>
              <DialogDescription>{deleteConfirmSession ? (t.deleteSessionConfirm || 'Eliminare questa sessione?') : ''}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirmSession(null)}>{t.cancel || 'Annulla'}</Button>
              <Button variant="destructive" onClick={() => deleteConfirmSession && deleteSession(deleteConfirmSession)}>{t.delete || 'Elimina'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="coco-v2-screen min-h-[100svh] sm:min-h-[100dvh] flex flex-col min-w-0">
      <Toaster theme={theme} />
      <header className="sticky top-0 z-50 glass coco-v2-header shrink-0">
        <div className="w-full max-w-lg sm:max-w-2xl lg:max-w-6xl xl:max-w-7xl mx-auto min-w-0 px-4 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between gap-2">
          <Button variant="ghost" size="icon" className="h-10 w-10 shrink-0" onClick={backToList} aria-label={t.back || 'Indietro'}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <Input
              placeholder={t.sessionNamePlaceholder || 'Nome sessione'}
              value={currentSession?.name ?? ''}
              onChange={(e) => setCurrentSession((prev) => (prev ? { ...prev, name: e.target.value } : null))}
              className="h-9 font-medium max-w-[180px] sm:max-w-[240px]"
            />
            <Button size="sm" onClick={saveCurrentSession} className="shrink-0 accent-bg hover:opacity-90 text-white border-0">
              {t.save || 'Salva'}
            </Button>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <LanguageDropdown lang={lang} setLang={setLang} t={t} />
            <AppearanceDropdown theme={theme} toggleTheme={toggleTheme} accent={accent} setAccent={setAccent} t={t} />
          </div>
        </div>
      </header>
      <main className="flex-1 w-full max-w-lg sm:max-w-2xl lg:max-w-6xl xl:max-w-7xl mx-auto min-w-0 px-4 sm:px-6 lg:px-8 py-6 pb-12">
        <div className="flex flex-col lg:flex-row lg:items-start gap-4 lg:gap-6 xl:gap-8">
          <aside className="w-full lg:w-[280px] xl:w-[320px] lg:shrink-0">
            <p className="text-sm text-muted-foreground">
              {t.sessionPrepEditorDesc || 'In alto: appunti liberi e timeline della sessione. Sotto, i luoghi: aggiungi ogni location (taverna, dungeon, città…) e dentro ciascuna organizza incontri, dialoghi, oggetti, personaggi e missioni. Puoi usare le checklist per obiettivi o cose da non dimenticare. Assegna un nome alla sessione e clicca Salva per conservarla.'}
            </p>
          </aside>
          <section className="flex-1 min-w-0">
        <div className="space-y-3">
          {sections.map(({ key, title, icon, placeholder }) => (
            <SectionEditor
              key={key}
              title={title}
              icon={icon}
              items={data[key] || []}
              onAdd={addItem(key)}
              onEdit={(index, item) => { const v = typeof item === 'object' && item?.text != null ? item.text : item; setEditingItem({ section: key, index, value: v }); setEditValue(v); }}
              onDelete={(index) => deleteItem(key, index)}
              onUpdateItem={(index, updatedItem) => updateItemFull(key, index, updatedItem)}
              t={t}
              placeholder={placeholder}
              defaultExpanded={false}
              compact
            />
          ))}
          <Card className="border-border/60">
            <CardHeader className="py-2 px-3">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  {t.sessionPrepPlaces || 'Luoghi'}
                </CardTitle>
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={addPlace}>
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  {t.addPlace || 'Aggiungi luogo'}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-0 px-3 pb-3">
              {luoghi.length === 0 ? (
                <p className="text-xs text-muted-foreground py-3">{t.noPlaces || 'Nessun luogo ancora. Clicca "Aggiungi luogo" per creare il primo (es. Taverna, Castello, Dungeon): in ogni luogo potrai inserire incontri, dialoghi, oggetti, personaggi e missioni specifici di quel posto.'}</p>
              ) : (
                <div className="space-y-2">
                  {luoghi.map((place, idx) => (
                    <PlaceCard
                      key={place.id}
                      place={place}
                      onUpdate={(updated) => updatePlace(idx, updated)}
                      onDelete={() => deletePlace(idx)}
                      onEditSubItem={(placeId, key, index, value) => { setEditingItem({ placeId, key, index, value }); setEditValue(value); }}
                      t={t}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
          </section>
        </div>
      </main>
      <Dialog open={!!editingItem} onOpenChange={(open) => { if (!open) { setEditingItem(null); setEditValue(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.edit || 'Modifica'}</DialogTitle>
          </DialogHeader>
          {editingItem && (
            <>
              <Input
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (editingItem.placeId != null) updatePlaceSub(editingItem.placeId, editingItem.key, editingItem.index, editValue);
                    else updateItem(editingItem.section, editingItem.index, editValue);
                    setEditingItem(null); setEditValue('');
                  }
                }}
                autoFocus
              />
              <DialogFooter>
                <Button variant="outline" onClick={() => { setEditingItem(null); setEditValue(''); }}>{t.cancel || 'Annulla'}</Button>
                <Button onClick={() => {
                  if (editingItem.placeId != null) updatePlaceSub(editingItem.placeId, editingItem.key, editingItem.index, editValue);
                  else updateItem(editingItem.section, editingItem.index, editValue);
                  setEditingItem(null); setEditValue('');
                }}>{t.save || 'Salva'}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============ MAIN APP ============

function App() {
  const [mode, setMode] = useState(null); // null | 'personal' | 'master' | 'join' | 'characters' | 'master-offline' | 'how-it-works'

  // Check for saved lobbies
  const checkSavedLobby = () => {
    // Check for master lobby
    const masterLobby = localStorage.getItem('dnd-master-lobby');
    if (masterLobby) {
      try {
        const data = JSON.parse(masterLobby);
        if (data.lobbyCode) {
          return { role: 'master', data };
        }
      } catch (e) {
        console.error('Error parsing master lobby:', e);
      }
    }
    
    // Check for player lobby
    const playerLobby = localStorage.getItem('dnd-player-lobby');
    if (playerLobby) {
      try {
        const data = JSON.parse(playerLobby);
        if (data.code) {
          return { role: 'player', data };
        }
      } catch (e) {
        console.error('Error parsing player lobby:', e);
      }
    }
    
    return null;
  };

  const [, setLobbyBannerRefresh] = useState(0);
  const savedLobby = checkSavedLobby();

  const handleRejoinLobby = () => {
    if (savedLobby?.role === 'master') {
      setMode('master');
    } else if (savedLobby?.role === 'player') {
      setMode('join');
    }
  };

  const handleDismissSavedLobby = () => {
    if (savedLobby?.role === 'master') {
      localStorage.removeItem('dnd-master-lobby');
    } else if (savedLobby?.role === 'player') {
      localStorage.removeItem('dnd-player-lobby');
    }
    setLobbyBannerRefresh((r) => r + 1);
  };

  return (
    <ThemeProvider>
      {({ theme, toggleTheme, accent, setAccent }) => (
        <div className="coco-v2 min-h-[100dvh]">
          <Analytics />
          <LanguageProvider>
          {({ lang, setLang, t }) => {
            if (!mode) {
              return (
                <>
                  <Toaster theme={theme} />
                  <HomeScreen 
                    onSelectMode={setMode} 
                    onManageCharacters={() => setMode('characters')}
                    onOpenHowItWorks={() => setMode('how-it-works')}
                    onRejoinLobby={handleRejoinLobby}
                    onDismissSavedLobby={handleDismissSavedLobby}
                    savedLobby={savedLobby}
                    t={t} 
                    theme={theme} 
                    toggleTheme={toggleTheme}
                    accent={accent}
                    setAccent={setAccent}
                    lang={lang}
                    setLang={setLang} 
                  />
                </>
              );
            }
            
            if (mode === 'personal' || mode === 'characters') {
              return <PersonalTrackingScreen onBack={() => setMode(null)} t={t} theme={theme} toggleTheme={toggleTheme} accent={accent} setAccent={setAccent} lang={lang} setLang={setLang} charactersOnly={mode === 'characters'} />;
            }

            if (mode === 'master-offline') {
              return (
                <MasterHubScreen
                  onBack={() => setMode(null)}
                  onEnterBattle={() => setMode('master-offline-combat')}
                  onManageNpc={() => setMode('master-npc-offline')}
                  onSessionPrep={() => setMode('master-prep-offline')}
                  t={t}
                  theme={theme}
                  toggleTheme={toggleTheme}
                  accent={accent}
                  setAccent={setAccent}
                  lang={lang}
                  setLang={setLang}
                />
              );
            }
            if (mode === 'master-offline-combat') {
              return <MasterOfflineScreen onBack={() => setMode('master-offline')} t={t} theme={theme} toggleTheme={toggleTheme} accent={accent} setAccent={setAccent} lang={lang} setLang={setLang} />;
            }
            if (mode === 'master-npc-offline') {
              return <MasterNpcScreen onBack={() => setMode('master-offline')} t={t} theme={theme} toggleTheme={toggleTheme} accent={accent} setAccent={setAccent} lang={lang} setLang={setLang} />;
            }
            if (mode === 'master-prep-offline') {
              return <SessionPrepScreen onBack={() => setMode('master-offline')} t={t} theme={theme} toggleTheme={toggleTheme} accent={accent} setAccent={setAccent} lang={lang} setLang={setLang} />;
            }

            if (mode === 'multiplayer') {
              return <MultiplayerScreen onBack={() => setMode(null)} onSelectMode={setMode} t={t} theme={theme} toggleTheme={toggleTheme} accent={accent} setAccent={setAccent} lang={lang} setLang={setLang} />;
            }
            
            if (mode === 'master') {
              return (
                <MasterHubScreen
                  onBack={() => setMode(null)}
                  onEnterBattle={() => setMode('master-lobby')}
                  onManageNpc={() => setMode('master-npc')}
                  onSessionPrep={() => setMode('master-prep')}
                  t={t}
                  theme={theme}
                  toggleTheme={toggleTheme}
                  accent={accent}
                  setAccent={setAccent}
                  lang={lang}
                  setLang={setLang}
                />
              );
            }
            if (mode === 'master-lobby') {
              return <MasterLobbyScreen onBack={() => setMode('master')} t={t} theme={theme} toggleTheme={toggleTheme} accent={accent} setAccent={setAccent} lang={lang} setLang={setLang} />;
            }
            if (mode === 'master-npc') {
              return <MasterNpcScreen onBack={() => setMode('master')} t={t} theme={theme} toggleTheme={toggleTheme} accent={accent} setAccent={setAccent} lang={lang} setLang={setLang} />;
            }
            if (mode === 'master-prep') {
              return <SessionPrepScreen onBack={() => setMode('master')} t={t} theme={theme} toggleTheme={toggleTheme} accent={accent} setAccent={setAccent} lang={lang} setLang={setLang} />;
            }
            
            if (mode === 'join') {
              return <JoinLobbyScreen onBack={() => setMode(null)} t={t} theme={theme} toggleTheme={toggleTheme} accent={accent} setAccent={setAccent} lang={lang} setLang={setLang} />;
            }

            if (mode === 'how-it-works') {
              return <HowItWorksScreen onBack={() => setMode(null)} t={t} theme={theme} toggleTheme={toggleTheme} accent={accent} setAccent={setAccent} lang={lang} setLang={setLang} />;
            }
            
            return null;
          }}
        </LanguageProvider>
        </div>
      )}
    </ThemeProvider>
  );
}

export default App;
